import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';

import styled from 'styled-components';
import { PointsQueryService } from '../services/pointsQueryService';
import { PointManagementService } from '../services/pointManagementService';
import { PointTypeManagementService } from '../services/pointTypeManagementService';
import { CoinManagementService } from '../services/coinManagementService';
import { userDataService } from '../services/userDataService';
import { TimeTagService } from '../services/timeTagService';
import GemIcon from '../components/icons/GemIcon';
import { 
  PointsTableData, 
  StudentAssignmentHistory
} from '../types/assignmentPointsMemo';
import { User, Class, ClassMember } from '../types/alarm';
import { PointGrantData } from '../types/pointManagement';
import { RelatedStudentInfo } from '../types/relatedStudent';
import PointManagementModal from '../components/PointManagementModal';
import StudentPointDetailModal from '../components/StudentPointDetailModal';
import ProfileImageSelectModal from '../components/ProfileImageSelectModal';
import CachedProfileImage from '../components/CachedProfileImage';
import StudentTagSelectModal from '../components/StudentTagSelectModal';
import { ClassMembersNotePartitionService } from '../services/classMembersNotePartitionService';
import { CalendarComponent } from '../components/CalendarComponent';
import { TimeTagSelectModal } from '../components/TimeTagSelectModal';
import { TimeTag } from '../components/TimeTagSettingsModal';
import { defaultSetService } from '../services/defaultSetService';
import ConfirmModal from '../components/ConfirmModal';
import {
  MemberProfileDisplayMode,
  readMemberProfileDisplayMode,
  resolveMemberProfileSource
} from '../utils/memberProfileDisplay';

/** 개별 기록 열람 비밀번호 검증용 SHA-256 해시 */
async function hashLockPassword(password: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// 포인트/코인 표시용 공통 스타일 (코인/포인트 모드 전환 시 사용)
const PointInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 0px;
  color: var(--color-accent-strong);

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const PointIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  position: relative;

  img {
    width: 18px;
    height: 18px;
  }

  /* 라이트 모드에서 메인 테마 초록색(#10504A)으로 보이도록 mask 사용 */
  :root[data-theme='light'] & {
    background-color: #10504A;
    mask-image: url('/point.svg');
    mask-size: 18px 18px;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url('/point.svg');
    -webkit-mask-size: 18px 18px;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    
    img {
      opacity: 0;
    }
  }

  /* 다크모드에서 흰색으로 보이도록 필터 적용 */
  :root:not([data-theme='light']) & img {
    filter: brightness(0) invert(1);
  }
`;

const PointMultiplySign = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: inherit;
  margin: 0 1px;
`;

const CompactPointsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  margin-bottom: 0px;
  color: var(--color-accent-strong);

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const CompactPointsText = styled.span`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: inherit;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
`;

// 레벨 색상 정의 (10개 색상으로 순환: 레벨 0,10,20...은 메인테마 초록/흰색, 나머지는 1~9 색상 반복)
const levelColors = [
  '#FC5555', // 레벨1, 11, 21... - 빨간색
  '#FF9933', // 레벨2, 12, 22... - 주황색
  '#CCCC00', // 레벨3, 13, 23... - 노란색
  '#669900', // 레벨4, 14, 24... - 연두색
  '#119473', // 레벨5, 15, 25... - 청록색
  '#0066CC', // 레벨6, 16, 26... - 파란색
  '#9900CC', // 레벨7, 17, 27... - 보라색
  '#920049', // 레벨8, 18, 28... - 진한 분홍색
  '#D60093'  // 레벨9, 19, 29... - 분홍색
];

// 레벨별 색상 가져오기 (10개 색상으로 순환)
const getLevelColor = (level: number): string => {
  // 레벨 0, 10, 20 등 (10의 배수)일 때는 메인테마 초록/흰색
  if (level % 10 === 0) {
    return 'var(--color-level-0)';
  }
  // 나머지 레벨은 1~9 색상 반복 (레벨 1→인덱스0, 레벨 2→인덱스1, ... 레벨 9→인덱스8)
  const colorIndex = (level % 10) - 1;
  return levelColors[colorIndex];
};

// 레벨 배지 텍스트 색상 가져오기
const getLevelTextColor = (level: number): string => {
  // 레벨 0, 10, 20 등 (10의 배수)일 때는 다크모드에서 흰색 배경이므로 검정색 텍스트
  if (level % 10 === 0) {
    // 다크모드에서 흰색 배경일 때 검정색 텍스트
    return 'var(--color-level-0-text)';
  }
  // 다른 레벨은 항상 흰색
  return 'white';
};

interface PointsTablePageProps {
  user: User | null;
  currentClass: Class | null;
  onBack: () => void;
  onLogout: () => void;
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments') => void;
  onUpdateUser: (updatedUser: User) => void;
  onProfileModalOpen: () => void;
  onClassManageModalOpen: () => void;
  classes: Class[];
  onSeatAssignmentClick: () => void;
  onLevelSettingsClick: () => void;
  onPointManagementClick: () => void;
  onItemCardSettingsClick: () => void;
  onAssignmentSettingsClick: () => void;
  onHomeNavigate: () => void;
  onAlarmNavigate?: () => void;
  // 모달 상태들
  showSeatAssignmentModal?: boolean;
  setShowSeatAssignmentModal?: (show: boolean) => void;
  showLevelSettingsModal?: boolean;
  setShowLevelSettingsModal?: (show: boolean) => void;
  showCoinSettingsModal?: boolean;
  setShowCoinSettingsModal?: (show: boolean) => void;
  showPointManagementModal?: boolean;
  setShowPointManagementModal?: (show: boolean) => void;
  showItemCardSettingsModal?: boolean;
  setShowItemCardSettingsModal?: (show: boolean) => void;
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
  currentMemberGroup?: string;
}

export interface PointsTablePageHandle {
  handleSeatAssignmentMenuClick: () => void;
  handleLevelSettingsMenuClick: () => void;
  handleCoinSettingsMenuClick: () => void;
  handlePointManagementMenuClick: () => void;
  handleItemCardSettingsMenuClick: () => void;
}

const PointsTablePage = forwardRef<PointsTablePageHandle, PointsTablePageProps>(({
  user, 
  currentClass, 
  onBack,
  onLogout,
  onNavigate,
  onUpdateUser,
  onProfileModalOpen,
  onClassManageModalOpen,
  classes,
  onSeatAssignmentClick,
  onLevelSettingsClick,
  onPointManagementClick,
  onItemCardSettingsClick,
  onAssignmentSettingsClick,
  onHomeNavigate,
  onAlarmNavigate,
  showSeatAssignmentModal,
  setShowSeatAssignmentModal,
  showLevelSettingsModal,
  setShowLevelSettingsModal,
  showCoinSettingsModal,
  setShowCoinSettingsModal,
  showPointManagementModal,
  setShowPointManagementModal,
  showItemCardSettingsModal,
  setShowItemCardSettingsModal,
  onLicenseClick,
  currentMemberGroup
}, ref) => {
  const [tableData, setTableData] = useState<PointsTableData | null>(null);
  const previousTableDataRef = useRef<PointsTableData | null>(null);
  const [, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // 초기 로드 여부 추적
  const [hasCoinData, setHasCoinData] = useState(false); // 코인 데이터 캐싱 여부
  const [error, setError] = useState<string | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<StudentAssignmentHistory | null>(null);
  const [classMembers, setClassMembers] = useState<any[]>([]);
  const [memberProfileDisplayMode, setMemberProfileDisplayMode] = useState<MemberProfileDisplayMode>('svg');
  const [activeTab, setActiveTab] = useState<'all' | 'general' | 'project' | 'routine'>('all');
  
  // 포인트/코인/노트 토글 상태 (다른 상태들보다 먼저 정의)
  const [displayMode, setDisplayMode] = useState<'points' | 'coins' | 'notes'>('points');
  
  // 더블클릭 감지를 위한 상태
  const [, setLastClickTime] = useState<number>(0);
  const [, setLastClickStudentId] = useState<string | null>(null);
  
  // 프로필 이미지 클릭 타이머 (더블클릭 감지용)
  const profileImageClickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastProfileImageClickTimeRef = useRef<number>(0);
  const lastProfileImageClickStudentIdRef = useRef<string | null>(null);
  
  // 프로필 이미지 선택 모달 상태
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);
  const [selectedStudentForProfile, setSelectedStudentForProfile] = useState<StudentAssignmentHistory | null>(null);
  
  // 멤버 선택 관련 상태
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(() => {
    const storageKey = user?.id && currentClass?.id ? `selectedStudents_${user.id}_${currentClass.id}` : null;
    if (!storageKey) return new Set();
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('Failed to restore selected students from localStorage:', error);
    }
    return new Set();
  });
  const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
  const [showPointGrantModal, setShowPointGrantModal] = useState<boolean>(false);
  
  // localStorage에서 토글 상태 복원을 위한 플래그
  const [isSettingsRestored, setIsSettingsRestored] = useState<boolean>(false);
  
  // 포인트 지급 모달 상태
  const [, setPointType] = useState<string>('일반');
  const [pointValue, setPointValue] = useState<string>('');
  const [pointMemo, setPointMemo] = useState<string>('');
  const [isGrantingPoints] = useState<boolean>(false);
  
  // 노트 타입 관리 상태
  const [selectedNoteType, setSelectedNoteType] = useState<'일반' | '학습' | '행동'>('일반');
  const [initialNoteType] = useState<'일반' | '학습' | '행동'>('일반');
  
  // 포인트 타입 관리 상태
  const [availablePointTypes, setAvailablePointTypes] = useState<{positive: any[], negative: any[]}>({positive: [], negative: []});
  const [selectedPointTypeId, setSelectedPointTypeId] = useState<string>('');
  
  
  // 포인트 관리 모달 상태
  const [localShowPointManagementModal, setLocalShowPointManagementModal] = useState<boolean>(false);
  const actualShowPointManagementModal = showPointManagementModal !== undefined ? showPointManagementModal : localShowPointManagementModal;
  const actualSetShowPointManagementModal = setShowPointManagementModal || setLocalShowPointManagementModal;
  
  // 멤버 포인트 상세 모달 상태
  const [showStudentPointDetailModal, setShowStudentPointDetailModal] = useState<boolean>(false);
  const [selectedStudentForDetail, setSelectedStudentForDetail] = useState<ClassMember | null>(null);
  
  // 레벨 설정 모달 상태
  const [localShowLevelSettingsModal, setLocalShowLevelSettingsModal] = useState<boolean>(false);
  const actualShowLevelSettingsModal = showLevelSettingsModal !== undefined ? showLevelSettingsModal : localShowLevelSettingsModal;
  const actualSetShowLevelSettingsModal = setShowLevelSettingsModal || setLocalShowLevelSettingsModal;
  
  const [levelUpPoints, setLevelUpPoints] = useState<number>(50); // 기본 레벨업 포인트
  const [levelItemCards, setLevelItemCards] = useState<{ [level: number]: string[] }>({});
  const [isEditingLevel, setIsEditingLevel] = useState<boolean>(false);
  const [levelCoins, setLevelCoins] = useState<{ [level: number]: number }>({});
  const [availableItemCards] = useState<any[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  
  const [coinDefaultPoints, setCoinDefaultPoints] = useState<number>(10); // 기본 코인 단위
  
  // 포인트 지급 모드에서 각 멤버별 포인트와 메모
  const [studentPointInputs, setStudentPointInputs] = useState<{[studentId: string]: {points: number, memo: string, pointTypeId: string, relatedStudents?: RelatedStudentInfo[], noteType?: '일반' | '학습' | '행동'}}>({});
  
  // 변경사항 추적을 위한 초기 상태 저장
  const [initialStudentData, setInitialStudentData] = useState<{[studentId: string]: {points: number, memo: string, pointTypeId: string, relatedStudents?: RelatedStudentInfo[], noteType?: '일반' | '학습' | '행동'}}>({});
  
  // 멤버 태그 선택 모달 상태
  const [showStudentTagModal, setShowStudentTagModal] = useState(false);
  const [currentNoteStudentId, setCurrentNoteStudentId] = useState<string>('');
  
  // 일괄 노트 작성 모달 상태
  const [showBulkNoteModal, setShowBulkNoteModal] = useState<boolean>(false);
  const [bulkNoteContent, setBulkNoteContent] = useState<string>('');
  const [bulkNoteSelectedStudents, setBulkNoteSelectedStudents] = useState<Set<string>>(new Set()); // 노트를 받을 멤버들
  const [bulkNoteRelatedStudents, setBulkNoteRelatedStudents] = useState<RelatedStudentInfo[]>([]); // 관련 멤버들
  const [showBulkNoteStudentSelectModal, setShowBulkNoteStudentSelectModal] = useState<boolean>(false);
  const [bulkNoteType, setBulkNoteType] = useState<'일반' | '학습' | '행동'>('일반'); // 일괄 노트 모달 전용 타입
  
  // 개별 기록 열람 잠금 (defaultSet에서 로드)
  const [individualRecordViewLocked, setIndividualRecordViewLocked] = useState<boolean>(false);
  const [individualRecordViewLockPasswordHash, setIndividualRecordViewLockPasswordHash] = useState<string | undefined>(undefined);
  const [showRecordViewPasswordModal, setShowRecordViewPasswordModal] = useState<boolean>(false);
  const [pendingRecordViewStudentId, setPendingRecordViewStudentId] = useState<string>('');
  const [recordViewPasswordInput, setRecordViewPasswordInput] = useState('');
  const [recordViewPasswordError, setRecordViewPasswordError] = useState('');

  // 개별 노트 관리 모달 상태
  const [showNoteManagementModal, setShowNoteManagementModal] = useState<boolean>(false);
  const [showNoteManagementCloseConfirm, setShowNoteManagementCloseConfirm] = useState(false);
  const [currentNoteManagementStudentId, setCurrentNoteManagementStudentId] = useState<string>('');
  const [mainNotes, setMainNotes] = useState<string[]>([]); // 메인 노트 배열 (현재 표시/편집 중)
  const [originalMainNotes, setOriginalMainNotes] = useState<string[]>([]); // 원본 메인 노트 배열 (변경사항 비교용)
  const [currentMainNoteIndex, setCurrentMainNoteIndex] = useState<number>(0); // 현재 표시 중인 메인 노트 인덱스
  const [mainNoteCache, setMainNoteCache] = useState<string[]>([]); // 변경사항 캐시
  const [mainNoteTitles, setMainNoteTitles] = useState<string[]>([]); // 메인 노트 제목 배열
  const [editingMainNoteTitleIndex, setEditingMainNoteTitleIndex] = useState<number | null>(null); // 편집 중인 제목 인덱스
  const [editingMainNoteTitleValue, setEditingMainNoteTitleValue] = useState<string>(''); // 편집 중인 제목 값
  const [showDeleteMainNoteConfirm, setShowDeleteMainNoteConfirm] = useState<boolean>(false); // 메인 노트 삭제 확인 모달
  const [showAddMainNoteTitleModal, setShowAddMainNoteTitleModal] = useState<boolean>(false); // 메인 노트 제목 입력 모달
  const [newMainNoteTitle, setNewMainNoteTitle] = useState<string>(''); // 새 메인 노트 제목
  const [observationNotes, setObservationNotes] = useState<Array<{id: string, content: string, relatedStudents?: RelatedStudentInfo[], recordedDate: Date, timeTag: string, noteType: '일반' | '학습' | '행동'}>>([]);
  /** 관찰 기록 입력 영역 확장 플래그(레거시·HMR 호환). UI는 항상 펼침이므로 true 유지 */
  const [showAddObservationNote, setShowAddObservationNote] = useState<boolean>(true);
  // 학생별 노트 개수 캐시 (일반/학습/행동 기록 개수)
  const [studentNoteCounts, setStudentNoteCounts] = useState<{[studentId: string]: {일반: number; 학습: number; 행동: number}}>({});
  const [newObservationNoteContent, setNewObservationNoteContent] = useState<string>('');
  const [currentObservationNotePage, setCurrentObservationNotePage] = useState<number>(1);
  const [hasUnsavedMainNoteChanges, setHasUnsavedMainNoteChanges] = useState<boolean>(false);
  
  /** 입력 섹션: 새 기록 추가·수정 저장 시 적용할 noteType */
  const [selectedObservationTab, setSelectedObservationTab] = useState<'일반' | '학습' | '행동'>('일반');
  /** 조회 섹션: 목록 필터만 (입력 유형과 독립) */
  const [observationListTypeFilter, setObservationListTypeFilter] = useState<'일반' | '학습' | '행동'>('일반');
  
  // 관찰 기록 추가용 날짜/타임태그 상태
  const [modalSelectedDate, setModalSelectedDate] = useState<Date>(new Date());
  const [modalSelectedTimeTag, setModalSelectedTimeTag] = useState<any>(null);
  const [showModalCalendar, setShowModalCalendar] = useState<boolean>(false);
  const [showModalTimeTagSelect, setShowModalTimeTagSelect] = useState<boolean>(false);
  const [showModalStudentTagSelect, setShowModalStudentTagSelect] = useState<boolean>(false);
  const [modalRelatedStudents, setModalRelatedStudents] = useState<RelatedStudentInfo[]>([]);
  const [modalSelectedDayOfWeek, setModalSelectedDayOfWeek] = useState<string>(() =>
    ['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]
  );
  
  // 관찰 기록 수정 모드 상태
  const [editingObservationNoteId, setEditingObservationNoteId] = useState<string>('');
  const [editingObservationNoteContent, setEditingObservationNoteContent] = useState<string>('');
  const [editingObservationNoteDate, setEditingObservationNoteDate] = useState<Date>(new Date());
  const [editingObservationNoteTimeTag, setEditingObservationNoteTimeTag] = useState<any>(null);
  const [editingObservationNoteRelatedStudents, setEditingObservationNoteRelatedStudents] = useState<RelatedStudentInfo[]>([]);
  const [editingObservationNoteDayOfWeek, setEditingObservationNoteDayOfWeek] = useState<string>(() =>
    ['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]
  );
  /** 수정 모드 진입 시 스냅샷(JSON) — 변경 여부로 저장·수정 버튼 활성화 */
  const [observationEditBaseline, setObservationEditBaseline] = useState<string | null>(null);
  
  // 관찰 기록 저장/프린트 필터 모달 상태
  const [showObservationFilterModal, setShowObservationFilterModal] = useState<boolean>(false);
  const [observationFilterMode, setObservationFilterMode] = useState<'save' | 'print'>('save');
  const [selectedNoteTypes, setSelectedNoteTypes] = useState<Array<'일반' | '학습' | '행동'>>(['일반', '학습', '행동']);
  const [observationSortOrder, setObservationSortOrder] = useState<'time' | 'type'>('time');
  
  // 포인트 타입 선택 모달
  const [showPointTypeSelectModal, setShowPointTypeSelectModal] = useState<boolean>(false);
  const [currentSelectingStudentId, setCurrentSelectingStudentId] = useState<string | null>(null);
  
  // 포인트 타입별 세트 설정 모달 (포인트 타입 선택 모달에서 열 때 사용)
  const [pointManagementModalInitialTab, setPointManagementModalInitialTab] = useState<'positive' | 'negative'>('positive');
  const [wasPointTypeSelectModalOpen, setWasPointTypeSelectModalOpen] = useState<boolean>(false); // 포인트 타입 선택 모달이 열려있었는지 추적
  
  // 타임 태그 관련 상태
  const [selectedTimeTag, setSelectedTimeTag] = useState<any>(null);
  const [availableTimeTags, setAvailableTimeTags] = useState<any[]>([]);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<string>(() =>
    ['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]
  );

  // 타임 태그 포맷/파싱 헬퍼
  const formatTimeTagLabel = (timeTag: TimeTag, day: string) => {
    const dayLabel = day || '전체';
    return `[${dayLabel}] ${timeTag.name}`;
  };

  const parseTimeTagLabel = (label?: string): { day: string; name: string } => {
    const defaultResult = { day: '일', name: '' };
    if (!label) return defaultResult;

    const trimmed = label.trim();
    const bracketMatch = trimmed.match(/^\[(.+?)\]\s*(.+)$/);
    if (bracketMatch) {
      const day = bracketMatch[1];
      const dayCandidates = ['일반', '일', '월', '화', '수', '목', '금', '토'];
      const mappedDay = day === '일반' ? '일' : (dayCandidates.includes(day) ? day : '일');
      return { day: mappedDay, name: bracketMatch[2] };
    }

    const parts = trimmed.split(' ');
    const dayCandidates = ['일반', '일', '월', '화', '수', '목', '금', '토'];
    if (parts.length >= 2 && dayCandidates.includes(parts[0])) {
      const day = parts[0] === '일반' ? '일' : parts[0];
      return { day, name: parts.slice(1).join(' ').trim() };
    }

    return { day: '일', name: trimmed };
  };

  /** 누가기록 수정 폼 변경 감지용(저장 시점과 동일한 날짜·태그·관련멤버·종류 직렬화) */
  const getObservationDirtyPayload = useCallback(
    (
      content: string,
      recordedDate: Date,
      timeTag: any,
      dayOfWeek: string,
      relatedStudents: RelatedStudentInfo[],
      noteType: '일반' | '학습' | '행동'
    ) => {
      const rd = new Date(recordedDate);
      if (timeTag?.startTime) {
        const [hours, minutes] = timeTag.startTime.split(':').map(Number);
        rd.setHours(hours, minutes, 0, 0);
      }
      const timeTagStr = timeTag ? formatTimeTagLabel(timeTag, dayOfWeek) : '';
      const rel = [...relatedStudents].map(r => r.id).sort().join(',');
      return JSON.stringify({ c: content.trim(), t: rd.getTime(), tag: timeTagStr, rel, nt: noteType });
    },
    []
  );

  const observationAddSaveEnabled = useMemo(
    () => !editingObservationNoteId && newObservationNoteContent.trim() !== '',
    [editingObservationNoteId, newObservationNoteContent]
  );

  const observationEditSaveEnabled = useMemo(() => {
    if (!editingObservationNoteId || !observationEditBaseline) return false;
    if (!editingObservationNoteContent.trim()) return false;
    const cur = getObservationDirtyPayload(
      editingObservationNoteContent,
      editingObservationNoteDate,
      editingObservationNoteTimeTag,
      editingObservationNoteDayOfWeek,
      editingObservationNoteRelatedStudents,
      selectedObservationTab
    );
    return cur !== observationEditBaseline;
  }, [
    editingObservationNoteId,
    observationEditBaseline,
    editingObservationNoteContent,
    editingObservationNoteDate,
    editingObservationNoteTimeTag,
    editingObservationNoteDayOfWeek,
    editingObservationNoteRelatedStudents,
    selectedObservationTab,
    getObservationDirtyPayload,
  ]);
  
  // 행동체크 포인트용 날짜 선택 (오늘 날짜로 초기화)
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD 형식
  });

  // 달력 모달 상태
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  
  // 타임 태그 선택 모달
  const [showTimeTagSelectModal, setShowTimeTagSelectModal] = useState<boolean>(false);

  // 날짜와 타임 태그 초기화 함수
  const resetDateAndTimeTag = async () => {
    // 1. 날짜를 오늘 날짜로 리셋
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;
    setSelectedDate(todayString);
    
    // 2. 현재 시각에 맞는 타임 태그 자동 선택
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    const currentTimeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    
    // 현재 요일 확인 (일요일=0, 월요일=1, ..., 토요일=6)
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const currentDayName = dayNames[currentTime.getDay()];
    
    // 타임 태그 로드
    try {
      const { TimeTagService } = await import('../services/timeTagService');
      const timeTags = await TimeTagService.getTimeTags(user?.id || '', currentClass?.id || '');
      
      // 현재 시각에 맞는 타임 태그 찾기
      let matchedTimeTag = null;
      
      // 1단계: 현재 요일에 맞는 타임 태그 찾기
      const dayTimeTags = timeTags.filter(tag => {
        const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
        return days.includes(currentDayName as any);
      });
      matchedTimeTag = dayTimeTags.find(tag => {
        const startTime = tag.startTime;
        const endTime = tag.endTime;
        return currentTimeString >= startTime && currentTimeString <= endTime;
      });
      
      // 2단계: 찾지 못했으면 타임 태그 없음
      if (matchedTimeTag) {
        setSelectedTimeTag(matchedTimeTag);
      } else {
        setSelectedTimeTag(null);
      }
    } catch (error) {
      console.error('❌ 타임 태그 로드 실패:', error);
      setSelectedTimeTag(null);
    }
  };


  // 커스텀 날짜 표시 컴포넌트
  // 모달 전용 날짜 표시 컴포넌트
  const ModalDateDisplay = ({ selectedDate, onDateChange, onCalendarClick }: { selectedDate: Date; onDateChange: (date: Date) => void; onCalendarClick: () => void }) => {
    const [editingField, setEditingField] = useState<'year' | 'month' | 'day' | null>(null);
    const [tempValue, setTempValue] = useState('');

    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const day = selectedDate.getDate();

    const handleFieldClick = (field: 'year' | 'month' | 'day') => {
      setEditingField(field);
      if (field === 'year') setTempValue(year.toString());
      else if (field === 'month') setTempValue(month.toString());
      else if (field === 'day') setTempValue(day.toString());
    };

    const handleKeyPress = (e: React.KeyboardEvent, field: 'year' | 'month' | 'day') => {
      if (e.key === 'Enter') {
        handleSave(field);
      } else if (e.key === 'Escape') {
        setEditingField(null);
        setTempValue('');
      }
    };

    const handleBlur = (field: 'year' | 'month' | 'day') => {
      handleSave(field);
    };

    const handleSave = (field: 'year' | 'month' | 'day') => {
      const value = parseInt(tempValue);

      if (isNaN(value)) {
        setEditingField(null);
        setTempValue('');
        return;
      }

      let newDate = '';
      if (field === 'year') {
        newDate = `${value}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } else if (field === 'month') {
        if (value < 1 || value > 12) {
          setEditingField(null);
          setTempValue('');
          return;
        }
        newDate = `${year}-${value.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } else if (field === 'day') {
        if (value < 1 || value > 31) {
          setEditingField(null);
          setTempValue('');
          return;
        }
        newDate = `${year}-${month.toString().padStart(2, '0')}-${value.toString().padStart(2, '0')}`;
      }

      // 유효한 날짜인지 확인
      const testDate = new Date(newDate);
      if (testDate.getFullYear() === value || (field !== 'year' && testDate.getMonth() + 1 === (field === 'month' ? value : month)) || (field === 'day' && testDate.getDate() === value)) {
        onDateChange(new Date(newDate));
      }

      setEditingField(null);
      setTempValue('');
    };

    return (
      <ModalDateDisplayContainer>
        <CalendarIcon onClick={onCalendarClick}>
          <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
            <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </CalendarIcon>
        <DateDisplayText>
          <DateYearRow>
            {editingField === 'year' ? (
              <DateInput
                $fieldType="year"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'year')}
                onBlur={() => handleBlur('year')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('year')}>{year}년</DatePart>
            )}
          </DateYearRow>
          <DateMonthDayRow>
            {editingField === 'month' ? (
              <DateInput
                $fieldType="month"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'month')}
                onBlur={() => handleBlur('month')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('month')}>{month}월</DatePart>
            )}
            {editingField === 'day' ? (
              <DateInput
                $fieldType="day"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'day')}
                onBlur={() => handleBlur('day')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('day')}>{day}일</DatePart>
            )}
          </DateMonthDayRow>
        </DateDisplayText>
      </ModalDateDisplayContainer>
    );
  };

  // 메인 페이지용 날짜 표시 컴포넌트
  const CustomDateDisplay = ({ selectedDate, onDateChange }: { selectedDate: string; onDateChange: (date: string) => void }) => {
    const [editingField, setEditingField] = useState<'year' | 'month' | 'day' | null>(null);
    const [tempValue, setTempValue] = useState('');
    
    const dateObj = new Date(selectedDate);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    
    const handleFieldClick = (field: 'year' | 'month' | 'day') => {
      setEditingField(field);
      if (field === 'year') setTempValue(year.toString());
      else if (field === 'month') setTempValue(month.toString());
      else if (field === 'day') setTempValue(day.toString());
    };
    
    const handleKeyPress = (e: React.KeyboardEvent, field: 'year' | 'month' | 'day') => {
      if (e.key === 'Enter') {
        handleSave(field);
      } else if (e.key === 'Escape') {
        setEditingField(null);
        setTempValue('');
      }
    };
    
    const handleBlur = (field: 'year' | 'month' | 'day') => {
      handleSave(field);
    };
    
    const handleSave = (field: 'year' | 'month' | 'day') => {
      const value = parseInt(tempValue);
      
      if (isNaN(value)) {
        setEditingField(null);
        setTempValue('');
        return;
      }
      
      let newDate = '';
      if (field === 'year') {
        newDate = `${value}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } else if (field === 'month') {
        if (value < 1 || value > 12) {
          setEditingField(null);
          setTempValue('');
          return;
        }
        newDate = `${year}-${value.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      } else if (field === 'day') {
        if (value < 1 || value > 31) {
          setEditingField(null);
          setTempValue('');
          return;
        }
        newDate = `${year}-${month.toString().padStart(2, '0')}-${value.toString().padStart(2, '0')}`;
      }
      
      // 유효한 날짜인지 확인
      const testDate = new Date(newDate);
      if (testDate.getFullYear() === value || (field !== 'year' && testDate.getMonth() + 1 === (field === 'month' ? value : month)) || (field === 'day' && testDate.getDate() === value)) {
        onDateChange(newDate);
      }
      
      setEditingField(null);
      setTempValue('');
    };
    
    return (
      <DateDisplayContainer>
        <CalendarIcon onClick={() => setShowCalendarModal(true)}>
          <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
            <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </CalendarIcon>
        <DateDisplayText>
          <DateYearRow>
            {editingField === 'year' ? (
              <DateInput
                $fieldType="year"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'year')}
                onBlur={() => handleBlur('year')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('year')}>{year}년</DatePart>
            )}
          </DateYearRow>
          <DateMonthDayRow>
            {editingField === 'month' ? (
              <DateInput
                $fieldType="month"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'month')}
                onBlur={() => handleBlur('month')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('month')}>{month}월</DatePart>
            )}
            {editingField === 'day' ? (
              <DateInput
                $fieldType="day"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'day')}
                onBlur={() => handleBlur('day')}
                autoFocus
              />
            ) : (
              <DatePart onClick={() => handleFieldClick('day')}>{day}일</DatePart>
            )}
          </DateMonthDayRow>
        </DateDisplayText>
      </DateDisplayContainer>
    );
  };
  const [bulkPointTypeId, setBulkPointTypeId] = useState<string>('');

  // 포인트 되돌리기 관련 상태
  const [showUndoModal, setShowUndoModal] = useState<boolean>(false);
  const [undoInfo, setUndoInfo] = useState<any>(null);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  // 레벨 변화 정보 저장 (확인 버튼 클릭 시 보상 지급/회수용)
  const [pendingLevelChanges, setPendingLevelChanges] = useState<Array<{
    studentId: string;
    oldTotalPoints: number;
    newTotalPoints: number;
    oldLevel: number;
    newLevel: number;
  }>>([]);

  // 🎯 자리 모드 상태
  const [viewMode, setViewMode] = useState<'number' | 'seat'>('number');
  const [seatArrangementData, setSeatArrangementData] = useState<any>(null);
  const [seatArrangementError, setSeatArrangementError] = useState<string>('');
  const [isSeatRotated, setIsSeatRotated] = useState<boolean>(false);
  
  // 🎯 그리드 정렬 상태 (번호 모드에서만 적용)
  const [gridAlignment, setGridAlignment] = useState<'left' | 'center'>('center');
  
  // localStorage 키 생성 함수
  const getStorageKey = useCallback((key: string) => {
    return currentClass ? `pointsTable_${key}_${currentClass.id}` : null;
  }, [currentClass]);

  // localStorage에서 선택 상태를 위한 키 생성 함수
  const getSelectedStudentsStorageKey = useCallback(() => {
    if (!user?.id || !currentClass?.id) return null;
    return `selectedStudents_${user.id}_${currentClass.id}`;
  }, [user?.id, currentClass?.id]);
  
  // 프로필 이미지 클릭 타이머 정리
  useEffect(() => {
    return () => {
      if (profileImageClickTimerRef.current) {
        clearTimeout(profileImageClickTimerRef.current);
        profileImageClickTimerRef.current = null;
      }
    };
  }, []);

  // currentClass가 변경되면 설정 복원 플래그 리셋
  useEffect(() => {
    setIsSettingsRestored(false);
  }, [currentClass?.id]);
  
  // isSelectionMode 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isSettingsRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.isSelectionMode = isSelectionMode;
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (error) {
      console.error('❌ localStorage에 isSelectionMode 저장 실패:', error);
    }
  }, [isSelectionMode, currentClass, isSettingsRestored, getStorageKey]);
  
  // viewMode 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isSettingsRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.viewMode = viewMode;
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (error) {
      console.error('❌ localStorage에 viewMode 저장 실패:', error);
    }
  }, [viewMode, currentClass, isSettingsRestored, getStorageKey]);
  
  // 서비스 인스턴스 (useMemo로 최적화)
  const pointsQueryService = React.useMemo(() => {
    return user && currentClass 
      ? new PointsQueryService(user.id, currentClass.id, currentMemberGroup)
      : null;
  }, [user, currentClass, currentMemberGroup]);

  const pointManagementService = React.useMemo(() => {
    return user && currentClass 
      ? new PointManagementService(user.id, currentClass.id, currentMemberGroup)
      : null;
  }, [user, currentClass, currentMemberGroup]);

  const pointTypeManagementService = React.useMemo(() => {
    return user && currentClass 
      ? new PointTypeManagementService(user.id, currentClass.id)
      : null;
  }, [user, currentClass]);

  const timeTagService = React.useMemo(() => {
    return user && currentClass
      ? TimeTagService
      : null;
  }, [user, currentClass]);


  // 메뉴 핸들러들을 외부에 노출
  useImperativeHandle(ref, () => ({
    handleSeatAssignmentMenuClick: () => {
      setShowSeatAssignmentModal?.(true);
    },
    handleLevelSettingsMenuClick: () => {
      actualSetShowLevelSettingsModal(true);
    },
    handleCoinSettingsMenuClick: () => {
      setShowCoinSettingsModal?.(true);
    },
    handlePointManagementMenuClick: () => {
      actualSetShowPointManagementModal(true);
    },
    handleItemCardSettingsMenuClick: () => {
      setShowItemCardSettingsModal?.(true);
    },
  }), [
    setShowSeatAssignmentModal,
    actualSetShowLevelSettingsModal,
    setShowCoinSettingsModal,
    actualSetShowPointManagementModal,
    setShowItemCardSettingsModal,
  ]);
  // 코인 설정 로드 (DefaultSet에서)
  const loadCoinSettings = useCallback(async () => {
    try {
      if (user && currentClass) {
        const coinSettings = await defaultSetService.loadCoinSettings(user.id, currentClass.id);
        setCoinDefaultPoints(coinSettings?.defaultPoints || 10);
      }
    } catch (error) {
      console.error('코인 설정 로드 실패:', error);
    }
  }, [user, currentClass]);

  const resolveDisplayProfileByStudentId = useCallback((studentId: string, fallbackProfileImage?: string) => {
    const member = classMembers.find((m: ClassMember) => m.id === studentId);
    if (member) {
      return resolveMemberProfileSource(member, memberProfileDisplayMode);
    }
    return fallbackProfileImage || 'basic-0';
  }, [classMembers, memberProfileDisplayMode]);

  // 개별 기록 열람 잠금 설정 로드 (DefaultSet에서)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user?.id || !currentClass?.id) return;
      try {
        const defaultSet = await defaultSetService.loadDefaultSet(user.id, currentClass.id);
        if (!mounted) return;
        setIndividualRecordViewLocked(defaultSet?.individualRecordViewLocked === true);
        setIndividualRecordViewLockPasswordHash(
          typeof defaultSet?.individualRecordViewLockPasswordHash === 'string'
            ? defaultSet.individualRecordViewLockPasswordHash
            : undefined
        );
        setMemberProfileDisplayMode(defaultSet?.memberProfileDisplayMode === 'photo' ? 'photo' : 'svg');
      } catch (e) {
        console.error('개별 기록 열람 설정 로드 실패:', e);
        if (user?.id && currentClass?.id) {
          setMemberProfileDisplayMode(readMemberProfileDisplayMode(user.id, currentClass.id));
        }
      }
    })();
    return () => { mounted = false; };
  }, [user?.id, currentClass?.id]);

  useEffect(() => {
    const handleDisplayModeChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ userId: string; classId: string; mode: MemberProfileDisplayMode }>;
      if (!customEvent.detail) return;
      if (customEvent.detail.userId === user?.id && customEvent.detail.classId === currentClass?.id) {
        setMemberProfileDisplayMode(customEvent.detail.mode);
      }
    };
    window.addEventListener('memberProfileDisplayModeChanged', handleDisplayModeChanged);
    return () => {
      window.removeEventListener('memberProfileDisplayModeChanged', handleDisplayModeChanged);
    };
  }, [user?.id, currentClass?.id]);

  // 탭별 데이터 필터링
  const getFilteredData = useCallback(() => {
    if (!tableData) return null;

    if (activeTab === 'all') {
      return tableData;
    }

    const filteredStudents = tableData.students.map(student => ({
      ...student,
      assignments: student.assignments.filter(assignment => assignment.type === activeTab)
    })).map(student => ({
      ...student,
      totalPoints: student.assignments.reduce((sum, assignment) => sum + assignment.points, 0),
      assignmentCount: student.assignments.length
    }));

    const totalStudents = filteredStudents.length;
    const totalAssignments = filteredStudents.reduce((sum, student) => sum + student.assignmentCount, 0);
    const totalPoints = filteredStudents.reduce((sum, student) => sum + student.totalPoints, 0);
    const averagePoints = totalStudents > 0 ? Math.round(totalPoints / totalStudents) : 0;

    return {
      ...tableData,
      students: filteredStudents,
      totalStudents,
      totalAssignments,
      totalPoints,
      averagePoints
    };
  }, [tableData, activeTab]);

  // 선택된 멤버의 과제 타입별 통계 계산
  const getSelectedStudentTypeStats = useCallback(() => {
    if (!selectedStudent) return { general: { count: 0, points: 0 }, project: { count: 0, points: 0 }, routine: { count: 0, points: 0 } };

    const stats = { general: { count: 0, points: 0 }, project: { count: 0, points: 0 }, routine: { count: 0, points: 0 } };

    selectedStudent.assignments.forEach(assignment => {
      if (assignment.type === 'general') {
        stats.general.count++;
        stats.general.points += assignment.points;
      } else if (assignment.type === 'project') {
        stats.project.count++;
        stats.project.points += assignment.points;
      } else if (assignment.type === 'routine') {
        stats.routine.count++;
        stats.routine.points += assignment.points;
      }
    });

    return stats;
  }, [selectedStudent]);

  // 학생별 노트 개수 로드
  const loadStudentNoteCounts = useCallback(async (studentIds: string[]) => {
    if (!user || !currentClass) {
      return;
    }

    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      const counts: {[studentId: string]: {일반: number; 학습: number; 행동: number}} = {};

      // 각 학생의 노트 데이터 가져오기
      for (const studentId of studentIds) {
        const studentNotes = await noteService.getStudentNotes(studentId);
        const observationNotes = studentNotes?.observationNotes || [];

        counts[studentId] = {
          일반: observationNotes.filter(note => note.noteType === '일반').length,
          학습: observationNotes.filter(note => note.noteType === '학습').length,
          행동: observationNotes.filter(note => note.noteType === '행동').length,
        };
      }

      setStudentNoteCounts(counts);
    } catch (error) {
      console.error('❌ 학생 노트 개수 로드 실패:', error);
    }
  }, [user, currentClass, currentMemberGroup]);

  // 데이터 로드
  // 중복 실행 방지를 위한 ref
  const isLoadingDataRef = useRef<boolean>(false);
  const loadDataRef = useRef<((forceRefresh?: boolean) => Promise<void>) | null>(null);

  const loadData = useCallback(async (forceRefresh?: boolean) => {
    if (!pointsQueryService || !user || !currentClass) return;

    // 강제 새로고침이면 로딩 락 해제 후 진행 (포인트/레벨 변경 후 즉시 반영용)
    if (forceRefresh) {
      isLoadingDataRef.current = false;
    }
    // 이미 로딩 중이면 실행하지 않음
    if (isLoadingDataRef.current) {
      return;
    }

    isLoadingDataRef.current = true;

    // 초기 로드가 아닐 때만 로딩 상태 표시 (코인 모드 전환 시 캐싱된 데이터가 있으면 깜박임 방지)
    if (!isInitialLoad && !(displayMode === 'coins' && hasCoinData)) {
      setLoading(true);
    }
    setError(null);

    try {
      // 클래스 멤버 정보 로드
      const { userDataService } = await import('../services/userDataService');
      const members = await userDataService.loadClassMembers(user.id, currentClass.id, currentMemberGroup);

      // 코인 정보 병합 (coinsHistory)
      let mergedMembers = members;
      try {
        const coinService = new CoinManagementService(user.id, currentClass.id, currentMemberGroup);
        const coinDocs = await coinService.getAllMembersCoinDocuments();
        const coinMap = new Map<string, any[]>();
        coinDocs.forEach(doc => {
          doc.students.forEach(student => {
            coinMap.set(student.studentId, student.coinsHistory || []);
          });
        });
        mergedMembers = members.map(m => ({
          ...m,
          coinsHistory: coinMap.get(m.id) || m.coinsHistory || []
        }));
      } catch (coinErr) {
        console.warn('코인 정보 병합 실패 (무시):', coinErr);
      }

      setClassMembers(mergedMembers);
      if (mergedMembers.length > 0) {
        setHasCoinData(true);
      }
      
      // 포인트 테이블 데이터 로드 (과제 문서에서 직접 조회)
      const data = await pointsQueryService.getPointsTableData(mergedMembers);

      // 이전 테이블 데이터에서 총점 캐시 (레벨 변화 표시용)
      const previousTotalPointsByStudentId = new Map<string, number>();
      if (previousTableDataRef.current?.students) {
        previousTableDataRef.current.students.forEach(student => {
          if (student?.studentId) {
            previousTotalPointsByStudentId.set(student.studentId, student.totalPoints || 0);
          }
        });
      }

      setTableData(data);
      previousTableDataRef.current = data;

      // 레벨업 포인트 단위 로드
      let currentLevelUpPoints = 50;
      if (pointTypeManagementService) {
        const pointTypeData = await pointTypeManagementService.getPointTypeData();
        currentLevelUpPoints = pointTypeData.levelUpPoints;
        setLevelUpPoints(currentLevelUpPoints);
      }

      // =========================
      // 레벨업/레벨다운 감지 및 보상 동기화
      // - 포인트 조회 페이지 로드 시 각 학생의 총점으로 레벨 계산
      // - 이전 총점과 비교하여 레벨 변화 감지
      // =========================
      if (data && data.students && data.students.length > 0) {
        try {
          const { PointManagementService } = await import('../services/pointManagementService');
          const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');

          const pointManagementService = new PointManagementService(user.id, currentClass.id, currentMemberGroup);
          const partitionService = new ClassMembersPartitionService(user.id, currentClass.id, currentMemberGroup);
          
          const calcLevel = (totalPoints: number) => Math.floor((totalPoints || 0) / currentLevelUpPoints);
          const levelChangeMessages: string[] = [];
          const levelChanges: Array<{
            studentId: string;
            oldTotalPoints: number;
            newTotalPoints: number;
            oldLevel: number;
            newLevel: number;
            studentLabel: string;
          }> = [];
          const processedStudentIds = new Set<string>(); // 중복 처리 방지

          // pointsQueryService에서 이미 조회한 membersPointDocuments 재사용 (중복 호출 방지)
          const membersPointDocuments = data.membersPointDocuments || await pointManagementService.getAllMembersPointDocuments();
          
          // 학생별 reward points를 미리 계산 (한 번의 조회로 모든 학생 처리)
          const studentRewardPointsMap = new Map<string, number>();
          membersPointDocuments.forEach((doc: any) => {
            doc.students.forEach((student: any) => {
              if (!studentRewardPointsMap.has(student.studentId)) {
                studentRewardPointsMap.set(student.studentId, 0);
              }
              const rewardPoints = student.points.reduce((sum: number, p: any) => sum + (p?.pointValue || 0), 0);
              studentRewardPointsMap.set(
                student.studentId, 
                studentRewardPointsMap.get(student.studentId)! + rewardPoints
              );
            });
          });

          // 각 학생에 대해 레벨 변화 감지
          for (const student of data.students) {
            if (processedStudentIds.has(student.studentId)) continue;
            processedStudentIds.add(student.studentId);

            try {
              // 현재 총점 (tableData에서 계산된 값: assignmentPoints + rewardPoints)
              const currentTotalPoints = student.totalPoints || 0;
              const currentLevel = calcLevel(currentTotalPoints);

              // 이전 레벨 확인: currentLevel 필드에서 가져오기
              const member = await partitionService.getMember(student.studentId);
              const oldLevelFromDB = (member as any)?.currentLevel ?? -1;
              const previousTotalPoints = previousTotalPointsByStudentId.get(student.studentId);
              const oldLevelFromPrevious = previousTotalPoints !== undefined
                ? calcLevel(previousTotalPoints)
                : undefined;
              
              // 이전 총점(정확한 총점)을 별도로 저장하진 않으므로,
              // 기존 구조에서는 '이전 레벨(currentLevel)'을 기준으로 레벨 변화만 감지합니다.
              // (보상 동기화는 oldLevel 기반으로 oldTotalPoints를 보수적으로 구성)
              const oldLevel = oldLevelFromPrevious !== undefined
                ? oldLevelFromPrevious
                : (oldLevelFromDB >= 0 ? oldLevelFromDB : currentLevel);
              const oldTotalPoints = previousTotalPoints !== undefined
                ? previousTotalPoints
                : oldLevel * currentLevelUpPoints;

              // 레벨 변화 감지: 현재 레벨과 이전 레벨 비교
              // 실제로 레벨이 변화한 경우에만 처리
              if (currentLevel !== oldLevel) {
                const studentLabel = `${student.studentNumber || ''}번 ${student.studentName}`.trim();
                
                // 레벨 변화 정보 저장 (확인 버튼 클릭 시 보상 지급/회수용)
                levelChanges.push({
                  studentId: student.studentId,
                  oldTotalPoints,
                  newTotalPoints: currentTotalPoints,
                  oldLevel,
                  newLevel: currentLevel,
                  studentLabel
                });

                // 토스트 메시지 추가 (HTML 형식, 열 맞춤: 번호이름 | 변화전 레벨 | → | 변화 후 레벨)
                const oldLevelColor = getLevelColor(oldLevel);
                const newLevelColor = getLevelColor(currentLevel);
                const levelChangeHtml = `<div style="display: flex; align-items: center; margin-bottom: 8px; gap: 0;">
                  <span style="min-width: 88px; flex-shrink: 0; padding: 4px 8px; border: 1px solid var(--color-border); border-radius: 4px; background: rgba(0,0,0,0.05); text-align: left;">${studentLabel}</span>
                  <span style="min-width: 72px; text-align: right; color: ${oldLevelColor};">${oldLevel} 레벨</span>
                  <span style="width: 28px; flex-shrink: 0; text-align: center;">→</span>
                  <span style="min-width: 72px; text-align: right; color: ${newLevelColor};">${currentLevel} 레벨</span>
                </div>`;
                levelChangeMessages.push(levelChangeHtml);
              }

              // 과제 동기화 등으로 currentLevel만 맞고 보상/rewardedLevels가 밀린 경우 보정 (그룹 프로필 일치)
              try {
                const { ensureLevelRewardsCatchUp } = await import('../utils/levelUpRewardHelper');
                const rewardedHint =
                  member && Array.isArray((member as any).rewardedLevels)
                    ? ((member as any).rewardedLevels as number[])
                    : undefined;
                await ensureLevelRewardsCatchUp(
                  user.id,
                  currentClass.id,
                  student.studentId,
                  currentTotalPoints,
                  currentLevelUpPoints,
                  currentMemberGroup,
                  rewardedHint
                );
              } catch (ensureErr) {
                console.warn(`레벨업 보상 누락 보정 실패 (${student.studentId}):`, ensureErr);
              }
            } catch (studentError) {
              console.error(`❌ 학생 ${student.studentId} 레벨업 감지 실패:`, studentError);
              // 개별 학생 실패해도 계속 진행
            }
          }

          // 레벨 변화가 있으면 레벨 변화 정보 저장하고 토스트 메시지 표시 (열 맞춤용 wrapper)
          if (levelChangeMessages.length > 0) {
            setPendingLevelChanges(levelChanges);
            showToastMessage(`<div style="text-align: left;">${levelChangeMessages.join('')}</div>`);
          }
        } catch (levelError) {
          console.error('❌ 레벨업 감지 중 오류 발생:', levelError);
          // 레벨업 감지 실패해도 페이지 로드는 계속 진행
        }
      }

        // 코인 설정 로드 (비동기이지만 await 없이 실행하여 loadData 완료를 기다리지 않음)
        loadCoinSettings();

        // 노트 개수 로드 (노트 모드일 때만)
        const currentDisplayModeForNotes = displayMode;
        if (currentDisplayModeForNotes === 'notes') {
          await loadStudentNoteCounts(members.map(m => m.id));
        }

        // 초기 로드 완료 표시
      const currentIsInitialLoadForSet = isInitialLoad;
      if (currentIsInitialLoadForSet) {
        setIsInitialLoad(false);
      }

    } catch (err) {
      setError(`데이터 로드 실패: ${err}`);
      console.error('포인트 테이블 데이터 로드 실패:', err);
      // 에러 발생 시에도 초기 로드 완료 표시
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } finally {
      setLoading(false);
      isLoadingDataRef.current = false;
    }
  }, [
    pointsQueryService,
    pointTypeManagementService,
    user,
    currentClass,
    currentMemberGroup,
    displayMode,
    hasCoinData,
    isInitialLoad,
    loadCoinSettings,
    loadStudentNoteCounts,
  ]);

  // loadData ref 업데이트
  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  // 클래스/사용자 변경 시 코인 캐시 초기화
  useEffect(() => {
    setHasCoinData(false);
  }, [user?.id, currentClass?.id]);

  // 초기 로드 및 user/currentClass 변경 시 데이터 로드
  // 코인 모드일 때는 코인 데이터가 없을 때만 로드
  useEffect(() => {
    const userId = user?.id;
    const classId = currentClass?.id;
    if (!pointsQueryService || !userId || !classId) return;
    // 코인 모드이고 코인 데이터가 있으면 로드하지 않음
    if (displayMode === 'coins' && hasCoinData) return;
    if (loadDataRef.current) {
      loadDataRef.current();
    }
  }, [pointsQueryService, user?.id, currentClass?.id, displayMode, hasCoinData]);

  // 멤버 클릭 처리 (선택 모드와 상세 보기 모드 구분)
  const handleStudentClick = (student: StudentAssignmentHistory) => {
    if (isSelectionMode) {
      // 선택 모드: 멤버 선택/해제
      const newSelected = new Set(selectedStudents);
      if (newSelected.has(student.studentId)) {
        newSelected.delete(student.studentId);
      } else {
        newSelected.add(student.studentId);
      }
      setSelectedStudents(newSelected);
      // localStorage에 저장
      const storageKey = getSelectedStudentsStorageKey();
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(Array.from(newSelected)));
        } catch (error) {
          console.error('Failed to save selected students to localStorage:', error);
        }
      }
    } else {
      // 상세 보기 모드: 클릭 시 바로 포인트 상세 모달 열기
      // classMembers에서 해당하는 ClassMember 찾기
      const classMember = classMembers.find(member => member.id === student.studentId);
      if (classMember) {
        setSelectedStudentForDetail(classMember);
        setShowStudentPointDetailModal(true);
      }
    }
  };

  // 프로필 이미지 더블클릭 처리
  const handleProfileImageDoubleClick = (e: React.MouseEvent, student: StudentAssignmentHistory) => {
    e.preventDefault(); // 기본 동작 방지
    e.stopPropagation(); // 부모 요소의 클릭 이벤트 전파 방지
    
    if (!isSelectionMode) {
      // 타이머가 있으면 취소 (더블클릭 감지)
      if (profileImageClickTimerRef.current) {
        clearTimeout(profileImageClickTimerRef.current);
        profileImageClickTimerRef.current = null;
      }
      
      setSelectedStudentForProfile(student);
      setIsProfileModalOpen(true);
      
      // 더블클릭 후 상태 초기화하여 부모 요소의 더블클릭 감지 방지
      setLastClickTime(0);
      setLastClickStudentId(null);
      lastProfileImageClickTimeRef.current = 0;
      lastProfileImageClickStudentIdRef.current = null;
    }
  };

  // 프로필 이미지 선택 확인
  const handleProfileImageConfirm = async (imageId: string) => {
    if (!user || !currentClass || !selectedStudentForProfile) return;

    try {
      // ✅ 낙관적 업데이트: 저장 직후 학생 블럭에 즉시 반영
      const targetStudentId = selectedStudentForProfile.studentId;
      setClassMembers(prev =>
        prev.map(m => (m.id === targetStudentId ? { ...m, profileImage: imageId } : m))
      );
      setTableData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          students: prev.students.map(s =>
            s.studentId === targetStudentId ? { ...s, profileImage: imageId } : s
          ),
          lastUpdated: new Date()
        };
      });
      // ✅ 자리 모드: seatArrangementData는 별도 상태라 동기 갱신 필요
      setSeatArrangementData((prev: any) => {
        if (!prev || !Array.isArray(prev)) return prev;
        return prev.map((s: any) =>
          s.studentId === targetStudentId ? { ...s, profileImage: imageId } : s
        );
      });

      await userDataService.updateMemberProfileImage(
        user.id,
        currentClass.id,
        selectedStudentForProfile.studentId,
        imageId,
        currentMemberGroup
      );

      // 데이터 정합성 맞춤(백그라운드): 대량 재조회라 체감 지연이 생길 수 있어 await 하지 않음
      // 프로필 변경 직후에는 다른 로딩과 겹쳐 loadData()가 무시될 수 있어 강제 새로고침 사용
      void loadData(true);
    } catch (err) {
      console.error('❌ 프로필 이미지 업데이트 실패:', err);
      alert('프로필 이미지 업데이트에 실패했습니다.');
    }
  };

  // 전체 선택
  const handleSelectAll = useCallback(() => {
    if (!tableData) return;
    const allStudentIds = new Set(tableData.students.map(student => student.studentId));
    setSelectedStudents(allStudentIds);
    // localStorage에 저장
    const storageKey = getSelectedStudentsStorageKey();
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(Array.from(allStudentIds)));
      } catch (error) {
        console.error('Failed to save selected students to localStorage:', error);
      }
    }
  }, [tableData, getSelectedStudentsStorageKey]);

  // 선택 취소
  const handleDeselectAll = useCallback(() => {
    setSelectedStudents(new Set());
    // localStorage에 저장
    const storageKey = getSelectedStudentsStorageKey();
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify([]));
      } catch (error) {
        console.error('Failed to save selected students to localStorage:', error);
      }
    }
  }, [getSelectedStudentsStorageKey]);

  // 멤버 개별 선택/해제
  const handleStudentSelection = useCallback((studentId: string) => {
    setSelectedStudents(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(studentId)) {
        newSelected.delete(studentId);
      } else {
        newSelected.add(studentId);
      }
      // localStorage에 저장
      const storageKey = getSelectedStudentsStorageKey();
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(Array.from(newSelected)));
        } catch (error) {
          console.error('Failed to save selected students to localStorage:', error);
        }
      }
      return newSelected;
    });
  }, [getSelectedStudentsStorageKey]);

  // 멤버별 노트 타입 토글
  const toggleStudentNoteType = (studentId: string) => {
    setStudentPointInputs(prev => {
      const current = prev[studentId] || { points: 0, memo: '', pointTypeId: '', relatedStudentIds: [] };
      const currentType = current.noteType || '일반';
      let nextType: '일반' | '학습' | '행동';
      
      switch (currentType) {
        case '일반': nextType = '학습'; break;
        case '학습': nextType = '행동'; break;
        case '행동': nextType = '일반'; break;
        default: nextType = '일반';
      }
      
      return {
        ...prev,
        [studentId]: {
          ...current,
          noteType: nextType
        }
      };
    });
  };

  // 선택 모드 토글
  const toggleSelectionMode = useCallback(() => {
    const newMode = !isSelectionMode;
    setIsSelectionMode(newMode);
    if (!newMode) {
      // 선택 모드 종료 시 선택 해제 및 입력 초기화
      setSelectedStudents(new Set());
      // localStorage에 저장
      const storageKey = getSelectedStudentsStorageKey();
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify([]));
        } catch (error) {
          console.error('Failed to save selected students to localStorage:', error);
        }
      }
      setStudentPointInputs({});
      // 노트 모드는 유지 (포인트 모드로 변경하지 않음)
    } else {
      // 선택 모드 시작 시 모든 멤버의 포인트를 0으로 초기화
      const initialInputs: {[key: string]: {points: number, memo: string, pointTypeId: string}} = {};
      tableData?.students.forEach(student => {
        initialInputs[student.studentId] = { points: 0, memo: '', pointTypeId: '' };
      });
      setStudentPointInputs(initialInputs);
    }
  }, [isSelectionMode, tableData, getSelectedStudentsStorageKey]);

  // 포인트 타입 로드
  const loadPointTypes = async () => {
    if (!pointTypeManagementService) return;
    
    try {
      const data = await pointTypeManagementService.getPointTypeData();
      setAvailablePointTypes({
        positive: data.positiveTypes,
        negative: data.negativeTypes
      });
    } catch (error) {
      console.error('포인트 타입 로드 실패:', error);
    }
  };

  // 타임 태그 로드
  const loadTimeTags = useCallback(async () => {
    if (!timeTagService || !user || !currentClass) return;

    try {
      const timeTags = await timeTagService.getTimeTags(user.id, currentClass.id);
      setAvailableTimeTags(timeTags);

      // 현재 요일과 시간에 맞는 타임 태그 자동 선택
      const now = new Date();
      const currentDayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][now.getDay()];
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      // 1단계: 현재 요일에 맞는 타임 태그 찾기
      let matchedTimeTag = timeTags.find(tag => {
        const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
        return days.includes(currentDayOfWeek as any) &&
               currentTime >= tag.startTime &&
               currentTime <= tag.endTime;
      });

      if (matchedTimeTag) {
        setSelectedTimeTag(matchedTimeTag);
        const days = Array.isArray(matchedTimeTag.dayOfWeek) ? matchedTimeTag.dayOfWeek : [matchedTimeTag.dayOfWeek];
        const selectedDay = days.includes(currentDayOfWeek as any) ? currentDayOfWeek : (days[0] as any);
        setSelectedDayOfWeek(selectedDay);
      } else {
        setSelectedTimeTag(null);
        setSelectedDayOfWeek(currentDayOfWeek);
      }
    } catch (error) {
      console.error('타임 태그 로드 실패:', error);
    }
  }, [timeTagService, user, currentClass]);


  // 🎯 자리 배치 데이터 로드
  const loadSeatArrangementData = useCallback(async () => {
    if (!user || !currentClass) {
      setSeatArrangementError('클래스 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
      
      // 현재 선택된 멤버 그룹의 ClassMembers 파티션에서 멤버 로드
      const partitionService = new ClassMembersPartitionService(user.id, currentClass.id, currentMemberGroup);
      const members = await partitionService.loadAllMembers();

      if (members.length === 0) {
        setSeatArrangementError('자리배치를 먼저 해주세요');
        setSeatArrangementData(null);
        return;
      }
      
      // 현재 월에 해당하는 자리 배치 찾기
      const currentDate = new Date();
      const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      // 자리 배치 데이터가 있는 월들 찾기
      const availableMonths = new Set<string>();
      members.forEach((member: any) => {
        if (member.seatPositions && typeof member.seatPositions === 'object') {
          // seatPositions은 객체 형태: { "2025-10": { row: 2, col: 3 }, "2025-09": { row: 1, col: 2 } }
          Object.keys(member.seatPositions).forEach((month: string) => {
            availableMonths.add(month);
          });
        }
      });

      let selectedMonth = currentMonth;
      
      // 현재 월이 없으면 가장 최근 월 선택
      if (!availableMonths.has(currentMonth)) {
        const sortedMonths = Array.from(availableMonths).sort().reverse();
        if (sortedMonths.length === 0) {
          setSeatArrangementError('자리배치를 먼저 해주세요');
          setSeatArrangementData(null);
          return;
        }
        selectedMonth = sortedMonths[0];
      }

      // 선택된 월의 자리 배치 데이터 추출
      const seatData = tableData?.students.map((student: any) => {
        const member = members.find((m: any) => m.id === student.studentId);
        const seatPosition = member?.seatPositions?.[selectedMonth]; // 객체에서 월별 데이터 추출
        
        return {
          studentId: student.studentId,
          name: student.studentName || '이름 없음',
          number: student.studentNumber,
          seatRow: seatPosition?.row || 0,
          seatCol: seatPosition?.col || 0,
          profileImage: student.profileImage,
          totalPoints: student.totalPoints || 0
        };
      }) || [];

      setSeatArrangementData(seatData);
      setSeatArrangementError('');
    } catch (error) {
      console.error('❌ [자리배치] 자리 배치 데이터 로드 실패:', error);
      setSeatArrangementError('자리배치를 먼저 해주세요');
      setSeatArrangementData(null);
    }
  }, [user, currentClass, currentMemberGroup, tableData]);

  // 포인트 지급 모달 열기
  const handleOpenPointGrantModal = async () => {
    if (selectedStudents.size === 0) {
      alert(displayMode === 'coins' ? '코인을 지급할 멤버를 선택해주세요.' : displayMode === 'notes' ? '노트를 작성할 멤버를 선택해주세요.' : '포인트를 지급할 멤버를 선택해주세요.');
      return;
    }
    
    if (displayMode === 'coins') {
      // 코인 모드일 때는 일괄 코인 지급 모달 열기
      setShowBulkCoinGrantModal(true);
    } else if (displayMode === 'notes') {
      // 노트 모드일 때는 별도 처리 (추후 구현)
    } else {
      // 포인트 모드일 때는 기존 포인트 지급 모달 열기
      await loadPointTypes();
      setShowPointGrantModal(true);
    }
  };

  // 코인 개별 저장 핸들러
  const handleSaveAllCoins = async () => {
    if (!user || !currentClass || !tableData) return;
    
    try {
      const changedStudents = Object.keys(studentPointInputs).filter(studentId => {
        const current = studentPointInputs[studentId];
        const initial = initialStudentData[studentId];
        
        if (!initial) {
          return current && (current.points !== 0 || (current.memo || '').trim() !== '');
        }
        
        return current.points !== initial.points || current.memo !== initial.memo;
      });

      if (changedStudents.length === 0) {
        alert('저장할 변경사항이 없습니다.');
        return;
      }

      const validStudents = changedStudents.filter(studentId => {
        const input = studentPointInputs[studentId];
        return input.points !== 0 || (input.memo || '').trim() !== '';
      });

      if (validStudents.length === 0) {
        alert('저장할 코인 데이터가 없습니다.');
        return;
      }

      // CoinManagementService를 사용하여 ClassMembersCoin에 저장
      const coinManagementService = new CoinManagementService(user.id, currentClass.id, currentMemberGroup);
      
      // 각 멤버에게 코인 지급
      for (const studentId of validStudents) {
        const input = studentPointInputs[studentId];
        const memo = (input.memo || '').trim();
        await coinManagementService.grantCoinsToStudent(studentId, input.points, memo, 'General');
      }

      // 개별 저장의 경우에도 되돌리기 정보 저장
      // 각 멤버의 개별 정보를 저장 (마지막 저장된 멤버들의 정보)
      if (validStudents.length > 0) {
        const studentCoinGrants = validStudents.map(studentId => {
          const input = studentPointInputs[studentId];
          return {
            studentId,
            amount: input.points,
            memo: input.memo || ''
          };
        });
        
        setLastCoinGrant({
          studentIds: validStudents,
          amount: 0, // 개별 저장이므로 0으로 설정
          memo: '', // 개별 저장이므로 빈 문자열로 설정
          individualGrants: studentCoinGrants // 개별 정보 저장
        });
      }

      showToastMessage(`${validStudents.length}명의 멤버 코인을 저장했습니다.`);
      
      // 저장 후 상태 초기화
      setStudentPointInputs({});
      setInitialStudentData({});
      setStudentNegativeModes({}); // 음수 모드 상태도 초기화
      
      // 데이터 새로고침
      await loadData();
      
    } catch (error) {
      console.error('코인 저장 실패:', error);
      alert('코인 저장에 실패했습니다.');
    }
  };

  // 일괄 코인 테이블 반영 핸들러 (멤버 블럭에만 입력)
  const handleBulkCoinTableReflect = () => {
    if (selectedStudents.size === 0) return;
    if (bulkCoinAmount === 0 || !bulkCoinMemo.trim()) {
      alert('코인 금액과 메모를 입력해주세요.');
      return;
    }

    try {
      // 선택된 멤버들의 블럭에 코인 입력
      const newStudentInputs = { ...studentPointInputs };
      
      Array.from(selectedStudents).forEach(studentId => {
        newStudentInputs[studentId] = {
          points: bulkCoinAmount,
          memo: bulkCoinMemo.trim(),
          pointTypeId: '' // 코인 모드에서는 포인트 타입 없음
        };
      });
      
      setStudentPointInputs(newStudentInputs);
      
      // 모달 닫기 및 초기화
      setShowBulkCoinGrantModal(false);
      setBulkCoinAmount(10);
      setBulkCoinMemo('');
      setIsBulkCoinNegativeMode(false);
      
      alert(`선택된 ${selectedStudents.size}명의 멤버 블럭에 코인 정보가 반영되었습니다.`);
    } catch (error) {
      console.error('코인 테이블 반영 실패:', error);
      alert('코인 테이블 반영에 실패했습니다.');
    }
  };

  // 일괄 코인 지급 핸들러
  const handleBulkCoinGrant = async () => {
    if (!user || !currentClass || selectedStudents.size === 0) return;
    if (bulkCoinAmount === 0 || !bulkCoinMemo.trim()) {
      alert('코인 금액과 메모를 입력해주세요.');
      return;
    }

    try {
      const { StudentCoinService } = await import('../services/studentCoinService');
      const coinService = new StudentCoinService(user.id, currentClass.id, currentMemberGroup);
      
      const studentIds = Array.from(selectedStudents);
      await coinService.grantCoinsToMultipleStudents(
        studentIds, 
        bulkCoinAmount, 
        bulkCoinMemo,
        'General'
      );

      // 마지막 지급 정보 저장 (되돌리기용)
      setLastCoinGrant({
        studentIds: studentIds,
        amount: bulkCoinAmount,
        memo: bulkCoinMemo
      });
      
      // 모달 닫기 및 초기화
      setShowBulkCoinGrantModal(false);
      setBulkCoinAmount(10);
      setBulkCoinMemo('');
      setIsBulkCoinNegativeMode(false); // 음수 모드 상태도 초기화
      
      // 데이터 새로고침
      await loadData();
      
      alert(`${selectedStudents.size}명의 멤버에게 ${bulkCoinAmount}코인을 지급했습니다.`);
    } catch (error) {
      console.error('일괄 코인 지급 실패:', error);
      alert('코인 지급에 실패했습니다.');
    }
  };

  // 포인트 지급 모달 닫기
  const handleClosePointGrantModal = () => {
    setShowPointGrantModal(false);
    setPointType('일반');
    setPointValue('');
    setPointMemo('');
    setSelectedPointTypeId('');
    setBulkPointTypeId('');
  };

  // 포인트 블럭 반영 처리 (멤버 블록에만 입력, 서버 저장하지 않음)
  const handleGrantPointsToBlocks = () => {
    if (!pointValue.trim()) {
      alert('지급할 포인트를 입력해주세요.');
      return;
    }
    
    const points = parseInt(pointValue);
    if (isNaN(points) || points <= 0) {
      alert('올바른 포인트 값을 입력해주세요.');
      return;
    }

    try {
      // 선택된 멤버들의 블록에 포인트 입력
      const newStudentInputs = { ...studentPointInputs };
      
      Array.from(selectedStudents).forEach(studentId => {
        // 지급한 포인트만 입력 (기존 값과 관계없이)
        const newPoints = points;
        
        newStudentInputs[studentId] = {
          points: newPoints,
          memo: pointMemo.trim(),
          pointTypeId: bulkPointTypeId || selectedPointTypeId
        };
        
        // 초기 상태도 설정 (아직 설정되지 않은 경우)
        if (!initialStudentData[studentId]) {
          setInitialStudentData(prev => ({
            ...prev,
            [studentId]: { points: 0, memo: '', pointTypeId: '' }
          }));
        }
      });

      setStudentPointInputs(newStudentInputs);

      // 선택 영역 모두 취소
      setSelectedStudents(new Set());
      // localStorage에 저장
      const storageKey = getSelectedStudentsStorageKey();
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify([]));
        } catch (error) {
          console.error('Failed to save selected students to localStorage:', error);
        }
      }
      
      // 모달 닫기
      handleClosePointGrantModal();
      
    } catch (error) {
      console.error('포인트 입력 실패:', error);
      alert('포인트 입력에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 포인트 실제 지급 처리 (서버에 저장)
  const handleGrantPoints = async () => {
    if (!user || !currentClass || selectedStudents.size === 0) return;
    if (!pointValue.trim()) {
      alert('지급할 포인트를 입력해주세요.');
      return;
    }
    
    const points = parseInt(pointValue);
    if (isNaN(points) || points <= 0) {
      alert('올바른 포인트 값을 입력해주세요.');
      return;
    }

    try {
      const { PointManagementService } = await import('../services/pointManagementService');
      const pointService = new PointManagementService(user.id, currentClass.id, currentMemberGroup);
      
      // 포인트 타입 ID 확인 및 기본값 설정
      let pointTypeId = bulkPointTypeId || selectedPointTypeId;
      if (!pointTypeId) {
        // 포인트 타입을 선택하지 않으면 자동으로 긍정행동 포인트로 설정
        pointTypeId = 'positive-default';
      }
      
      // 선택된 멤버들에게 포인트 지급
      const studentIds = Array.from(selectedStudents);
      
      // 멤버 이름과 번호 정보 가져오기
      const studentNames: { [key: string]: string } = {};
      const studentNumbers: { [key: string]: string } = {};
      
      if (tableData) {
        studentIds.forEach(studentId => {
          const student = tableData.students.find(s => s.studentId === studentId);
          if (student) {
            studentNames[studentId] = student.studentName || `멤버 ${studentId.slice(-4)}`;
            studentNumbers[studentId] = student.studentNumber || '';
          }
        });
      }
      
      // 포인트 카테고리 결정
      let pointCategory: 'positive' | 'negative' = 'positive'; // 기본값은 positive

      if (pointTypeId === 'positive-default') {
        pointCategory = 'positive';
      } else if (pointTypeId === 'negative-default') {
        pointCategory = 'negative';
      } else {
        // 커스텀 포인트 타입인 경우
        const allTypes = [...availablePointTypes.positive, ...availablePointTypes.negative];
        const selectedType = allTypes.find(type => type.id === pointTypeId);
        pointCategory = selectedType?.category === 'positive' ? 'positive' : 'negative';
      }
      
      await pointService.grantPointsToStudents(
        studentIds,
        {
          pointValue: points,
          pointMemo: pointMemo.trim(),
          pointType: pointTypeId,
          recordedDate: new Date(),
          pointCategory: pointCategory
        },
        studentNames,
        studentNumbers
      );

      // 포인트/레벨 즉시 반영: tableData 낙관적 갱신 (코인처럼 조용히 반영)
      const pointDelta = pointCategory === 'positive' ? points : -points;
      setTableData(prev => {
        if (!prev) return prev;
        const updatedStudents = prev.students.map(s => {
          if (!studentIds.includes(s.studentId)) return s;
          return { ...s, totalPoints: (s.totalPoints || 0) + pointDelta };
        });
        const newTotalPoints = updatedStudents.reduce((sum, s) => sum + (s.totalPoints || 0), 0);
        return {
          ...prev,
          students: updatedStudents,
          totalPoints: newTotalPoints,
          averagePoints: prev.totalStudents > 0 ? Math.round(newTotalPoints / prev.totalStudents) : prev.averagePoints
        };
      });

      // 모달 닫기 및 초기화
      handleClosePointGrantModal();

      // 선택 영역 모두 취소
      setSelectedStudents(new Set());
      // localStorage에 저장
      const storageKey = getSelectedStudentsStorageKey();
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify([]));
        } catch (error) {
          console.error('Failed to save selected students to localStorage:', error);
        }
      }

      // 날짜와 타임 태그는 초기화하지 않음 (사용자가 선택한 값 유지)

      // 캐시 무효화 후 백그라운드에서 데이터 동기화 (화면은 이미 낙관 반영됨)
      userDataService.invalidateMembersCache(user.id, currentClass.id, currentMemberGroup);
      pointService.invalidatePointDocumentsCache();
      loadData();

      alert(`${selectedStudents.size}명의 멤버에게 ${points}포인트를 지급했습니다.`);
    } catch (error) {
      console.error('포인트 지급 실패:', error);
      alert('포인트 지급에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 변경사항이 있는지 확인하는 함수
  const hasUnsavedChanges = (): boolean => {
    // 노트 타입 변경 확인
    const noteTypeChanged = selectedNoteType !== initialNoteType;
    
    // 멤버 입력 데이터 변경 확인
    const studentDataChanged = Object.keys(studentPointInputs).some(studentId => {
      const current = studentPointInputs[studentId];
      const initial = initialStudentData[studentId];
      
      if (!initial) {
        // 초기 상태가 없으면 변경사항이 있다고 판단
        return current && (current.points !== 0 || (current.memo || '').trim() !== '' || current.pointTypeId !== '' || current.noteType !== undefined);
      }
      
      return current.points !== initial.points || 
             current.memo !== initial.memo || 
             current.pointTypeId !== initial.pointTypeId ||
             current.noteType !== initial.noteType;
    });
    
    return noteTypeChanged || studentDataChanged;
  };

  // 노트 저장 함수
  const handleSaveNotes = async () => {
    if (!user || !currentClass || !tableData) return;
    
    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      
      const notesToSave = Object.entries(studentPointInputs).filter(([_, input]) => 
        input.memo && input.memo.trim() !== ''
      );
      
      if (notesToSave.length === 0) {
        alert('저장할 노트가 없습니다.');
        return;
      }
      
      // ClassMembersNote 컬렉션이 초기화되지 않은 경우 초기화
      try {
        const existingMembers = await noteService.loadAllMembers();
        if (existingMembers.length === 0) {
          console.log('📚 ClassMembersNote 컬렉션 초기화 중...');
          // 현재 클래스 멤버들로 초기화
          const classMembers: Array<{ id: string }> = tableData.students.map(student => ({
            id: student.studentId
          }));
          await noteService.initializeClassNotes(classMembers);
        }
      } catch (initError) {
        console.error('ClassMembersNote 초기화 실패:', initError);
        // 초기화 실패해도 계속 진행
      }
      
      for (const [studentId, input] of notesToSave) {
        // 날짜 버튼의 날짜와 현재 시각을 결합
        const selectedDateObj = new Date(selectedDate);
        const now = new Date();
        
        // 날짜는 선택한 날짜, 시간은 현재 시각
        const recordedDate = new Date(
          selectedDateObj.getFullYear(),
          selectedDateObj.getMonth(),
          selectedDateObj.getDate(),
          now.getHours(),
          now.getMinutes(),
          now.getSeconds()
        );
        
        const timeTagStr = selectedTimeTag 
          ? formatTimeTagLabel(selectedTimeTag, selectedDayOfWeek)
          : '';
        
        await noteService.addObservationNote(studentId, {
          content: input.memo,
          relatedStudents: input.relatedStudents || [],
          recordedDate: recordedDate,
          timeTag: timeTagStr,
          noteType: input.noteType || '일반'
        });
      }
      
      showToastMessage(`${notesToSave.length}개의 노트를 저장했습니다.`);

      // 저장 후 최신 데이터 다시 로드 (캐시된 데이터 갱신)
      await loadData();

      // 저장 후 상태 초기화
      setStudentPointInputs({});
      setInitialStudentData({});
      
    } catch (error) {
      console.error('노트 저장 실패:', error);
      alert('노트 저장에 실패했습니다.');
    }
  };

  // 멤버 태그 선택 모달 열기
  const handleOpenStudentTagModal = (studentId: string) => {
    setCurrentNoteStudentId(studentId);
    setShowStudentTagModal(true);
  };

  // 멤버 태그 선택 확인
  const handleStudentTagConfirm = (selectedStudents: RelatedStudentInfo[]) => {
    if (currentNoteStudentId) {
      setStudentPointInputs(prev => ({
        ...prev,
        [currentNoteStudentId]: {
          ...prev[currentNoteStudentId],
          relatedStudents: selectedStudents // 변경: relatedStudentIds → relatedStudents
        }
      }));
    }
    setShowStudentTagModal(false);
    setCurrentNoteStudentId('');
  };

  // 일괄 노트 작성 모달 열기
  const handleOpenBulkNoteModal = () => {
    // 현재 선택된 멤버들을 노트를 받을 멤버으로 자동 설정 (코인 일괄 지급과 동일)
    setBulkNoteContent('');
    setBulkNoteSelectedStudents(new Set(selectedStudents));
    setBulkNoteRelatedStudents([]);
    setBulkNoteType('일반'); // 노트 타입 초기화
    setShowBulkNoteModal(true);
  };

  // 일괄 노트 작성 모달 닫기
  const handleCloseBulkNoteModal = () => {
    setShowBulkNoteModal(false);
    setBulkNoteContent('');
    setBulkNoteSelectedStudents(new Set());
    setBulkNoteRelatedStudents([]);
    setBulkNoteType('일반'); // 노트 타입 초기화
  };

  // 일괄 노트용 멤버 선택 모달 열기
  const handleOpenBulkNoteStudentSelectModal = () => {
    setShowBulkNoteStudentSelectModal(true);
  };

  // 일괄 노트용 멤버 선택 확인
  const handleBulkNoteStudentConfirm = (selectedStudents: RelatedStudentInfo[]) => {
    setBulkNoteRelatedStudents(selectedStudents);
    setShowBulkNoteStudentSelectModal(false);
  };

  // 일괄 노트 테이블 반영
  const handleBulkNoteApplyToTable = () => {
    if (bulkNoteContent.trim() === '') {
      alert('기록 내용을 입력해주세요.');
      return;
    }

    if (bulkNoteSelectedStudents.size === 0) {
      alert('노트를 받을 멤버를 선택해주세요.');
      return;
    }

    // 관련멤버은 선택사항이므로 제거

    // 선택된 멤버들의 테이블에 노트 내용과 관련 멤버 데이터 반영
    const updatedInputs = { ...studentPointInputs };
    const selectedStudentIds = Array.from(bulkNoteSelectedStudents);
    const relatedStudentIds = Array.from(bulkNoteRelatedStudents);
    
    // 관련 학생들의 정보를 가져옴
    const relatedStudents: RelatedStudentInfo[] = relatedStudentIds.map(id => {
      const member = classMembers.find(m => m.id === id);
      return member ? {
        id: member.id,
        name: member.name,
        number: member.number,
        groupName: currentMemberGroup || 'A'
      } : {
        id: id,
        name: '알 수 없음',
        number: '??',
        groupName: '삭제됨'
      };
    });

    selectedStudentIds.forEach(studentId => {
      // 각 멤버에 대해 자기 자신을 제외한 관련 멤버들을 설정
      const studentRelatedStudents = relatedStudents.filter(student => student.id !== studentId);

      updatedInputs[studentId] = {
        ...updatedInputs[studentId],
        memo: bulkNoteContent,
        points: 0,
        pointTypeId: '',
        relatedStudents: studentRelatedStudents,
        noteType: bulkNoteType
      };
    });
    setStudentPointInputs(updatedInputs);
    
    alert('테이블에 반영되었습니다.');
    handleCloseBulkNoteModal();
  };

  // 일괄 노트 저장
  const handleBulkNoteSave = async () => {
    if (!user || !currentClass) return;
    
    if (bulkNoteContent.trim() === '') {
      alert('기록 내용을 입력해주세요.');
      return;
    }

    if (bulkNoteSelectedStudents.size === 0) {
      alert('노트를 받을 멤버를 선택해주세요.');
      return;
    }

    // 관련멤버은 선택사항이므로 제거

    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      
      // 선택된 멤버들에 대해 노트 저장
      const selectedStudentIds = Array.from(bulkNoteSelectedStudents);
      const relatedStudentIds = Array.from(bulkNoteRelatedStudents);

      // 관련 학생들의 정보를 가져옴
      const relatedStudents: RelatedStudentInfo[] = relatedStudentIds.map(id => {
        const member = classMembers.find(m => m.id === id);
        return member ? {
          id: member.id,
          name: member.name,
          number: member.number,
          groupName: currentMemberGroup || 'A'
        } : {
          id: id,
          name: '알 수 없음',
          number: '??',
          groupName: '삭제됨'
        };
      });

      for (const studentId of selectedStudentIds) {
        // 각 멤버에 대해 자기 자신을 제외한 관련 멤버들을 설정
        const studentRelatedStudents = relatedStudents.filter(student => student.id !== studentId);

        await noteService.addObservationNote(studentId, {
          content: bulkNoteContent,
          relatedStudents: studentRelatedStudents,
          recordedDate: new Date(selectedDate),
          timeTag: selectedTimeTag ? formatTimeTagLabel(selectedTimeTag, selectedDayOfWeek) : '',
          noteType: bulkNoteType
        });
      }
      
      // 학생 블록의 기록 개수 즉시 반영
      loadStudentNoteCounts(Array.from(selectedStudentIds));
      alert('노트가 저장되었습니다.');
      handleCloseBulkNoteModal();
    } catch (error) {
      console.error('일괄 노트 저장 실패:', error);
      alert('노트 저장에 실패했습니다.');
    }
  };

  // 개별 노트 관리 모달 실제 열기 (데이터 로드 후 모달 표시)
  const openNoteManagementModalForStudent = useCallback(async (studentId: string) => {
    if (!user || !currentClass) return;
    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      const studentNotes = await noteService.getStudentNotes(studentId);
      setCurrentNoteManagementStudentId(studentId);
      const loadedMainNotes = studentNotes?.mainNotes || [];
      setMainNotes(loadedMainNotes.length > 0 ? loadedMainNotes : ['']);
      setOriginalMainNotes(loadedMainNotes);
      setMainNoteCache(loadedMainNotes);
      setCurrentMainNoteIndex(0);
      const titles = loadedMainNotes.length > 0
        ? loadedMainNotes.map((_, index) => `기록 ${index + 1}`)
        : ['기록 1'];
      const storageKey = `mainNoteTitles_${user.id}_${currentClass.id}_${studentId}`;
      try {
        const savedTitles = localStorage.getItem(storageKey);
        if (savedTitles) {
          const parsedTitles = JSON.parse(savedTitles);
          const mergedTitles = loadedMainNotes.map((_, index) =>
            parsedTitles[index] || `노트 ${index + 1}`
          );
          setMainNoteTitles(mergedTitles);
        } else {
          setMainNoteTitles(titles);
        }
      } catch (error) {
        console.error('제목 불러오기 실패:', error);
        setMainNoteTitles(titles);
      }
      setEditingMainNoteTitleIndex(null);
      setEditingMainNoteTitleValue('');
      setObservationNotes(studentNotes?.observationNotes || []);
      setShowAddObservationNote(true);
      setNewObservationNoteContent('');
      setEditingObservationNoteId('');
      setEditingObservationNoteContent('');
      setEditingObservationNoteDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
      setCurrentObservationNotePage(1);
      setHasUnsavedMainNoteChanges(false);
      setModalSelectedDate(new Date(selectedDate));
      setModalSelectedTimeTag(selectedTimeTag);
      setModalSelectedDayOfWeek(selectedDayOfWeek);
      setModalRelatedStudents([]);
      setObservationListTypeFilter('일반');
      setObservationEditBaseline(null);
      setShowNoteManagementModal(true);
    } catch (error) {
      console.error('노트 로드 실패:', error);
      alert('노트를 불러오는데 실패했습니다.');
    }
  }, [user, currentClass, currentMemberGroup, selectedDate, selectedTimeTag, selectedDayOfWeek]);

  // 개별 노트 관리 모달 열기 (잠금 시 비밀번호 모달 먼저 표시)
  const handleOpenNoteManagementModal = useCallback(async (studentId: string) => {
    if (!user || !currentClass) return;
    if (individualRecordViewLocked) {
      setPendingRecordViewStudentId(studentId);
      setRecordViewPasswordInput('');
      setRecordViewPasswordError('');
      setShowRecordViewPasswordModal(true);
      return;
    }
    await openNoteManagementModalForStudent(studentId);
  }, [user, currentClass, individualRecordViewLocked, openNoteManagementModalForStudent]);

  // 개별 기록 열람 비밀번호 모달 확인
  const handleRecordViewPasswordSubmit = useCallback(async () => {
    if (!pendingRecordViewStudentId.trim()) return;
    if (!recordViewPasswordInput.trim()) {
      setRecordViewPasswordError('비밀번호를 입력해 주세요.');
      return;
    }
    if (!individualRecordViewLockPasswordHash) {
      setRecordViewPasswordError('잠금 설정을 찾을 수 없습니다.');
      return;
    }
    try {
      const inputHash = await hashLockPassword(recordViewPasswordInput.trim());
      if (inputHash !== individualRecordViewLockPasswordHash) {
        setRecordViewPasswordError('비밀번호가 올바르지 않습니다.');
        return;
      }
      const studentId = pendingRecordViewStudentId;
      setShowRecordViewPasswordModal(false);
      setPendingRecordViewStudentId('');
      setRecordViewPasswordInput('');
      setRecordViewPasswordError('');
      await openNoteManagementModalForStudent(studentId);
    } catch (e) {
      console.error('비밀번호 확인 실패:', e);
      setRecordViewPasswordError('확인 중 오류가 발생했습니다.');
    }
  }, [pendingRecordViewStudentId, recordViewPasswordInput, individualRecordViewLockPasswordHash, openNoteManagementModalForStudent]);

  const noteManagementHasDiscardableChanges = () => {
    if (hasUnsavedMainNoteChanges || editingMainNoteTitleIndex !== null) return true;
    if (editingObservationNoteId) {
      if (!observationEditBaseline) return false;
      return (
        getObservationDirtyPayload(
          editingObservationNoteContent,
          editingObservationNoteDate,
          editingObservationNoteTimeTag,
          editingObservationNoteDayOfWeek,
          editingObservationNoteRelatedStudents,
          selectedObservationTab
        ) !== observationEditBaseline
      );
    }
    return newObservationNoteContent.trim() !== '' || modalRelatedStudents.length > 0;
  };

  const performCloseNoteManagementModal = () => {
    setShowNoteManagementCloseConfirm(false);
    setShowNoteManagementModal(false);
    setCurrentNoteManagementStudentId('');
    setMainNotes([]);
    setOriginalMainNotes([]);
    setMainNoteCache([]);
    setCurrentMainNoteIndex(0);
    setMainNoteTitles([]);
    setEditingMainNoteTitleIndex(null);
    setEditingMainNoteTitleValue('');
    setShowDeleteMainNoteConfirm(false);
    setShowAddMainNoteTitleModal(false);
    setNewMainNoteTitle('');
    setObservationNotes([]);
    setShowAddObservationNote(true);
    setNewObservationNoteContent('');
    setEditingObservationNoteId('');
    setEditingObservationNoteContent('');
    setEditingObservationNoteDate(new Date());
    setEditingObservationNoteTimeTag(null);
    setEditingObservationNoteRelatedStudents([]);
    setCurrentObservationNotePage(1);
    setEditingObservationNoteDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
    setHasUnsavedMainNoteChanges(false);
    setModalRelatedStudents([]);
    setModalSelectedDate(new Date());
    setModalSelectedTimeTag(null);
    setModalSelectedDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
    setShowModalCalendar(false);
    setShowModalTimeTagSelect(false);
    setShowModalStudentTagSelect(false);
    setSelectedObservationTab('일반');
    setObservationListTypeFilter('일반');
    setObservationEditBaseline(null);
  };

  const handleRequestCloseNoteManagementModal = () => {
    if (noteManagementHasDiscardableChanges()) {
      setShowNoteManagementCloseConfirm(true);
      return;
    }
    performCloseNoteManagementModal();
  };

  const handleDismissNoteManagementCloseConfirm = () => {
    setShowNoteManagementCloseConfirm(false);
  };

  const handleCloseNoteManagementWithoutSave = () => {
    setShowNoteManagementCloseConfirm(false);
    performCloseNoteManagementModal();
  };

  const handleSaveAndCloseNoteManagementModal = async () => {
    setShowNoteManagementCloseConfirm(false);
    handleMainNoteTitleBlur();
    if (hasUnsavedMainNoteChanges) {
      const ok = await handleSaveMainNote();
      if (!ok) return;
    }
    if (editingObservationNoteId && observationEditSaveEnabled) {
      const ok = await handleEditObservationNote(editingObservationNoteId);
      if (!ok) return;
    }
    if (!editingObservationNoteId && observationAddSaveEnabled) {
      const ok = await handleAddObservationNote();
      if (!ok) return;
    }
    performCloseNoteManagementModal();
  };

  // 메인 노트 저장 (모든 변경사항 일괄 저장)
  const handleSaveMainNote = async (): Promise<boolean> => {
    if (!user || !currentClass || !currentNoteManagementStudentId) return false;
    
    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      // 빈 노트 제거 후 저장
      const notesToSave = mainNoteCache.filter(note => note.trim() !== '');
      await noteService.updateMainNotes(currentNoteManagementStudentId, notesToSave);
      setMainNotes(notesToSave.length > 0 ? notesToSave : ['']);
      setOriginalMainNotes(notesToSave);
      setMainNoteCache(notesToSave);
      
      // 제목 배열 길이 조정
      const updatedTitles = [...mainNoteTitles];
      while (updatedTitles.length < notesToSave.length) {
        updatedTitles.push(`노트 ${updatedTitles.length + 1}`);
      }
      while (updatedTitles.length > notesToSave.length && notesToSave.length > 0) {
        updatedTitles.pop();
      }
      if (notesToSave.length === 0) {
        updatedTitles.length = 0;
        updatedTitles.push('노트 1');
      }
      setMainNoteTitles(updatedTitles);
      
      // 제목 localStorage에 저장
      const storageKey = `mainNoteTitles_${user.id}_${currentClass.id}_${currentNoteManagementStudentId}`;
      try {
        localStorage.setItem(storageKey, JSON.stringify(updatedTitles));
      } catch (error) {
        console.error('제목 저장 실패:', error);
      }
      
      // 인덱스 조정 (저장 후 배열 길이에 맞춰)
      if (currentMainNoteIndex >= notesToSave.length && notesToSave.length > 0) {
        setCurrentMainNoteIndex(notesToSave.length - 1);
      } else if (notesToSave.length === 0) {
        setCurrentMainNoteIndex(0);
        setMainNotes(['']);
      }
      setHasUnsavedMainNoteChanges(false);
      alert('개별 종합 기록이 저장되었습니다.');
      return true;
    } catch (error) {
      console.error('개별 종합 기록 저장 실패:', error);
      alert('개별 종합 기록 저장에 실패했습니다.');
      return false;
    }
  };

  // 메인 노트 제목 더블클릭 편집 시작
  const handleMainNoteTitleDoubleClick = (index: number) => {
    setEditingMainNoteTitleIndex(index);
    setEditingMainNoteTitleValue(mainNoteTitles[index] || `기록 ${index + 1}`);
  };

  // 메인 노트 제목 편집 완료
  const handleMainNoteTitleBlur = () => {
    if (editingMainNoteTitleIndex === null) return;
    
    const updatedTitles = [...mainNoteTitles];
    while (updatedTitles.length <= editingMainNoteTitleIndex) {
      updatedTitles.push(`노트 ${updatedTitles.length + 1}`);
    }
    updatedTitles[editingMainNoteTitleIndex] = editingMainNoteTitleValue.trim() || `노트 ${editingMainNoteTitleIndex + 1}`;
    setMainNoteTitles(updatedTitles);
    
    // 제목 localStorage에 저장
    if (user && currentClass && currentNoteManagementStudentId) {
      const storageKey = `mainNoteTitles_${user.id}_${currentClass.id}_${currentNoteManagementStudentId}`;
      try {
        localStorage.setItem(storageKey, JSON.stringify(updatedTitles));
      } catch (error) {
        console.error('제목 저장 실패:', error);
      }
    }
    
    setEditingMainNoteTitleIndex(null);
    setEditingMainNoteTitleValue('');
  };

  // 메인 노트 제목 편집 취소
  const handleMainNoteTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleMainNoteTitleBlur();
    } else if (e.key === 'Escape') {
      setEditingMainNoteTitleIndex(null);
      setEditingMainNoteTitleValue('');
    }
  };

  // 존재하는 메모 인덱스 목록 가져오기 (내용이 있는 메모만)
  // 삭제/추가 반영을 위해 mainNotes와 mainNoteCache만 사용 (originalMainNotes는 서버 원본이므로 제외)
  const getExistingNoteIndices = (): number[] => {
    const indices: number[] = [];
    // 실제로 존재하는 배열의 최대 길이만 확인 (삭제된 노트는 탐색하지 않음)
    const maxLength = Math.max(mainNotes.length, mainNoteCache.length);
    
    for (let i = 0; i < maxLength; i++) {
      // mainNoteCache와 mainNotes만 확인 (originalMainNotes 제외)
      const noteContent = mainNoteCache[i] || mainNotes[i] || '';
      if (noteContent.trim() !== '') {
        indices.push(i);
      }
    }
    return indices;
  };

  // 메인 노트 네비게이션 (이전) - 존재하는 메모만 순회
  const handlePreviousMainNote = () => {
    // 현재 노트의 변경사항을 캐시에 저장
    const updatedCache = [...mainNoteCache];
    while (updatedCache.length <= currentMainNoteIndex) {
      updatedCache.push('');
    }
    updatedCache[currentMainNoteIndex] = mainNotes[currentMainNoteIndex] || '';
    setMainNoteCache(updatedCache);
    
    // 존재하는 메모 인덱스 목록 가져오기
    const existingIndices = getExistingNoteIndices();
    
    // 현재 인덱스보다 이전의 존재하는 메모 찾기
    const prevIndex = existingIndices.filter(idx => idx < currentMainNoteIndex).pop();
    
    if (prevIndex !== undefined) {
      setCurrentMainNoteIndex(prevIndex);
      
      // 이전 노트가 mainNotes 배열에 없으면 캐시에서 로드
      const updatedNotes = [...mainNotes];
      while (updatedNotes.length <= prevIndex) {
        updatedNotes.push('');
      }
      updatedNotes[prevIndex] = updatedCache[prevIndex] || '';
      setMainNotes(updatedNotes);
      
      // 변경사항 체크
      const hasChanges = JSON.stringify(updatedCache) !== JSON.stringify(originalMainNotes);
      setHasUnsavedMainNoteChanges(hasChanges);
    }
  };

  // 메인 노트 네비게이션 (다음) - 존재하는 메모만 순회
  const handleNextMainNote = () => {
    // 현재 노트의 변경사항을 캐시에 저장
    const updatedCache = [...mainNoteCache];
    while (updatedCache.length <= currentMainNoteIndex) {
      updatedCache.push('');
    }
    updatedCache[currentMainNoteIndex] = mainNotes[currentMainNoteIndex] || '';
    setMainNoteCache(updatedCache);
    
    // 존재하는 메모 인덱스 목록 가져오기
    const existingIndices = getExistingNoteIndices();
    
    // 현재 인덱스보다 다음의 존재하는 메모 찾기
    const nextIndex = existingIndices.find(idx => idx > currentMainNoteIndex);
    
    if (nextIndex !== undefined) {
      setCurrentMainNoteIndex(nextIndex);
      
      // 다음 노트가 mainNotes 배열에 없으면 캐시에서 로드
      const updatedNotes = [...mainNotes];
      while (updatedNotes.length <= nextIndex) {
        updatedNotes.push('');
      }
      updatedNotes[nextIndex] = updatedCache[nextIndex] || '';
      setMainNotes(updatedNotes);
      
      // 변경사항 체크
      const hasChanges = JSON.stringify(updatedCache) !== JSON.stringify(originalMainNotes);
      setHasUnsavedMainNoteChanges(hasChanges);
    }
  };

  /** 기록 탭 클릭: 현재 편집 내용을 캐시에 반영한 뒤 선택 인덱스로 전환 */
  const handleSelectMainNoteIndex = (index: number) => {
    if (index === currentMainNoteIndex) return;

    const updatedCache = [...mainNoteCache];
    while (updatedCache.length <= currentMainNoteIndex) {
      updatedCache.push('');
    }
    updatedCache[currentMainNoteIndex] = mainNotes[currentMainNoteIndex] || '';
    setMainNoteCache(updatedCache);

    setCurrentMainNoteIndex(index);

    const updatedNotes = [...mainNotes];
    while (updatedNotes.length <= index) {
      updatedNotes.push('');
    }
    updatedNotes[index] = updatedCache[index] ?? updatedNotes[index] ?? '';
    setMainNotes(updatedNotes);

    const hasChanges = JSON.stringify(updatedCache) !== JSON.stringify(originalMainNotes);
    setHasUnsavedMainNoteChanges(hasChanges);
  };

  // 현재 메인 노트 변경시 캐시 업데이트 및 변경사항 추적
  const handleMainNoteChange = (value: string) => {
    const updatedNotes = [...mainNotes];
    // 현재 인덱스가 배열 범위를 벗어나면 확장
    while (updatedNotes.length <= currentMainNoteIndex) {
      updatedNotes.push('');
    }
    updatedNotes[currentMainNoteIndex] = value;
    setMainNotes(updatedNotes);
    
    // 캐시에도 반영
    const updatedCache = [...mainNoteCache];
    while (updatedCache.length <= currentMainNoteIndex) {
      updatedCache.push('');
    }
    updatedCache[currentMainNoteIndex] = value;
    setMainNoteCache(updatedCache);
    
    // 변경사항 체크 (원본과 비교)
    const hasChanges = JSON.stringify(updatedCache) !== JSON.stringify(originalMainNotes);
    setHasUnsavedMainNoteChanges(hasChanges);
  };

  // 메인 노트 추가 버튼 클릭 (제목 입력 모달 열기)
  const handleAddMainNoteClick = () => {
    setNewMainNoteTitle('');
    setShowAddMainNoteTitleModal(true);
  };

  // 메인 노트 추가 실행 (제목 입력 후)
  const executeAddMainNote = () => {
    if (!newMainNoteTitle.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    // 현재 노트의 변경사항을 캐시에 저장
    const updatedCache = [...mainNoteCache];
    while (updatedCache.length <= currentMainNoteIndex) {
      updatedCache.push('');
    }
    updatedCache[currentMainNoteIndex] = mainNotes[currentMainNoteIndex] || '';
    setMainNoteCache(updatedCache);
    
    // 마지막 위치 찾기 (mainNotes, mainNoteCache 중 가장 긴 길이)
    // 삭제/추가 반영을 위해 originalMainNotes는 제외 (서버 원본이므로 현재 상태와 다를 수 있음)
    const maxLength = Math.max(
      mainNotes.length,
      mainNoteCache.length
    );
    const lastIndex = maxLength > 0 ? maxLength : 0;
    
    // 마지막에 새 노트 추가
    const updatedNotes = [...mainNotes];
    while (updatedNotes.length <= lastIndex) {
      updatedNotes.push('');
    }
    updatedNotes[lastIndex] = '';
    setMainNotes(updatedNotes);
    
    // 캐시에도 마지막에 새 노트 추가
    while (updatedCache.length <= lastIndex) {
      updatedCache.push('');
    }
    updatedCache[lastIndex] = '';
    setMainNoteCache(updatedCache);
    
    // 제목 배열에도 새 제목 추가 (입력한 제목 사용)
    const updatedTitles = [...mainNoteTitles];
    while (updatedTitles.length <= lastIndex) {
      updatedTitles.push(`기록 ${updatedTitles.length + 1}`);
    }
    updatedTitles[lastIndex] = newMainNoteTitle.trim();
    setMainNoteTitles(updatedTitles);
    
    // 새 노트로 이동
    setCurrentMainNoteIndex(lastIndex);
    
    // 변경사항 체크
    const hasChanges = JSON.stringify(updatedCache) !== JSON.stringify(originalMainNotes);
    setHasUnsavedMainNoteChanges(hasChanges);
    
    // 모달 닫기 및 상태 초기화
    setShowAddMainNoteTitleModal(false);
    setNewMainNoteTitle('');
  };

  // 메인 노트 삭제 확인 모달 열기
  const handleDeleteMainNoteClick = () => {
    setShowDeleteMainNoteConfirm(true);
  };

  // 메인 노트 삭제 실행
  const executeDeleteMainNote = () => {
    const totalCount = Math.max(mainNotes.length, mainNoteCache.length);
    if (totalCount <= 1) {
      // 마지막 하나는 삭제하지 않고 내용만 비우기
      const updatedNotes = [''];

      // 현재 노트의 변경사항을 캐시에 저장
      const updatedCache = [...mainNoteCache];
      while (updatedCache.length <= currentMainNoteIndex) {
        updatedCache.push('');
      }
      updatedCache[currentMainNoteIndex] = '';
      
      // 배열이 비어있으면 빈 배열로 설정
      updatedCache.length = 0;
      updatedCache.push('');

      setMainNotes(updatedNotes);
      setMainNoteCache(updatedCache);
      
      // 제목 배열도 초기화
      setMainNoteTitles(['노트 1']);
      
      setCurrentMainNoteIndex(0);
      
      // 변경사항 체크
      const hasChanges = JSON.stringify(updatedCache) !== JSON.stringify(originalMainNotes);
      setHasUnsavedMainNoteChanges(hasChanges);
      setShowDeleteMainNoteConfirm(false);
      return;
    }
    
    // 현재 노트 삭제
    const updatedNotes = [...mainNotes];
    updatedNotes.splice(currentMainNoteIndex, 1);
    setMainNotes(updatedNotes);
    
    // 캐시에서도 삭제
    const updatedCache = [...mainNoteCache];
    updatedCache.splice(currentMainNoteIndex, 1);
    setMainNoteCache(updatedCache);
    
    // 제목 배열에서도 삭제하고 재정렬
    const updatedTitles = [...mainNoteTitles];
    updatedTitles.splice(currentMainNoteIndex, 1);
    // 제목을 재정렬 (1부터 다시 번호 매기기)
    const renumberedTitles = updatedTitles.map((title, index) => {
      // 기본값으로 시작하는 제목만 재정렬
      if (title.startsWith('노트 ')) {
        return `노트 ${index + 1}`;
      }
      return title;
    });
    setMainNoteTitles(renumberedTitles);
    
    // 인덱스 조정 (삭제 후 마지막 인덱스를 넘으면 조정)
    let newIndex = currentMainNoteIndex;
    if (newIndex >= updatedNotes.length) {
      newIndex = Math.max(0, updatedNotes.length - 1);
    }
    setCurrentMainNoteIndex(newIndex);
    
    // 변경사항 체크
    const hasChanges = JSON.stringify(updatedCache) !== JSON.stringify(originalMainNotes);
    setHasUnsavedMainNoteChanges(hasChanges);
    setShowDeleteMainNoteConfirm(false);
  };

  // 관찰 기록 추가
  const handleAddObservationNote = async (): Promise<boolean> => {
    if (!user || !currentClass || !currentNoteManagementStudentId || newObservationNoteContent.trim() === '') return false;
    
    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      const newNote = {
        id: Date.now().toString(),
        content: newObservationNoteContent.trim(),
        relatedStudents: modalRelatedStudents,
        recordedDate: new Date(modalSelectedDate),
        timeTag: modalSelectedTimeTag
          ? formatTimeTagLabel(modalSelectedTimeTag, modalSelectedDayOfWeek)
          : '',
        noteType: selectedObservationTab
      };
      
      await noteService.addObservationNote(currentNoteManagementStudentId, newNote);
      
      // 로컬 상태 업데이트
      setObservationNotes(prev => [newNote, ...prev]);
      setNewObservationNoteContent('');
      setShowAddObservationNote(true);
      setCurrentObservationNotePage(1);
      setModalRelatedStudents([]);
      
      // 학생 블록의 기록 개수 즉시 반영
      loadStudentNoteCounts([currentNoteManagementStudentId]);
      alert('관찰 기록이 추가되었습니다.');
      return true;
    } catch (error) {
      console.error('관찰 기록 추가 실패:', error);
      alert('관찰 기록 추가에 실패했습니다.');
      return false;
    }
  };

  // 관찰 기록 수정
  const handleEditObservationNote = async (noteId: string): Promise<boolean> => {
    if (!user || !currentClass || !currentNoteManagementStudentId || editingObservationNoteContent.trim() === '') return false;
    
    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      
      // 날짜와 시간을 결합하여 recordedDate 생성
      const recordedDate = new Date(editingObservationNoteDate);
      if (editingObservationNoteTimeTag && editingObservationNoteTimeTag.startTime) {
        const [hours, minutes] = editingObservationNoteTimeTag.startTime.split(':').map(Number);
        recordedDate.setHours(hours, minutes, 0, 0);
      }
      
      const updatedNote = {
        id: noteId,
        content: editingObservationNoteContent.trim(),
        relatedStudentIds: editingObservationNoteRelatedStudents,
        recordedDate: recordedDate,
        timeTag: editingObservationNoteTimeTag ? formatTimeTagLabel(editingObservationNoteTimeTag, editingObservationNoteDayOfWeek) : '',
        noteType: selectedObservationTab
      };
      
      await noteService.updateObservationNote(currentNoteManagementStudentId, noteId, updatedNote);
      
      // 로컬 상태 업데이트
      setObservationNotes(prev => prev.map(note => note.id === noteId ? updatedNote : note));
      
      // 수정 모드 종료
      setEditingObservationNoteId('');
      setEditingObservationNoteContent('');
      setEditingObservationNoteDate(new Date());
      setEditingObservationNoteTimeTag(null);
      setEditingObservationNoteRelatedStudents([]);
      setEditingObservationNoteDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
      setObservationEditBaseline(null);

      // 학생 블록의 기록 개수 즉시 반영
      loadStudentNoteCounts([currentNoteManagementStudentId]);
      alert('관찰 기록이 수정되었습니다.');
      return true;
    } catch (error) {
      console.error('관찰 기록 수정 실패:', error);
      alert('관찰 기록 수정에 실패했습니다.');
      return false;
    }
  };

  // 관찰 기록 삭제
  const handleDeleteObservationNote = async (noteId: string) => {
    if (!user || !currentClass || !currentNoteManagementStudentId) return;
    
    if (!window.confirm('이 관찰 기록을 삭제하시겠습니까?')) return;
    
    try {
      const noteService = new ClassMembersNotePartitionService(user.id, currentClass.id, currentMemberGroup);
      await noteService.deleteObservationNote(currentNoteManagementStudentId, noteId);
      
      // 로컬 상태 업데이트
      setObservationNotes(prev => prev.filter(note => note.id !== noteId));
      
      // 학생 블록의 기록 개수 즉시 반영
      loadStudentNoteCounts([currentNoteManagementStudentId]);
      alert('관찰 기록이 삭제되었습니다.');
      if (editingObservationNoteId === noteId) {
        closeObservationEditSection();
      }
    } catch (error) {
      console.error('관찰 기록 삭제 실패:', error);
      alert('관찰 기록 삭제에 실패했습니다.');
    }
  };

  // 메인 노트 섹션 PDF 저장
  const handleSaveMainNotesAsPdf = async () => {
    if (!mainNotes || mainNotes.length === 0) {
      alert('저장할 개별 종합 기록이 없습니다.');
      return;
    }

    const student = tableData?.students.find(s => s.studentId === currentNoteManagementStudentId);
    const studentName = student ? `${student.studentNumber}번 ${student.studentName}` : '학생';
    
    // HTML 생성
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = '210mm';
    tempContainer.style.padding = '15mm';
    tempContainer.style.fontFamily = 'Malgun Gothic, sans-serif';
    tempContainer.style.backgroundColor = '#ffffff';
    tempContainer.style.color = '#000000';
    
    const titleHtml = `
      <h1 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">개별 종합 기록 - ${studentName}</h1>
      <div style="font-size: 10px; color: #666; margin-bottom: 20px;">생성일: ${new Date().toLocaleDateString('ko-KR')}</div>
    `;
    
    const notesHtml = mainNotes.map((note, index) => {
      if (!note || note.trim() === '') return '';
      const noteTitle = mainNoteTitles[index] || `노트 ${index + 1}`;
      return `
        <div style="margin-bottom: 20px; page-break-inside: avoid; border-bottom: 1px solid #d0d0d0; padding-bottom: 15px;">
          <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">${noteTitle}</h3>
          <div style="font-size: 12px; line-height: 1.6; white-space: pre-wrap; color: #000;">${note}</div>
        </div>
      `;
    }).join('');
    
    tempContainer.innerHTML = titleHtml + notesHtml;
    document.body.appendChild(tempContainer);
    
    try {
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      } as any);
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pdfWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = margin;
      
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - margin * 2);
      
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= (pdfHeight - margin * 2);
      }
      
      const fileName = `메인노트_${studentName}_${new Date().toLocaleDateString('ko-KR').replace(/\//g, '-')}.pdf`;
      pdf.save(fileName);
      
      document.body.removeChild(tempContainer);
    } catch (error) {
      console.error('PDF 저장 실패:', error);
      alert('PDF 저장에 실패했습니다.');
      if (document.body.contains(tempContainer)) {
        document.body.removeChild(tempContainer);
      }
    }
  };

  // 메인 노트 섹션 프린트
  const handlePrintMainNotes = async () => {
    if (!mainNotes || mainNotes.length === 0) {
      alert('인쇄할 개별 종합 기록이 없습니다.');
      return;
    }

    const student = tableData?.students.find(s => s.studentId === currentNoteManagementStudentId);
    const studentName = student ? `${student.studentNumber}번 ${student.studentName}` : '학생';
    
    const notesHtml = mainNotes.map((note, index) => {
      if (!note || note.trim() === '') return '';
      const noteTitle = mainNoteTitles[index] || `노트 ${index + 1}`;
      return `
        <div style="margin-bottom: 20px; page-break-inside: avoid;">
          <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">${noteTitle}</h3>
          <div style="font-size: 12px; line-height: 1.6; white-space: pre-wrap;">${note}</div>
        </div>
      `;
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('팝업 차단을 해제해주세요.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>개별 종합 기록 - ${studentName}</title>
          <style>
            @media print {
              body { margin: 0; padding: 15mm; }
              @page { size: A4; margin: 15mm; }
            }
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: 'Malgun Gothic', sans-serif;
            }
            h1 {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .date {
              font-size: 10px;
              color: #666;
              margin-bottom: 20px;
            }
          </style>
        </head>
        <body>
          <h1>개별 종합 기록 - ${studentName}</h1>
          <div class="date">생성일: ${new Date().toLocaleDateString('ko-KR')}</div>
          ${notesHtml}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // 관찰 기록 필터링 및 정렬
  const getFilteredAndSortedNotes = (notes: typeof observationNotes, types: Array<'일반' | '학습' | '행동'>, sortOrder: 'time' | 'type') => {
    // 필터링
    const filtered = notes.filter(note => types.includes(note.noteType));
    
    // 정렬
    if (sortOrder === 'time') {
      return [...filtered].sort((a, b) => b.recordedDate.getTime() - a.recordedDate.getTime());
    } else {
      // 종류별 정렬: 일반 -> 학습 -> 행동 순
      const typeOrder: { [key: string]: number } = { '일반': 0, '학습': 1, '행동': 2 };
      return [...filtered].sort((a, b) => {
        if (typeOrder[a.noteType] !== typeOrder[b.noteType]) {
          return typeOrder[a.noteType] - typeOrder[b.noteType];
        }
        return b.recordedDate.getTime() - a.recordedDate.getTime();
      });
    }
  };

  const getObservationTabDotColor = (noteType: '일반' | '학습' | '행동') => {
    if (noteType === '일반') return '#d3d3d3';
    if (noteType === '학습') return '#4DC7AE';
    return '#ff6b6b';
  };

  const closeObservationEditSection = useCallback(() => {
    setEditingObservationNoteId('');
    setEditingObservationNoteContent('');
    setEditingObservationNoteDate(new Date());
    setEditingObservationNoteTimeTag(null);
    setEditingObservationNoteRelatedStudents([]);
    setEditingObservationNoteDayOfWeek(['일', '월', '화', '수', '목', '금', '토'][new Date().getDay()]);
    setObservationEditBaseline(null);
    setShowAddObservationNote(true);
  }, []);

  const loadObservationNoteIntoEdit = useCallback(
    (note: typeof observationNotes[0]) => {
      setEditingObservationNoteId(note.id);
      setEditingObservationNoteContent(note.content);
      setEditingObservationNoteDate(note.recordedDate);
      const parsedTag = parseTimeTagLabel(note.timeTag);
      const existingTimeTag = availableTimeTags.find(tag => tag.name === parsedTag.name);
      const timeTagObj = existingTimeTag || null;
      setEditingObservationNoteTimeTag(timeTagObj);
      setEditingObservationNoteDayOfWeek(parsedTag.day);
      const related = note.relatedStudents || [];
      setEditingObservationNoteRelatedStudents(related);
      setSelectedObservationTab(note.noteType);
      setCurrentObservationNotePage(1);
      setObservationEditBaseline(
        getObservationDirtyPayload(
          note.content,
          note.recordedDate,
          timeTagObj,
          parsedTag.day,
          related,
          note.noteType
        )
      );
    },
    [availableTimeTags, getObservationDirtyPayload]
  );

  // 관찰 기록 필터 모달에서 확인 버튼 클릭
  const handleObservationFilterConfirm = async () => {
    if (selectedNoteTypes.length === 0) {
      alert('최소 하나의 기록 종류를 선택해주세요.');
      return;
    }

    const filteredNotes = getFilteredAndSortedNotes(observationNotes, selectedNoteTypes, observationSortOrder);
    
    if (filteredNotes.length === 0) {
      alert('선택한 조건에 맞는 관찰 기록이 없습니다.');
      return;
    }

    setShowObservationFilterModal(false);

    if (observationFilterMode === 'save') {
      await handleSaveObservationNotesAsPdfWithFilter(filteredNotes);
    } else {
      await handlePrintObservationNotesWithFilter(filteredNotes);
    }
  };

  // 관찰 기록 섹션 PDF 저장 (필터 적용)
  const handleSaveObservationNotesAsPdfWithFilter = async (filteredNotes: typeof observationNotes) => {
    if (!filteredNotes || filteredNotes.length === 0) {
      alert('저장할 관찰 기록이 없습니다.');
      return;
    }

    const student = tableData?.students.find(s => s.studentId === currentNoteManagementStudentId);
    const studentName = student ? `${student.studentNumber}번 ${student.studentName}` : '학생';
    
    // HTML 생성
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = '210mm';
    tempContainer.style.padding = '15mm';
    tempContainer.style.fontFamily = 'Malgun Gothic, sans-serif';
    tempContainer.style.backgroundColor = '#ffffff';
    tempContainer.style.color = '#000000';
    
    const titleHtml = `
      <h1 style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">관찰 기록 - ${studentName}</h1>
      <div style="font-size: 10px; color: #666; margin-bottom: 20px;">생성일: ${new Date().toLocaleDateString('ko-KR')}</div>
    `;
    
    const notesHtml = filteredNotes.map((note) => {
      const dateStr = note.recordedDate.toLocaleDateString('ko-KR');
      const timeTagText = note.timeTag && note.timeTag !== '일반' ? note.timeTag : '';
      const dateTimeText = timeTagText ? `${dateStr} | ${timeTagText}` : dateStr;
      const relatedText = note.relatedStudents && note.relatedStudents.length > 0
        ? ` | 관련멤버 ${note.relatedStudents.length}명` 
        : '';
      
      return `
        <div style="display: table; width: 100%; margin-bottom: 20px; page-break-inside: avoid; border-bottom: 1px solid #d0d0d0; padding-bottom: 15px;">
          <div style="display: table-row;">
            <div style="display: table-cell; width: 80px; vertical-align: top; padding-right: 12px;">
              <div style="font-size: 12px; font-weight: bold; color: #333;">${note.noteType}</div>
              <div style="font-size: 9px; color: #666; margin-top: 8px;">${dateTimeText}${relatedText}</div>
            </div>
            <div style="display: table-cell; vertical-align: top;">
              <div style="font-size: 12px; line-height: 1.6; white-space: pre-wrap; color: #000;">${note.content}</div>
            </div>
          </div>
        </div>
      `;
    }).join('');
    
    tempContainer.innerHTML = titleHtml + notesHtml;
    document.body.appendChild(tempContainer);
    
    try {
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      } as any);
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pdfWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = margin;
      
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - margin * 2);
      
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= (pdfHeight - margin * 2);
      }
      
      const fileName = `관찰기록_${studentName}_${new Date().toLocaleDateString('ko-KR').replace(/\//g, '-')}.pdf`;
      pdf.save(fileName);
      
      document.body.removeChild(tempContainer);
    } catch (error) {
      console.error('PDF 저장 실패:', error);
      alert('PDF 저장에 실패했습니다.');
      if (document.body.contains(tempContainer)) {
        document.body.removeChild(tempContainer);
      }
    }
  };

  // 관찰 기록 섹션 프린트 (필터 적용)
  const handlePrintObservationNotesWithFilter = async (filteredNotes: typeof observationNotes) => {
    if (!filteredNotes || filteredNotes.length === 0) {
      alert('인쇄할 관찰 기록이 없습니다.');
      return;
    }

    const student = tableData?.students.find(s => s.studentId === currentNoteManagementStudentId);
    const studentName = student ? `${student.studentNumber}번 ${student.studentName}` : '학생';
    
    const notesHtml = filteredNotes.map((note) => {
      const dateStr = note.recordedDate.toLocaleDateString('ko-KR');
      const timeTagText = note.timeTag && note.timeTag !== '일반' ? note.timeTag : '';
      const dateTimeText = timeTagText ? `${dateStr} | ${timeTagText}` : dateStr;
      const relatedText = note.relatedStudents && note.relatedStudents.length > 0
        ? ` | 관련멤버 ${note.relatedStudents.length}명` 
        : '';
      
      return `
        <div style="display: table; width: 100%; margin-bottom: 20px; page-break-inside: avoid; border-bottom: 1px solid #d0d0d0; padding-bottom: 15px;">
          <div style="display: table-row;">
            <div style="display: table-cell; width: 80px; vertical-align: top; padding-right: 12px;">
              <div style="font-size: 12px; font-weight: bold; color: #333;">${note.noteType}</div>
              <div style="font-size: 9px; color: #666; margin-top: 8px;">${dateTimeText}${relatedText}</div>
            </div>
            <div style="display: table-cell; vertical-align: top;">
              <div style="font-size: 12px; line-height: 1.6; white-space: pre-wrap; color: #000;">${note.content}</div>
            </div>
          </div>
        </div>
      `;
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('팝업 차단을 해제해주세요.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>관찰 기록 - ${studentName}</title>
          <style>
            @media print {
              body { margin: 0; padding: 15mm; }
              @page { size: A4; margin: 15mm; }
            }
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: 'Malgun Gothic', sans-serif;
            }
            h1 {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .date {
              font-size: 10px;
              color: #666;
              margin-bottom: 20px;
            }
          </style>
        </head>
        <body>
          <h1>관찰 기록 - ${studentName}</h1>
          <div class="date">생성일: ${new Date().toLocaleDateString('ko-KR')}</div>
          ${notesHtml}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // 모든 변경사항 저장
  const handleSaveAllChanges = async () => {
    if (!pointManagementService || !tableData) return;
    
    // 노트 모드일 때는 노트 저장 로직 실행
    if (displayMode === 'notes') {
      await handleSaveNotes();
      return;
    }
    
    // 코인 모드일 때는 다른 로직 실행
    if (displayMode === 'coins') {
      await handleSaveAllCoins();
      return;
    }
    
    try {
      const changedStudents = Object.keys(studentPointInputs).filter(studentId => {
        const current = studentPointInputs[studentId];
        const initial = initialStudentData[studentId];
        
        if (!initial) {
          return current && (current.points !== 0 || (current.memo || '').trim() !== '' || current.pointTypeId !== '');
        }
        
        return current.points !== initial.points || 
               current.memo !== initial.memo || 
               current.pointTypeId !== initial.pointTypeId;
      });

      if (changedStudents.length === 0) {
        alert('저장할 변경사항이 없습니다.');
        return;
      }

      // 유효한 멤버들만 필터링
      const validStudents = changedStudents.filter(studentId => {
        const input = studentPointInputs[studentId];
        return input.points !== 0 || (input.memo || '').trim() !== '';
      });

      if (validStudents.length === 0) {
        alert('저장할 포인트 데이터가 없습니다.');
        return;
      }

      // 모든 멤버의 포인트 데이터를 한 번에 처리
      const studentNames: { [key: string]: string } = {};
      const studentNumbers: { [key: string]: string } = {};
      const pointGrantDataList: { [studentId: string]: PointGrantData } = {};

      // 각 멤버의 데이터 준비
      for (const studentId of validStudents) {
        const input = studentPointInputs[studentId];
        const student = tableData.students.find(s => s.studentId === studentId);
        
        if (!student) continue;

        // 포인트 타입 정보 가져오기
        const positiveType = availablePointTypes.positive.find(type => type.id === input.pointTypeId);
        const negativeType = availablePointTypes.negative.find(type => type.id === input.pointTypeId);
        
        // 기본 블럭 처리
        let pointCategory = 'positive';
        let pointTypeName = '일반';
        
        if (input.pointTypeId === 'positive-default') {
          pointCategory = 'positive';
          pointTypeName = '긍정행동';
        } else if (input.pointTypeId === 'negative-default') {
          pointCategory = 'negative';
          pointTypeName = '행동체크';
        } else if (positiveType) {
          // positiveTypes 배열에서 찾은 경우
          pointCategory = 'positive';
          pointTypeName = '긍정행동';
        } else if (negativeType) {
          // negativeTypes 배열에서 찾은 경우
          pointCategory = 'negative';
          pointTypeName = '행동체크';
        }
        // 그 외의 경우는 기본값 '일반' 유지

        studentNames[studentId] = student.studentName || `멤버 ${studentId.slice(-4)}`;
        studentNumbers[studentId] = student.studentNumber ? student.studentNumber.toString() : '0';
        
        // 모든 포인트 타입에 대해 날짜와 타임 태그 정보 추가
        // 날짜는 선택한 날짜로, 시간은 현재 시간으로 설정
        const selectedDateObj = new Date(selectedDate);
        const now = new Date();
        const recordedDate = new Date(
          selectedDateObj.getFullYear(),
          selectedDateObj.getMonth(),
          selectedDateObj.getDate(),
          now.getHours(),
          now.getMinutes(),
          now.getSeconds()
        );

        const pointGrantData: PointGrantData = {
          pointType: pointTypeName,
          pointValue: input.points,
          pointMemo: (input.memo || '').trim(),
          recordedDate: recordedDate, // 선택한 날짜 + 현재 시간
          pointCategory: pointCategory as 'positive' | 'negative'
        };

        // 타임 태그 정보 추가 (모든 포인트 타입에 적용)
        if (selectedTimeTag) {
          pointGrantData.timeTag = formatTimeTagLabel(selectedTimeTag, selectedDayOfWeek);
        }

        pointGrantDataList[studentId] = pointGrantData;
      }

      // 모든 멤버를 일괄 처리 (되돌리기 기능을 위해)
      const allStudentIds = validStudents;
      const allStudentNames: { [key: string]: string } = {};
      const allStudentNumbers: { [key: string]: string } = {};
      
      // 모든 멤버 정보 수집
      for (const studentId of validStudents) {
        allStudentNames[studentId] = studentNames[studentId];
        allStudentNumbers[studentId] = studentNumbers[studentId];
      }
      
      // 레벨업 체크를 위해 이전 포인트 저장
      const oldTotalPoints: { [studentId: string]: number } = {};
      if (tableData) {
        validStudents.forEach(studentId => {
          const student = tableData.students.find(s => s.studentId === studentId);
          if (student) {
            oldTotalPoints[studentId] = student.totalPoints || 0;
          }
        });
      }

      // 개별 포인트 데이터로 멤버들에게 포인트 지급
      await pointManagementService.grantPointsToStudentsWithIndividualData(
        allStudentIds,
        pointGrantDataList,
        allStudentNames,
        allStudentNumbers
      );

      showToastMessage(`${validStudents.length}명의 멤버 포인트를 저장했습니다.`);

      // 저장 후 최신 데이터 다시 로드 (캐시 무효화 후 loadData로 즉시 반영 - 예전 동작 복원)
      if (user && currentClass) {
        userDataService.invalidateMembersCache(user.id, currentClass.id, currentMemberGroup);
      }
      pointManagementService.invalidatePointDocumentsCache();
      await loadData();

      // 저장 후 상태 초기화
      setStudentPointInputs({});
      setInitialStudentData({});

      // 날짜와 타임 태그 초기화
      await resetDateAndTimeTag();

      // 데이터 새로고침하여 포인트 확인 모드에서 즉시 반영되도록 함
      await loadData();

      // 레벨업 체크 및 보상 지급
      if (user && currentClass) {
        const { syncLevelUpRewardsByPoints } = await import('../utils/levelUpRewardHelper');
        
        for (const studentId of validStudents) {
            const oldPoints = oldTotalPoints[studentId] || 0;
          const delta = pointGrantDataList[studentId]?.pointValue || 0;
          const newPoints = oldPoints + delta;
            
            try {
            await syncLevelUpRewardsByPoints(
                user.id,
                currentClass.id,
                studentId,
                oldPoints,
                newPoints,
                levelUpPoints,
                currentMemberGroup
              );
          } catch (error) {
            console.error(`레벨업 보상 지급 실패 (멤버 ${studentId}):`, error);
          }

          // 포인트 지급 자체로는 ClassMembers 문서가 변하지 않으므로 캐시 무효화 불필요
          // 레벨 보상 처리 시점에 캐시 무효화가 이루어짐
        }
      }
      
    } catch (error) {
      console.error('포인트 저장 실패:', error);
      alert('포인트 저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 모든 데이터 초기화
  const handleResetAllChanges = () => {
    setStudentPointInputs({});
    setInitialStudentData({});
    setStudentNegativeModes({}); // 음수 모드 상태도 초기화
    setSelectedNoteType(initialNoteType); // 노트 타입도 초기화
  };

  // 레벨 설정 모달 닫기
  const handleCloseLevelSettingsModal = () => {
    actualSetShowLevelSettingsModal(false);
  };

  // 레벨 설정 모달 열릴 때 배경 스크롤 비활성화
  useEffect(() => {
    if (localShowLevelSettingsModal) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [localShowLevelSettingsModal]);

  // selectedLevel 변경 시 편집 모드 해제
  useEffect(() => {
    if (selectedLevel !== null) {
      setIsEditingLevel(false);
    }
  }, [selectedLevel]);

  // 레벨 설정 저장
  const handleSaveLevelSettings = async () => {
    try {
      if (pointTypeManagementService) {
        await pointTypeManagementService.updateLevelUpPoints(levelUpPoints);
        await pointTypeManagementService.updateLevelItemCards(levelItemCards);
        await pointTypeManagementService.updateLevelCoins(levelCoins);
        actualSetShowLevelSettingsModal(false);
      }
    } catch (error) {
      console.error('레벨 설정 저장 실패:', error);
      alert('레벨 설정 저장에 실패했습니다.');
    }
  };

  // 레벨 계산 함수
  const calculateLevel = (totalPoints: number): { level: number, progress: number, nextLevelPoints: number } => {
    // 레벨 계산: 0-9점(레벨0), 10-19점(레벨1), 20-29점(레벨2)
    // 10점이면 레벨 1, 20점이면 레벨 2
    const level = Math.floor(totalPoints / levelUpPoints);
    const currentLevelStartPoints = level * levelUpPoints;
    const currentLevelPoints = totalPoints - currentLevelStartPoints;
    const progress = (currentLevelPoints / levelUpPoints) * 100;
    const nextLevelPoints = levelUpPoints - currentLevelPoints;
    
    return { level, progress, nextLevelPoints };
  };

  // 최근 포인트 지급 되돌리기
  const handleUndoLastPoints = async () => {
    if (displayMode === 'coins') {
      // 코인 모드일 때: 마지막 코인 지급 취소
      await handleUndoLastCoinGrant();
    } else {
      // 포인트 모드일 때: 기존 로직
      if (!pointManagementService) return;
      
      try {
        // 레벨 변화(레벨업/레벨다운) 동기화를 위해 되돌리기 전 상태 저장
        const lastGranted = pointManagementService.getLastGrantedPoints();
        const oldTotalsByStudentId: { [studentId: string]: number } = {};
        if (tableData && lastGranted?.studentIds?.length) {
          lastGranted.studentIds.forEach((studentId: string) => {
            const s = tableData.students.find(st => st.studentId === studentId);
            oldTotalsByStudentId[studentId] = s?.totalPoints || 0;
          });
        }

        const result = await pointManagementService.undoLastGrantedPoints();
        
        if (result.success && result.undoInfo) {
          setUndoInfo(result.undoInfo);
          setShowUndoModal(true);

          // ✅ 포인트가 감소(되돌리기)하여 레벨이 내려갔을 때 보상 회수 동기화
          if (user && currentClass && lastGranted?.studentIds?.length) {
            try {
              const { syncLevelUpRewardsByPoints } = await import('../utils/levelUpRewardHelper');

              for (const studentId of lastGranted.studentIds) {
                const oldPoints = oldTotalsByStudentId[studentId] ?? 0;
                const delta =
                  (lastGranted.pointDataList && lastGranted.pointDataList[studentId]?.pointValue) ??
                  (lastGranted.pointData?.pointValue ?? 0);
                const newPoints = oldPoints - delta;

                await syncLevelUpRewardsByPoints(
                  user.id,
                  currentClass.id,
                  studentId,
                  oldPoints,
                  newPoints,
                  levelUpPoints,
                  currentMemberGroup
                );
              }
            } catch (e) {
              console.error('레벨 보상 회수 동기화(되돌리기) 실패:', e);
            }
          }
          
          // 데이터 새로고침
          await loadData();
        } else {
          alert(result.message);
        }
      } catch (error) {
        console.error('포인트 되돌리기 실패:', error);
        alert('포인트 되돌리기에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  // 마지막 코인 지급 취소 (ClassMembersCoin만 갱신, 성공 인원은 서비스 반환값 기준)
  const handleUndoLastCoinGrant = async () => {
    if (!lastCoinGrant || !user || !currentClass) {
      alert('취소할 코인 지급 내역이 없습니다.');
      return;
    }

    try {
      const coinManagementService = new CoinManagementService(user.id, currentClass.id, currentMemberGroup);
      let successCount = 0;

      if (lastCoinGrant.individualGrants && lastCoinGrant.individualGrants.length > 0) {
        // 개별 저장: 멤버별 금액/메모로 ClassMembersCoin에서만 취소
        for (const grant of lastCoinGrant.individualGrants) {
          const count = await coinManagementService.revokeCoinsFromStudents(
            [grant.studentId],
            grant.amount,
            grant.memo
          );
          successCount += count;
        }
      } else {
        // 일괄 저장: 동일 금액/메모로 ClassMembersCoin에서만 취소
        successCount = await coinManagementService.revokeCoinsFromStudents(
          lastCoinGrant.studentIds,
          lastCoinGrant.amount,
          lastCoinGrant.memo
        );
      }

      if (successCount > 0) {
        alert(`${successCount}명의 멤버에게서 코인 지급을 취소했습니다.`);
      } else {
        alert('취소할 수 있는 코인 지급 내역이 없습니다.');
      }

      setLastCoinGrant(null);
      await loadData();
    } catch (error) {
      console.error('코인 지급 취소 실패:', error);
      alert('코인 지급 취소에 실패했습니다.');
    }
  };

  // 되돌리기 모달 닫기
  const handleCloseUndoModal = () => {
    setShowUndoModal(false);
    setUndoInfo(null);
  };

  // 토스트 표시 함수
  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    // 자동 닫힘 제거 - 확인 버튼으로만 닫히도록
  };

  
  // localStorage에서 토글 설정 복원 (모든 상태 정의 이후에 실행)
  useEffect(() => {
    const classId = currentClass?.id;
    if (!classId) {
      setIsSettingsRestored(false);
      return;
    }
    
    if (isSettingsRestored) {
      return;
    }
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) {
      setIsSettingsRestored(true);
      return;
    }
    
    try {
      const savedSettings = localStorage.getItem(storageKey);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        
        // 상태 업데이트를 배치로 처리
        if (settings.isSelectionMode !== undefined) {
          setIsSelectionMode(settings.isSelectionMode);
        }
        if (settings.viewMode === 'number' || settings.viewMode === 'seat') {
          setViewMode(settings.viewMode);
        }
        if (settings.displayMode === 'points' || settings.displayMode === 'coins' || settings.displayMode === 'notes') {
          setDisplayMode(settings.displayMode);
        }
        if (settings.gridAlignment === 'left' || settings.gridAlignment === 'center') {
          setGridAlignment(settings.gridAlignment);
        }
        
        setIsSettingsRestored(true);
      } else {
        setIsSettingsRestored(true);
      }
    } catch (error) {
      console.error('❌ localStorage에서 설정 복원 실패:', error);
      setIsSettingsRestored(true);
    }
  }, [currentClass?.id, isSettingsRestored, getStorageKey]);
  
  // displayMode 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isSettingsRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.displayMode = displayMode;
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (error) {
      console.error('❌ localStorage에 displayMode 저장 실패:', error);
    }
  }, [displayMode, currentClass, isSettingsRestored, getStorageKey]);
  
  // gridAlignment 변경 시 localStorage에 저장
  useEffect(() => {
    if (!currentClass || !isSettingsRestored) return;
    
    const storageKey = getStorageKey('settings');
    if (!storageKey) return;
    
    try {
      const existingSettings = localStorage.getItem(storageKey);
      const settings = existingSettings ? JSON.parse(existingSettings) : {};
      settings.gridAlignment = gridAlignment;
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch (error) {
      console.error('❌ localStorage에 gridAlignment 저장 실패:', error);
    }
  }, [gridAlignment, currentClass, isSettingsRestored, getStorageKey]);
  
  // 노트 모드일 때 노트 개수 로드
  useEffect(() => {
    if (displayMode === 'notes' && tableData && tableData.students.length > 0) {
      const studentIds = tableData.students.map(s => s.studentId);
      loadStudentNoteCounts(studentIds);
    }
  }, [displayMode, tableData, loadStudentNoteCounts]);
  
  // viewMode가 'seat'이고 tableData가 로드되었을 때 자리 배치 데이터 자동 로드
  useEffect(() => {
    if (viewMode === 'seat' && tableData && tableData.students && tableData.students.length > 0 && !seatArrangementData && !seatArrangementError) {
      loadSeatArrangementData().catch((error) => {
        console.error('❌ 자리 배치 데이터 자동 로드 실패:', error);
      });
    }
  }, [viewMode, tableData, seatArrangementData, seatArrangementError, loadSeatArrangementData]);
  
  // 일괄 코인 지급 모달 상태
  const [showBulkCoinGrantModal, setShowBulkCoinGrantModal] = useState<boolean>(false);
  const [bulkCoinAmount, setBulkCoinAmount] = useState<number>(10);
  const [bulkCoinMemo, setBulkCoinMemo] = useState<string>('');
  const [isBulkCoinNegativeMode, setIsBulkCoinNegativeMode] = useState<boolean>(false);
  
  // 멤버별 음수 모드 상태 관리
  const [studentNegativeModes, setStudentNegativeModes] = useState<{[studentId: string]: boolean}>({});
  
  // 일괄 코인 지급 모달이 열릴 때 음수 모드 초기화
  useEffect(() => {
    if (showBulkCoinGrantModal) {
      setIsBulkCoinNegativeMode(false);
      setBulkCoinAmount(10);
      setBulkCoinMemo('');
    }
  }, [showBulkCoinGrantModal]);
  
  // 마지막 코인 지급 정보 (되돌리기용)
  const [lastCoinGrant, setLastCoinGrant] = useState<{
    studentIds: string[], 
    amount: number, 
    memo: string,
    individualGrants?: {studentId: string, amount: number, memo: string}[]
  } | null>(null);

  // 코인 히스토리에서 총 코인 수 계산 (V1 배열 / V2 객체 듀얼 지원)
  const calculateTotalCoins = (coinsHistory?: any[] | Record<string, any>): number => {
    if (!coinsHistory) return 0;
    const arr = Array.isArray(coinsHistory)
      ? coinsHistory
      : typeof coinsHistory === 'object'
        ? Object.values(coinsHistory)
        : [];
    if (arr.length === 0) return 0;
    return arr.reduce((total, history) => total + (history?.earnedCoin || 0), 0);
  };

  // 초기 로드
  useEffect(() => {
    if (user && currentClass) {
      loadData();
      loadTimeTags(); // 타임 태그 자동 로드 및 선택
      
      // 기본 프로필 이미지들을 프리로드
      const { imageCacheService } = require('../services/imageCacheService');
      const { PROFILE_IMAGE_IDS } = require('../services/imageCacheService');
      imageCacheService.preloadImages(PROFILE_IMAGE_IDS.slice(0, 10)); // 처음 10개만 프리로드
    }
  }, [user, currentClass, loadData, loadTimeTags]);


  const filteredData = getFilteredData();
  const selectedStudentTypeStats = getSelectedStudentTypeStats();

  // 마감일 표시용 포맷 (ISO 또는 날짜 문자열 → mm/dd)
  const formatDueDateDisplay = (dueDate: string | undefined): string => {
    if (!dueDate) return '-';
    const date = new Date(dueDate);
    if (isNaN(date.getTime())) return dueDate;
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${m}/${d}`;
  };

  if (!user || !currentClass) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-panel)', padding: '36px 0 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Message>사용자 또는 클래스가 선택되지 않았습니다.</Message>
      </div>
    );
  }


  return (
    <Container>
      <GlobalStyles />

      <Content>
      {/* 초기 로드 중일 때만 로딩 표시 (데이터가 없을 때만) */}
      {isInitialLoad && !tableData && <LoadingSpinner>로딩 중...</LoadingSpinner>}

      {error && (
        <ErrorMessage>
          ❌ {error}
        </ErrorMessage>
      )}

      {/* 포인트 지급 컨트롤 - 페이지 상단 */}
      {tableData && (
        <>
          <FirstRowContainer>
            {/* 1구역: 날짜 버튼, 타임 태그 버튼 (왼쪽 정렬) */}
            <LeftSectionContainer>
              <CustomDateDisplay
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
              />
              <TimeTagSelectButton
                onClick={() => setShowTimeTagSelectModal(true)}
                title="타임 태그 선택"
              >
                <TimeTagIcon>
                  {selectedTimeTag 
                    ? (selectedDayOfWeek || (Array.isArray(selectedTimeTag.dayOfWeek) 
                        ? selectedTimeTag.dayOfWeek[0] 
                        : selectedTimeTag.dayOfWeek))
                    : '#'}
                </TimeTagIcon>
                <TimeTagInfo>
                  {selectedTimeTag ? (
                    <>
                      <TimeTagName>
                        {selectedTimeTag.name}
                      </TimeTagName>
                      <TimeTagTime>{selectedTimeTag.startTime} ~ {selectedTimeTag.endTime}</TimeTagTime>
                    </>
                  ) : (
                    <>
                      <TimeTagName>타임 태그</TimeTagName>
                      <TimeTagTime>선택 안됨</TimeTagTime>
                    </>
                  )}
                </TimeTagInfo>
              </TimeTagSelectButton>
            </LeftSectionContainer>
            
            {/* 2구역: 모드 토글 (중앙 정렬) */}
            <CenterSectionContainer>
              <ModeIconToggle
                role="button"
                tabIndex={0}
                aria-pressed={isSelectionMode}
                aria-label={isSelectionMode ? '데이터 기록 모드, 클릭하면 데이터 조회 모드로 전환' : '데이터 조회 모드, 클릭하면 데이터 기록 모드로 전환'}
                onClick={toggleSelectionMode}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelectionMode(); } }}
                $isSelectionMode={isSelectionMode}
              >
                <ModeCurrentLabel>
                  {isSelectionMode ? '데이터 기록 모드' : '데이터 조회 모드'}
                </ModeCurrentLabel>
                <ToggleKnob $isSelectionMode={isSelectionMode}>
                  {isSelectionMode ? <PenIcon /> : <SearchIcon />}
                </ToggleKnob>
              </ModeIconToggle>
            </CenterSectionContainer>
            
            {/* 3구역: 저장하기 버튼 (오른쪽 정렬) */}
            <RightSectionContainer>
            <SaveAllButton 
              onClick={handleSaveAllChanges}
              disabled={!hasUnsavedChanges()}
              tabIndex={-1}
            >
              저장하기
            </SaveAllButton>
            </RightSectionContainer>
          </FirstRowContainer>
          
          <SecondRowContainer>
            {/* 1행: [번호/자리] [포인트/코인] */}
            <ButtonRow>
              <ViewModeToggleButton 
                $isSeatMode={viewMode === 'seat'} 
                $isDisabled={false}
                onClick={async () => {
                  if (viewMode === 'number') {
                    // 자리 모드로 전환
                    await loadSeatArrangementData();
                    setViewMode('seat');
                  } else {
                    // 번호 모드로 전환
                    setViewMode('number');
                    setSeatArrangementError('');
                  }
                }}
                tabIndex={-1}
              >
                <ViewModeToggleOption $isActive={viewMode === 'number'} $isDisabled={false}>번호</ViewModeToggleOption>
                <ViewModeToggleOption $isActive={viewMode === 'seat'} $isDisabled={false}>자리</ViewModeToggleOption>
              </ViewModeToggleButton>
              
              <ToggleButton 
                $displayMode={displayMode} 
                $isDisabled={false}
                tabIndex={-1}
              >
                <ToggleOption 
                  $isActive={displayMode === 'points'} 
                  $isDisabled={false}
                  onClick={() => setDisplayMode('points')}
                >
                  포인트
                </ToggleOption>
                <ToggleOption 
                  $isActive={displayMode === 'coins'} 
                  $isDisabled={false}
                  onClick={() => setDisplayMode('coins')}
                >
                  코인
                </ToggleOption>
                <ToggleOption 
                  $isActive={displayMode === 'notes'} 
                  $isDisabled={false}
                  onClick={() => setDisplayMode('notes')}
                >
                  기록
                </ToggleOption>
              </ToggleButton>
            </ButtonRow>

            {/* 2행: [전체선택] [선택취소] [일괄포인트 지급] */}
            <ButtonRow>
              <CompactButton onClick={handleSelectAll} disabled={!isSelectionMode} tabIndex={-1}>
                전체선택
              </CompactButton>
              <CompactButton onClick={handleDeselectAll} disabled={!isSelectionMode} tabIndex={-1}>
                선택취소
              </CompactButton>
              <MainActionButton 
                onClick={displayMode === 'notes' ? handleOpenBulkNoteModal : handleOpenPointGrantModal}
                disabled={!isSelectionMode || selectedStudents.size === 0}
                tabIndex={-1}
              >
                {displayMode === 'coins' ? '일괄 코인 지급' : displayMode === 'notes' ? '일괄 기록 작성' : '일괄 포인트 지급'}
              </MainActionButton>
            </ButtonRow>

            {/* 3행: [초기화] [마지막 포인트 지급 취소] */}
            <ButtonRow>
              <ResetAllButton 
                onClick={handleResetAllChanges}
                disabled={!hasUnsavedChanges()}
                tabIndex={-1}
              >
                초기화
              </ResetAllButton>
              <UndoButton 
                onClick={handleUndoLastPoints}
                disabled={displayMode === 'coins' ? !lastCoinGrant || !lastCoinGrant.studentIds || lastCoinGrant.studentIds.length === 0 : displayMode === 'notes' ? true : !pointManagementService?.getLastGrantedPoints()}
                tabIndex={-1}
              >
                {displayMode === 'coins' ? '마지막 코인 지급 취소' : displayMode === 'notes' ? '마지막 노트 취소' : '마지막 포인트 지급 취소'}
              </UndoButton>
            </ButtonRow>
          </SecondRowContainer>
        </>
      )}

      {tableData && (
        <TableContainer>

          {/* 🎯 자리 모드 에러 메시지 */}
          {viewMode === 'seat' && seatArrangementError && (
            <div style={{
              padding: '20px',
              background: 'var(--color-panel)',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              color: '#6c757d',
              textAlign: 'center',
              fontSize: '14px',
              margin: '20px'
            }}>
              {seatArrangementError}
            </div>
          )}

          {/* 🎯 번호 모드: 기존 그리드 */}
          {viewMode === 'number' && (
            <StudentsGrid $alignment={gridAlignment}>
              {tableData.students.map((student, index) => {
                const studentInput = studentPointInputs[student.studentId] || { points: 0, memo: '', pointTypeId: '' };
                const defaultPointUnit = 1; // TODO: 환경설정에서 가져오기
                const pointInputTabIndex = (index * 2) + 1; // 1, 3, 5, 7, ...
                const memoInputTabIndex = (index * 2) + 2; // 2, 4, 6, 8, ...
                
                return (
                  <StudentCard 
                    key={student.studentId}
                    $isSelected={selectedStudents.has(student.studentId)}
                    $isSelectionMode={isSelectionMode}
                    $hasData={!!(studentPointInputs[student.studentId] && (
                      studentPointInputs[student.studentId].points !== 0 || 
                      (studentPointInputs[student.studentId].memo || '').trim() !== '' ||
                      (studentPointInputs[student.studentId].pointTypeId || '') !== ''
                    ))}
                    tabIndex={-1}
                    onClick={(e) => {
                      // 노트 모드일 때는 handleStudentClick 호출하지 않음 (노트 관리 모달만 열림)
                      if (!isSelectionMode && displayMode !== 'notes') {
                        handleStudentClick(student);
                      }
                      // isSelectionMode일 때는 아무 동작하지 않음
                      // 노트 모드일 때는 StudentCardLayout의 onClick에서 handleOpenNoteManagementModal이 호출됨
                    }}
                  >
                    {displayMode === 'notes' ? (
                      isSelectionMode ? (
                        // 포인트 지급 모드의 노트 모드: 원래 상태 유지 (사람 아이콘 + 이름 + 텍스트 입력 필드)
                        <NoteCardLayout $isSelectionMode={isSelectionMode}>
                          {/* 프로필 이미지 영역 */}
                          <ProfileImageArea 
                            $isSelectionMode={isSelectionMode}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (isSelectionMode) {
                                handleStudentSelection(student.studentId);
                              }
                            }}
                            onDoubleClick={!isSelectionMode ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleProfileImageDoubleClick(e, student);
                            } : undefined}
                          >
                            <CachedProfileImage
                              imageId={resolveDisplayProfileByStudentId(student.studentId, student.profileImage)}
                              alt={`${student.studentName} 프로필`}
                            />
                            {isSelectionMode && selectedStudents.has(student.studentId) && (
                              <SelectionIndicator>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                                  <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </SelectionIndicator>
                            )}
                          </ProfileImageArea>
                          
                          {/* 2열: 멤버 정보와 노트 입력 */}
                          <NoteContentColumn>
                            {/* 1행: 사람 아이콘 + 멤버 이름 */}
                            <StudentInfoRow>
                              <PersonIcon onClick={() => handleOpenStudentTagModal(student.studentId)}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                              </PersonIcon>
                              <NoteStudentName>{student.studentName}</NoteStudentName>
                              {(() => {
                                const relatedStudents = studentPointInputs[student.studentId]?.relatedStudents || [];
                                // 본인을 제외한 관련 멤버들만 필터링
                                const filteredRelatedStudents = relatedStudents.filter(relatedStudent => relatedStudent.id !== student.studentId);
                                const relatedCount = filteredRelatedStudents.length;
                                
                                return relatedCount > 0 ? (
                                  <RelatedStudentsInfo>
                                    외 {relatedCount}명
                                  </RelatedStudentsInfo>
                                ) : null;
                              })()}
                            </StudentInfoRow>
                            
                            {/* 2행: 텍스트 입력 필드 */}
                            <NoteTextArea
                              placeholder="멤버에 대한 노트를 입력하세요..."
                              value={studentPointInputs[student.studentId]?.memo || ''}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                setStudentPointInputs(prev => ({
                                  ...prev,
                                  [student.studentId]: {
                                    ...prev[student.studentId],
                                    memo: e.target.value,
                                    points: 0, // 노트 모드에서는 포인트 0
                                    pointTypeId: ''
                                  }
                                }));
                              }}
                            />
                          </NoteContentColumn>
                          
                          {/* 타입 토글 버튼 (포인트 지급 모드에서만 표시) */}
                          <NoteProfileColumn>
                            <NumberModeTypeButton onClick={() => toggleStudentNoteType(student.studentId)}>
                              {(() => {
                                const noteType = studentPointInputs[student.studentId]?.noteType || '일반';
                                const color = noteType === '일반' ? '#d3d3d3' :
                                            noteType === '학습' ? '#4DC7AE' :
                                            '#ff6b6b';
                                return (
                                  <>
                                    <span style={{
                                      display: 'inline-block',
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: color,
                                      marginRight: '6px'
                                    }}></span>
                                    {noteType}
                                  </>
                                );
                              })()}
                            </NumberModeTypeButton>
                          </NoteProfileColumn>
                        </NoteCardLayout>
                      ) : (
                        // 포인트 확인 모드의 노트 모드: 1열 프로필 이미지, 2열 번호/이름만 표시
                        <StudentCardLayout 
                          $isSelectionMode={isSelectionMode}
                          $displayMode={displayMode}
                          onClick={() => handleOpenNoteManagementModal(student.studentId)}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* 왼쪽: 프로필 이미지 영역 */}
                          <ProfileImageArea 
                            $isSelectionMode={isSelectionMode}
                            $displayMode={displayMode}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (isSelectionMode) {
                                handleStudentSelection(student.studentId);
                              }
                            }}
                            onDoubleClick={!isSelectionMode ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleProfileImageDoubleClick(e, student);
                            } : undefined}
                          >
                            <CachedProfileImage
                              imageId={resolveDisplayProfileByStudentId(student.studentId, student.profileImage)}
                              alt="프로필"
                              fallbackImageId="basic-0"
                              onDoubleClick={!isSelectionMode ? (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleProfileImageDoubleClick(e, student);
                              } : undefined}
                            />
                            {isSelectionMode && selectedStudents.has(student.studentId) && (
                              <SelectionIndicator>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                                  <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </SelectionIndicator>
                            )}
                          </ProfileImageArea>
                          
                          {/* 중앙: 정보 영역 */}
                          <InfoArea $isSelectionMode={isSelectionMode}>
                            <InfoTopRow $isSelectionMode={isSelectionMode}>
                              <StudentInfoGroup>
                                <StudentNumber>{student.studentNumber ? String(student.studentNumber).padStart(2, '0') : `ID: ${student.studentId.slice(-4)}`}</StudentNumber>
                                <StudentName>{student.studentName || `멤버 ${student.studentId.slice(-4)}`}</StudentName>
                              </StudentInfoGroup>
                              {!isSelectionMode && (
                                <LevelBadge $level={calculateLevel(student.totalPoints).level}>{calculateLevel(student.totalPoints).level}</LevelBadge>
                              )}
                            </InfoTopRow>
                            {/* 노트(기록) 모드: 중간 이름 크게 + 하단에 숫자 한 줄 */}
                            {displayMode === 'notes' && !isSelectionMode && (
                              <>
                                <NoteMiddleName>
                                  <NoteMiddleNameText>{student.studentName || `멤버 ${student.studentId.slice(-4)}`}</NoteMiddleNameText>
                                </NoteMiddleName>

                                <NoteCountBottomRow>
                                  <NoteCountBadge $bg="#D3D3D3">
                                    {studentNoteCounts[student.studentId]?.일반 || 0}
                                  </NoteCountBadge>
                                  <NoteCountBadge $bg="#4CC7AE">
                                    {studentNoteCounts[student.studentId]?.학습 || 0}
                                  </NoteCountBadge>
                                  <NoteCountBadge $bg="#FF6B6B">
                                    {studentNoteCounts[student.studentId]?.행동 || 0}
                                  </NoteCountBadge>
                                </NoteCountBottomRow>
                              </>
                            )}
                          </InfoArea>
                          
                          {/* 오른쪽: 레벨 진행 막대 (포인트 확인 모드일 때만 표시) */}
                          <RightArea $isSelectionMode={isSelectionMode}>
                            {!isSelectionMode && (
                              <LevelProgressBar $progress={calculateLevel(student.totalPoints).progress} $level={calculateLevel(student.totalPoints).level}>
                                {/* 레벨업 진행도 구현 */}
                              </LevelProgressBar>
                            )}
                          </RightArea>
                        </StudentCardLayout>
                      )
                    ) : (
                      // 기존 포인트/코인 모드일 때의 레이아웃
                    <StudentCardLayout $isSelectionMode={isSelectionMode} $displayMode={displayMode}>
                      {/* 왼쪽: 프로필 이미지 영역 */}
                      <ProfileImageArea 
                        $isSelectionMode={isSelectionMode}
                        $displayMode={displayMode}
                        tabIndex={-1}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isSelectionMode) {
                            // 포인트 지급 모드에서는 멤버 선택
                            handleStudentSelection(student.studentId);
                          }
                        }}
                        onDoubleClick={!isSelectionMode ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleProfileImageDoubleClick(e, student);
                        } : undefined}
                      >
                        <CachedProfileImage
                          imageId={resolveDisplayProfileByStudentId(student.studentId, student.profileImage)}
                          alt="프로필"
                          fallbackImageId="basic-0"
                          onDoubleClick={!isSelectionMode ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProfileImageDoubleClick(e, student);
                          } : undefined}
                        />
                        {isSelectionMode && selectedStudents.has(student.studentId) && (
                          <SelectionIndicator>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong)"/>
                              <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </SelectionIndicator>
                        )}
                      </ProfileImageArea>
                      
                      {/* 중앙: 정보 영역 */}
                      <InfoArea $isSelectionMode={isSelectionMode}>
                        <InfoTopRow $isSelectionMode={isSelectionMode}>
                          <StudentInfoGroup>
                            <StudentNumber>{student.studentNumber ? String(student.studentNumber).padStart(2, '0') : `ID: ${student.studentId.slice(-4)}`}</StudentNumber>
                            <StudentName>{student.studentName || `멤버 ${student.studentId.slice(-4)}`}</StudentName>
                          </StudentInfoGroup>
                          {!isSelectionMode && (
                            <LevelBadge $level={calculateLevel(student.totalPoints).level}>{calculateLevel(student.totalPoints).level}</LevelBadge>
                          )}
                        </InfoTopRow>

                        {!isSelectionMode ? (
                          (() => {
                            const member = classMembers.find(m => m.id === student.studentId);
                            const totalCoins = calculateTotalCoins(member?.coinsHistory);

                            const formatCompact = (value: number) => {
                              if (value >= 1_000_000) return `${Math.floor(value / 1_000_000)}M`;
                              if (value >= 1_000) return `${Math.floor(value / 1_000)}K`;
                              return '';
                            };

                            if (displayMode === 'coins') {
                              const compactCoin = formatCompact(totalCoins);
                              const showCompactCoin = !!compactCoin;
                              const displayCoinStr = showCompactCoin
                                ? (totalCoins % 1000).toString().padStart(3, '0')
                                : totalCoins.toString().padStart(3, '0');

                              return (
                                <>
                                  <CoinCompactRow>
                                    <CoinIcon>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                      </svg>
                                    </CoinIcon>
                                    <MultiplySign style={{ visibility: showCompactCoin ? 'visible' : 'hidden' }}>×</MultiplySign>
                                    <CompactCoinsText style={{ visibility: showCompactCoin ? 'visible' : 'hidden' }}>
                                      {showCompactCoin ? compactCoin : '0'}
                                    </CompactCoinsText>
                                  </CoinCompactRow>
                                  <CoinTotalDisplay $digitCount={showCompactCoin ? 3 : totalCoins.toString().length}>
                                    {displayCoinStr}
                                  </CoinTotalDisplay>
                                </>
                              );
                            }

                            const compactPoints = formatCompact(student.totalPoints);
                            const showCompactPoints = !!compactPoints;
                            const displayPointsStr = showCompactPoints
                              ? (student.totalPoints % 1000).toString().padStart(3, '0')
                              : student.totalPoints.toString().padStart(3, '0');

                            return (
                              <>
                                <PointInfoRow>
                                  <CompactPointsRow>
                                    <PointIcon>
                                      <img src="/point.svg" alt="포인트" width={18} height={18} />
                                    </PointIcon>
                                    <PointMultiplySign style={{ visibility: showCompactPoints ? 'visible' : 'hidden' }}>×</PointMultiplySign>
                                    <CompactPointsText style={{ visibility: showCompactPoints ? 'visible' : 'hidden' }}>
                                      {showCompactPoints ? compactPoints : '0'}
                                    </CompactPointsText>
                                  </CompactPointsRow>
                                </PointInfoRow>
                                <TotalPointsDisplay $digitCount={showCompactPoints ? 3 : student.totalPoints.toString().length}>
                                  {displayPointsStr}
                                </TotalPointsDisplay>
                              </>
                            );
                          })()
                        ) : (
                          <PointInputContainer>
                            <PointValueInput
                              $isCoinMode={displayMode === 'coins'}
                              $isNegative={displayMode === 'coins' && studentInput.points !== undefined && studentInput.points < 0}
                              $isZero={studentInput.points === 0 || studentInput.points === undefined}
                              type="text"
                              tabIndex={pointInputTabIndex}
                              value={studentInput.points !== undefined ? 
                                (studentNegativeModes[student.studentId] && studentInput.points === 0 ? '-0' : studentInput.points.toString()) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                const isCurrentlyNegative = studentNegativeModes[student.studentId] || false;
                                
                                // 코인 모드일 때는 음수도 허용
                                if (displayMode === 'coins') {
                                  let processedValue = value;
                                  
                                  // 음수 모드가 아닌 경우 숫자만 허용
                                  if (!isCurrentlyNegative) {
                                    processedValue = processedValue.replace(/[^0-9]/g, '');
                                  } else {
                                    // 음수 모드인 경우 숫자와 마이너스 기호만 허용
                                    processedValue = processedValue.replace(/[^0-9-]/g, '');
                                    // 마이너스 기호는 맨 앞에만 허용
                                    if (processedValue.includes('-') && processedValue.indexOf('-') !== 0) {
                                      processedValue = processedValue.replace(/-/g, '');
                                    }
                                    // 마이너스 기호가 여러 개인 경우 첫 번째만 유지
                                    if ((processedValue.match(/-/g) || []).length > 1) {
                                      processedValue = '-' + processedValue.replace(/-/g, '');
                                    }
                                    // 음수 모드인데 마이너스 기호가 없으면 자동으로 추가
                                    if (processedValue && !processedValue.startsWith('-') && isCurrentlyNegative) {
                                      processedValue = '-' + processedValue;
                                    }
                                  }
                                  
                                  // 모든 수를 지우면 양수 모드로 되돌아가기
                                  if (processedValue === '') {
                                    setStudentNegativeModes(prev => ({
                                      ...prev,
                                      [student.studentId]: false
                                    }));
                                    setStudentPointInputs(prev => ({
                                      ...prev,
                                      [student.studentId]: {
                                        ...prev[student.studentId],
                                        points: 0
                                      }
                                    }));
                                    return;
                                  }
                                  
                                  // -0에서 0을 지우면 양수 모드로 되돌아가기
                                  if (processedValue === '-') {
                                    setStudentNegativeModes(prev => ({
                                      ...prev,
                                      [student.studentId]: false
                                    }));
                                    setStudentPointInputs(prev => ({
                                      ...prev,
                                      [student.studentId]: {
                                        ...prev[student.studentId],
                                        points: 0
                                      }
                                    }));
                                    return;
                                  }
                                  
                                  // -0 상태에서 숫자를 입력할 때 처리
                                  if (processedValue === '-0') {
                                    setStudentPointInputs(prev => ({
                                      ...prev,
                                      [student.studentId]: {
                                        ...prev[student.studentId],
                                        points: 0
                                      }
                                    }));
                                    return;
                                  }
                                  
                                  const numValue = parseInt(processedValue) || 0;
                                  setStudentPointInputs(prev => ({
                                    ...prev,
                                    [student.studentId]: {
                                      ...prev[student.studentId],
                                      points: numValue
                                    }
                                  }));
                                } else {
                                  // 포인트 모드일 때는 양수만 허용 (음수 모드 상태 초기화)
                                  setStudentNegativeModes(prev => ({
                                    ...prev,
                                    [student.studentId]: false
                                  }));
                                  if (value === '' || /^\d+$/.test(value)) {
                                    setStudentPointInputs(prev => ({
                                      ...prev,
                                      [student.studentId]: {
                                        ...prev[student.studentId],
                                        points: value === '' ? 0 : parseInt(value)
                                      }
                                    }));
                                  }
                                }
                              }}
                              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (displayMode === 'coins') {
                                  // '-' 키를 누르면 음수 모드 토글
                                  if (e.key === '-') {
                                    e.preventDefault();
                                    const isCurrentlyNegative = studentNegativeModes[student.studentId] || false;
                                    
                                    if (isCurrentlyNegative) {
                                      // 음수 모드에서 양수 모드로 전환
                                      setStudentNegativeModes(prev => ({
                                        ...prev,
                                        [student.studentId]: false
                                      }));
                                      setStudentPointInputs(prev => ({
                                        ...prev,
                                        [student.studentId]: {
                                          ...prev[student.studentId],
                                          points: 0
                                        }
                                      }));
                                    } else {
                                      // 양수 모드에서 음수 모드로 전환
                                      setStudentNegativeModes(prev => ({
                                        ...prev,
                                        [student.studentId]: true
                                      }));
                                      setStudentPointInputs(prev => ({
                                        ...prev,
                                        [student.studentId]: {
                                          ...prev[student.studentId],
                                          points: 0
                                        }
                                      }));
                                    }
                                  }
                                  // 'Delete' 또는 'Backspace'로 모든 내용을 지우면 양수 모드로 복귀
                                  else if ((e.key === 'Delete' || e.key === 'Backspace') && e.currentTarget.value === '') {
                                    setStudentNegativeModes(prev => ({
                                      ...prev,
                                      [student.studentId]: false
                                    }));
                                    setStudentPointInputs(prev => ({
                                      ...prev,
                                      [student.studentId]: {
                                        ...prev[student.studentId],
                                        points: 0
                                      }
                                    }));
                                  }
                                }
                              }}
                              onDoubleClick={(e: React.MouseEvent<HTMLInputElement>) => {
                                e.stopPropagation();
                                (e.target as HTMLInputElement).select();
                              }}
                              onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
                              placeholder="0"
                            />
                          </PointInputContainer>
                        )}
                      </InfoArea>
                      
                      
                      {/* 하단: 메모 입력 행 (포인트 지급 모드일 때만) */}
                      {isSelectionMode && (
                        <BottomRow onClick={(e) => e.stopPropagation()}>
                          <PointTypeSelectButton
                            onClick={(e) => {
                              if (displayMode !== 'coins') { // 포인트 모드일 때만 작동
                                e.stopPropagation();
                                setCurrentSelectingStudentId(student.studentId);
                                loadPointTypes();
                                loadTimeTags(); // 타임 태그도 함께 로드
                                setShowPointTypeSelectModal(true);
                              }
                            }}
                            disabled={displayMode === 'coins'} // 코인 모드일 때 비활성화
                            tabIndex={-1}
                          >
                            {studentInput.pointTypeId ? (
                              (() => {
                                if (studentInput.pointTypeId === 'positive-default') {
                                  return <GemIcon color="red" size={16} />;
                                } else if (studentInput.pointTypeId === 'negative-default') {
                                  return <GemIcon color="gray" size={16} />;
                                } else {
                                  const allTypes = [...availablePointTypes.positive, ...availablePointTypes.negative];
                                  const type = allTypes.find(t => t.id === studentInput.pointTypeId);
                                  return type ? <GemIcon color={type.category === 'positive' ? 'red' : 'gray'} size={16} /> : '세트';
                                }
                              })()
                            ) : (
                              '세트'
                            )}
                          </PointTypeSelectButton>
                          <MemoInputField
                            placeholder="메모를 입력하세요"
                            value={studentInput.memo}
                            tabIndex={memoInputTabIndex}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setStudentPointInputs(prev => ({
                                ...prev,
                                [student.studentId]: {
                                  ...prev[student.studentId],
                                  memo: e.target.value
                                }
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </BottomRow>
                      )}
                      
                      {/* 오른쪽: 레벨업 바 및 버튼 */}
                      <RightArea $isSelectionMode={isSelectionMode} onClick={(e) => e.stopPropagation()}>
                        {!isSelectionMode ? (
                          <LevelProgressBar $progress={calculateLevel(student.totalPoints).progress} $level={calculateLevel(student.totalPoints).level}>
                            {/* 레벨업 진행도 구현 */}
                          </LevelProgressBar>
                        ) : (
                          <PointControlButtons>
                            <PlusButton
                              onClick={() => {
                                const incrementValue = displayMode === 'coins' ? coinDefaultPoints : defaultPointUnit;
                                setStudentPointInputs(prev => ({
                                  ...prev,
                                  [student.studentId]: {
                                    ...prev[student.studentId],
                                    points: (prev[student.studentId]?.points || 0) + incrementValue
                                  }
                                }));
                              }}
                              tabIndex={-1}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </PlusButton>
                            <CenterButtonWrapper>
                              <ClearButton
                                onClick={() => {
                                  // 음수 모드 상태도 함께 초기화
                                  setStudentNegativeModes(prev => ({
                                    ...prev,
                                    [student.studentId]: false
                                  }));
                                  setStudentPointInputs(prev => ({
                                    ...prev,
                                    [student.studentId]: {
                                      points: 0,
                                      memo: '',
                                      pointTypeId: ''
                                    }
                                  }));
                                }}
                                tabIndex={-1}
                              >
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="1" y="1" width="8" height="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                </svg>
                              </ClearButton>
                            </CenterButtonWrapper>
                            <MinusButtonBottom
                              onClick={() => {
                                const decrementValue = displayMode === 'coins' ? coinDefaultPoints : defaultPointUnit;
                                setStudentPointInputs(prev => ({
                                  ...prev,
                                  [student.studentId]: {
                                    ...prev[student.studentId],
                                    points: displayMode === 'coins' 
                                      ? (prev[student.studentId]?.points || 0) - decrementValue // 코인 모드에서는 음수 허용
                                      : Math.max(0, (prev[student.studentId]?.points || 0) - decrementValue) // 포인트 모드에서는 0 이하 금지
                                  }
                                }));
                              }}
                              tabIndex={-1}
                            >
                              <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </MinusButtonBottom>
                          </PointControlButtons>
                        )}
                      </RightArea>
                    </StudentCardLayout>
                    )}
                </StudentCard>
                );
              })}
            </StudentsGrid>
          )}

          {/* 🎯 자리 모드: 16x16 그리드 */}
          {viewMode === 'seat' && !seatArrangementError && seatArrangementData && (
            (() => {
              // 자리 모드 그리드: 상/하단의 "완전히 비어있는 행"만 trim (중간 빈 행은 유지)
              // 180도 회전 적용 후 좌표를 기준으로 trim 해야 함
              const GRID_TRACKS = 16; // 0..15
              const ROTATE_MAX_INDEX = 14; // 기존 로직 유지 (PresentationPage 방식)

              const usedRows = new Set<number>();
              const usedCols = new Set<number>();
              let minUsedRow = Number.POSITIVE_INFINITY;
              let maxUsedRow = Number.NEGATIVE_INFINITY;

              seatArrangementData.forEach((seatStudent: any) => {
                const seatRow = typeof seatStudent?.seatRow === 'number' ? seatStudent.seatRow : 0;
                const seatCol = typeof seatStudent?.seatCol === 'number' ? seatStudent.seatCol : 0;

                const rotatedRow = isSeatRotated ? (ROTATE_MAX_INDEX - seatRow) : seatRow;
                const rotatedCol = isSeatRotated ? (ROTATE_MAX_INDEX - seatCol) : seatCol;

                const occupiedRows = [rotatedRow, rotatedRow + 1];
                const occupiedCols = [rotatedCol, rotatedCol + 1];

                occupiedRows.forEach((r) => {
                  if (r < 0 || r >= GRID_TRACKS) return;
                  usedRows.add(r);
                  minUsedRow = Math.min(minUsedRow, r);
                  maxUsedRow = Math.max(maxUsedRow, r);
                });

                occupiedCols.forEach((c) => {
                  if (c < 0 || c >= GRID_TRACKS) return;
                  usedCols.add(c);
                });
              });

              const effectiveMinRow = Number.isFinite(minUsedRow) ? minUsedRow : 0;
              const effectiveMaxRow = Number.isFinite(maxUsedRow) ? maxUsedRow : (GRID_TRACKS - 1);

              const trimmedStartRow = Math.max(0, Math.min(effectiveMinRow, GRID_TRACKS - 1));
              const trimmedEndRow = Math.max(0, Math.min(effectiveMaxRow, GRID_TRACKS - 1));
              const rowOffset = trimmedStartRow;

              const gridTemplateRows = Array.from(
                { length: Math.max(1, trimmedEndRow - trimmedStartRow + 1) },
                (_, idx) => {
                  const rowIndex = trimmedStartRow + idx;
                  return usedRows.has(rowIndex) ? '1fr' : '0.5fr';
                }
              ).join(' ');

              const gridTemplateColumns = Array.from(
                { length: GRID_TRACKS },
                (_, i) => (usedCols.has(i) ? '1fr' : '0.5fr')
              ).join(' ');

              return (
                <>
                  <SeatModeGrid 
                    $gridTemplateColumns={gridTemplateColumns}
                    $gridTemplateRows={gridTemplateRows}
                  >
                    {seatArrangementData.map((seatStudent: any, index: number) => {
                    // tableData에서 해당 멤버의 상세 정보 찾기 (자리 모드에서도 포인트/레벨 즉시 반영용)
                    const fullStudentData = tableData?.students.find((s: any) => s.studentId === seatStudent.studentId);
                    const displayTotalPoints = fullStudentData?.totalPoints ?? seatStudent.totalPoints ?? 0;
                    const memberCoins = classMembers.find(m => m.id === seatStudent.studentId);
                    const totalCoins = calculateTotalCoins(memberCoins?.coinsHistory);
                    const studentInput = studentPointInputs[seatStudent.studentId] || { points: 0, memo: '', pointTypeId: '' };
                    const formatCompact = (value: number) => {
                      if (value >= 1_000_000) return `${Math.floor(value / 1_000_000)}M`;
                      if (value >= 1_000) return `${Math.floor(value / 1_000)}K`;
                      return '';
                    };
                    
                    // 180도 회전 좌표 계산 (PresentationPage와 동일한 방식)
                    // 그리드가 16x16이고 템플릿이 역순이면, 인덱스 0은 15가 되고, 인덱스 n은 15-n이 됨
                    // 블럭이 2x2를 차지하므로 (seatRow, seatCol)에서 시작하는 블럭은 회전 후 (14-seatRow, 14-seatCol)에서 시작
                    // 0-based로 계산 후 1-based로 변환
                    const rotatedRow = isSeatRotated 
                      ? 14 - seatStudent.seatRow
                      : seatStudent.seatRow;
                    const rotatedCol = isSeatRotated 
                      ? 14 - seatStudent.seatCol
                      : seatStudent.seatCol;
                    
                    // gridRow/gridColumn은 1-based이므로 +1
                    const rotatedGridRow = (rotatedRow - rowOffset) + 1;
                    const rotatedGridCol = rotatedCol + 1;
                    
                    const seatBlockHasData = !!(studentPointInputs[seatStudent.studentId] && (
                      studentPointInputs[seatStudent.studentId].points !== 0 ||
                      (studentPointInputs[seatStudent.studentId].memo || '').trim() !== '' ||
                      (studentPointInputs[seatStudent.studentId].pointTypeId || '') !== ''
                    ));
                    return (
                      <SeatModeStudentBlock
                        key={seatStudent.studentId}
                        $hasData={seatBlockHasData}
                        style={{
                          gridRow: `${rotatedGridRow} / span 2`,
                          gridColumn: `${rotatedGridCol} / span 2`,
                          display: 'grid',
                          gridTemplateColumns: isSelectionMode ? '60px 1fr auto' : '80px 1fr 12px',
                          gridTemplateRows: isSelectionMode ? '60px auto' : '1fr',
                          gap: '4px',
                          overflow: 'hidden',
                          position: 'relative',
                          fontSize: '12px',
                          padding: '4px 4px 6px 4px',
                          height: '100%',
                          flexDirection: 'column',
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!isSelectionMode && fullStudentData) {
                            if (displayMode === 'notes') {
                              handleOpenNoteManagementModal(fullStudentData.studentId);
                            } else {
                              const classMember = classMembers.find(member => member.id === fullStudentData.studentId);
                              if (classMember) {
                                setSelectedStudentForDetail(classMember);
                                setShowStudentPointDetailModal(true);
                              }
                            }
                          }
                        }}
                      >
                        {/* 자리모드(확인 모드): 3열 세로 레벨바 (번호모드와 동일) */}
                        {!isSelectionMode && (
                          <div
                            style={{
                              gridRow: '1 / 2',
                              gridColumn: '3 / 4',
                              display: 'flex',
                              alignItems: 'stretch',
                              justifyContent: 'center',
                              padding: '0 2px',
                              height: '100%',
                              minHeight: '0',
                            }}
                          >
                            <LevelProgressBar
                              $progress={calculateLevel(displayTotalPoints).progress}
                              $level={calculateLevel(displayTotalPoints).level}
                            />
                          </div>
                        )}

                        {displayMode === 'notes' ? (
                          isSelectionMode ? (
                            // 노트 모드 - 포인트 지급 모드: 1열 프로필+타입버튼, 2열 사람아이콘+이름+텍스트필드(3열까지 확장)
                            <>
                              {/* 1열: 프로필 이미지 + 타입 토글 버튼 - 포인트/코인 탭과 동일하게 테두리 적용 */}
                              <div style={{
                                gridRow: '1 / 2',
                                gridColumn: '1 / 2',
                                width: isSelectionMode ? '60px' : '80px',
                                maxWidth: isSelectionMode ? '60px' : '80px',
                                height: isSelectionMode ? '60px' : '80px',
                                aspectRatio: '1 / 1',
                                minWidth: isSelectionMode ? '60px' : '80px',
                                minHeight: isSelectionMode ? '60px' : '80px',
                                background: 'transparent',
                                border: '2px solid #000000',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                flexShrink: 0,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isSelectionMode) {
                                  handleStudentSelection(seatStudent.studentId);
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}>
                                <CachedProfileImage
                                  imageId={resolveDisplayProfileByStudentId(seatStudent.studentId, seatStudent.profileImage)}
                                  alt={seatStudent.name}
                                  fallbackImageId="basic-0"
                                />
                                {isSelectionMode && selectedStudents.has(seatStudent.studentId) && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    background: 'var(--color-accent-strong)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10
                                  }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M4 12L10 18L20 6" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                )}
                              </div>

                              {/* 타입 토글 버튼 - 포인트 모드 세트 버튼과 동일한 위치와 크기 */}
                              <div style={{
                                gridRow: '2 / 3',
                                gridColumn: '1 / 2',
                                display: 'flex',
                                alignItems: 'flex-end',
                                padding: '0'
                              }}>
                                <SeatModeTypeButton onClick={() => toggleStudentNoteType(seatStudent.studentId)}>
                                  {(() => {
                                    const noteType = studentPointInputs[seatStudent.studentId]?.noteType || '일반';
                                    const color = noteType === '일반' ? '#d3d3d3' : 
                                                noteType === '학습' ? '#4DC7AE' : 
                                                '#ff6b6b';
                                    return (
                                      <>
                                        <span style={{
                                          display: 'inline-block',
                                          width: '6px',
                                          height: '6px',
                                          borderRadius: '50%',
                                          backgroundColor: color,
                                          marginRight: '4px'
                                        }}></span>
                                        {noteType}
                                      </>
                                    );
                                  })()}
                                </SeatModeTypeButton>
                              </div>

                              {/* 2열: 사람 아이콘 + 이름 + 텍스트 필드 (3열까지 확장) */}
                              <div style={{
                                gridRow: '1 / 3',
                                gridColumn: '2 / 4',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                padding: '0',
                                height: '100%'
                              }}>
                                {/* 1행: 사람 아이콘 + 이름 */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  flexShrink: 0
                                }}>
                                  <div onClick={() => handleOpenStudentTagModal(seatStudent.studentId)} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer'
                                  }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                  </div>
                                  <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                                    {seatStudent.name}
                                  </span>
                                  {(() => {
                                    const relatedStudents = studentPointInputs[seatStudent.studentId]?.relatedStudents || [];
                                    // 본인을 제외한 관련 멤버들만 필터링
                                    const filteredRelatedStudents = relatedStudents.filter(student => student.id !== seatStudent.studentId);
                                    const relatedCount = filteredRelatedStudents.length;
                                    return relatedCount > 0 && (
                                      <span style={{ fontSize: '10px', color: 'var(--color-text-gray)' }}>
                                        외 {relatedCount}명
                                      </span>
                                    );
                                  })()}
                                </div>
                                
                                {/* 2행: 텍스트 필드 - 상단 정렬되도록 textarea 사용 */}
                                <textarea
                                  placeholder="노트를 입력하세요"
                                  value={studentPointInputs[seatStudent.studentId]?.memo || ''}
                                  onChange={(e) => {
                                    setStudentPointInputs(prev => ({ 
                                      ...prev, 
                                      [seatStudent.studentId]: { 
                                        ...prev[seatStudent.studentId], 
                                        memo: e.target.value 
                                      } 
                                    }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '100%',
                                    padding: '6px 8px 4px 8px',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    height: '100%',
                                    minHeight: '40px',
                                    lineHeight: 1.4,
                                    resize: 'none',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    display: 'block',
                                    flex: 1,
                                    background: 'var(--color-student-block-element-bg)',
                                    color: 'var(--color-text-primary)'
                                  }}
                                  onFocus={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(var(--color-accent-rgb), 0.5)';
                                    e.currentTarget.style.boxShadow = '0 0 0 2px var(--color-accent-soft)';
                                  }}
                                  onBlur={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--color-border)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                  tabIndex={-1}
                                />
                              </div>
                            </>
                          ) : (
                            // 노트 모드 - 포인트 확인 모드: 포인트/코인 모드와 동일한 구조 (숫자 제외)
                            <>
                              {/* 🎯 1구역: 프로필 이미지 */}
                              <div style={{
                                gridRow: '1 / 2',
                                gridColumn: '1 / 2',
                                width: isSelectionMode ? '60px' : '80px',
                                maxWidth: isSelectionMode ? '60px' : '80px',
                                height: isSelectionMode ? '60px' : '80px',
                                aspectRatio: '1 / 1',
                                minWidth: isSelectionMode ? '60px' : '80px',
                                minHeight: isSelectionMode ? '60px' : '80px',
                                background: 'transparent',
                                border: '2px solid #000000',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                flexShrink: 0,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => handleOpenNoteManagementModal(seatStudent.studentId)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}>
                                <CachedProfileImage
                                  imageId={resolveDisplayProfileByStudentId(seatStudent.studentId, seatStudent.profileImage)}
                                  alt={seatStudent.name}
                                  fallbackImageId="basic-0"
                                />
                              </div>

                              {/* 🎯 2구역: 정보 영역 */}
                              <div style={{
                                gridRow: '1 / 2',
                                gridColumn: '2 / 3',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0px',
                                padding: '0px 4px',
                                minWidth: '0',
                                height: '100%'
                              }}>
                                {/* 번호/이름 + 레벨 */}
                                <div style={{
                                  display: 'flex',
                                  gap: '4px',
                                  alignItems: 'stretch',
                                  justifyContent: 'space-between',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  overflow: 'hidden',
                                  lineHeight: '1',
                                  marginBottom: '0px',
                                  flex: 1,
                                  position: 'relative'
                                }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '0', flex: 1, height: '100%' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: '1px',
                                        alignItems: 'baseline',
                                        minWidth: '0',
                                        paddingRight: displayMode === 'notes' ? '28px' : undefined
                                      }}
                                    >
                                      <span style={{ fontSize: '14px', color: 'var(--color-text-gray)', fontWeight: 500, flexShrink: 0 }}>{seatStudent.number}.</span>
                                      <span style={{ fontSize: '14px', color: 'var(--color-text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {seatStudent.name}
                                      </span>
                                    </div>
                                    {/* 노트(기록) 모드: 중간 이름 크게 + 하단 숫자 한 줄 */}
                                    {displayMode === 'notes' && !isSelectionMode && (
                                      <>
                                        <div style={{
                                          flex: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          minWidth: '0'
                                        }}>
                                          <span style={{
                                            fontSize: '26px',
                                            fontWeight: 900,
                                            color: 'var(--color-text-primary)',
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            lineHeight: 1.05,
                                            width: '100%'
                                          }}>
                                            {seatStudent.name}
                                          </span>
                                        </div>

                                        <div style={{
                                          width: '100%',
                                          marginTop: 'auto',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          gap: '6px',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          <span style={{
                                            background: '#D3D3D3',
                                            color: '#ffffff',
                                            borderRadius: 7,
                                            padding: '2px 7px',
                                            fontSize: 11,
                                            fontWeight: 900,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center'
                                          }}>
                                            {studentNoteCounts[seatStudent.studentId]?.일반 || 0}
                                          </span>
                                          <span style={{
                                            background: '#4CC7AE',
                                            color: '#ffffff',
                                            borderRadius: 7,
                                            padding: '2px 7px',
                                            fontSize: 11,
                                            fontWeight: 900,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center'
                                          }}>
                                            {studentNoteCounts[seatStudent.studentId]?.학습 || 0}
                                          </span>
                                          <span style={{
                                            background: '#FF6B6B',
                                            color: '#ffffff',
                                            borderRadius: 7,
                                            padding: '2px 7px',
                                            fontSize: 11,
                                            fontWeight: 900,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'center'
                                          }}>
                                            {studentNoteCounts[seatStudent.studentId]?.행동 || 0}
                                          </span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  {!isSelectionMode && (
                                    <div style={{
                                      ...(displayMode === 'notes' ? { position: 'absolute' as const, right: 0, top: 0 } : {}),
                                      background: getLevelColor(calculateLevel(displayTotalPoints).level),
                                      color: getLevelTextColor(calculateLevel(displayTotalPoints).level),
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      padding: '2px 4px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: '24px',
                                      height: '18px',
                                      textAlign: 'center',
                                      lineHeight: '14px',
                                      letterSpacing: '0.5px',
                                      flexShrink: 0
                                    }}>
                                      {calculateLevel(displayTotalPoints).level}
                                    </div>
                                  )}
                                </div>

                                {/* 노트 모드에서는 포인트/코인 숫자 표시하지 않음 - 포인트/코인 모드와 동일한 간격을 위해 빈 공간 추가 */}
                                {displayMode !== 'notes' && (
                                  <div style={{
                                    height: '32px',
                                    marginBottom: '1px'
                                  }} />
                                )}
                              </div>

                            </>
                          )
                        ) : (
                          // 기존 포인트/코인 모드 구조
                          <>
                        {/* 🎯 1구역: 프로필 이미지 */}
                        <div style={{
                          gridRow: '1 / 2',
                          gridColumn: '1 / 2',
                          // 자리모드: 데이터 입력 모드(60x60), 데이터 조회 모드(80x80)
                          width: isSelectionMode ? '60px' : '80px',
                          maxWidth: isSelectionMode ? '60px' : '80px',
                          minWidth: isSelectionMode ? '60px' : '80px',
                          height: isSelectionMode ? '60px' : '80px',
                          maxHeight: isSelectionMode ? '60px' : '80px',
                          minHeight: isSelectionMode ? '60px' : '80px',
                          aspectRatio: '1 / 1',
                          background: 'transparent',
                          border: '2px solid #000000',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          flexShrink: 0,
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isSelectionMode) {
                            // 포인트 지급 모드에서는 멤버 선택
                            handleStudentSelection(seatStudent.studentId);
                          }
                        }}
                        onDoubleClick={!isSelectionMode ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // 포인트 확인 모드에서만 프로필 선택 모달 열기
                          if (fullStudentData) {
                            setSelectedStudentForProfile(fullStudentData);
                            setIsProfileModalOpen(true);
                          }
                        } : undefined}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}>
                          <CachedProfileImage
                            imageId={resolveDisplayProfileByStudentId(seatStudent.studentId, seatStudent.profileImage)}
                            alt={seatStudent.name}
                            fallbackImageId="basic-0"
                            onDoubleClick={!isSelectionMode ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // 포인트 확인 모드에서만 프로필 선택 모달 열기
                              if (fullStudentData) {
                                setSelectedStudentForProfile(fullStudentData);
                                setIsProfileModalOpen(true);
                              }
                            } : undefined}
                          />
                          {isSelectionMode && selectedStudents.has(seatStudent.studentId) && (
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              border: '2px solid white',
                              background: 'var(--color-accent-strong)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 10
                            }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 12L10 18L20 6" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* 🎯 2구역: 정보 영역 */}
                        <div style={{
                          gridRow: '1 / 2',
                          gridColumn: '2 / 3',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0px',
                          padding: '0px 4px',
                          minWidth: '0'
                        }}>
                          {/* 번호/이름 + 레벨 */}
                          <div style={{
                            display: 'flex',
                            gap: '4px',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            overflow: 'hidden',
                            lineHeight: '1',
                            marginBottom: '0px'
                          }}>
                            <div style={{ display: 'flex', gap: '1px', alignItems: 'baseline', minWidth: '0', flex: 1 }}>
                              <span style={{ fontSize: '14px', color: 'var(--color-text-gray)', fontWeight: 500, flexShrink: 0 }}>{seatStudent.number}.</span>
                              <span style={{ fontSize: '14px', color: 'var(--color-text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {seatStudent.name}
                              </span>
                            </div>
                            {!isSelectionMode && (
                              <div style={{
                                background: getLevelColor(calculateLevel(displayTotalPoints).level),
                                color: getLevelTextColor(calculateLevel(displayTotalPoints).level),
                                fontSize: '10px',
                                fontWeight: 700,
                                padding: '2px 4px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '24px',
                                height: '18px',
                                textAlign: 'center',
                                lineHeight: '14px',
                                letterSpacing: '0.5px',
                                flexShrink: 0
                              }}>
                                {calculateLevel(displayTotalPoints).level}
                              </div>
                            )}
                          </div>

                          {isSelectionMode ? (
                            // 포인트 지급 모드: 입력 필드
                            <PointInputContainer>
                              <PointValueInput
                                $isCoinMode={displayMode === 'coins'}
                                $isNegative={displayMode === 'coins' && studentInput.points !== undefined && studentInput.points < 0}
                                $isZero={studentInput.points === 0 || studentInput.points === undefined}
                                type="text"
                                tabIndex={0}
                                value={studentInput.points !== undefined ? 
                                  (studentNegativeModes[seatStudent.studentId] && studentInput.points === 0 ? '-0' : studentInput.points.toString()) : ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const value = e.target.value;
                                  const isCurrentlyNegative = studentNegativeModes[seatStudent.studentId] || false;
                                  
                                  if (displayMode === 'coins') {
                                    let processedValue = value;
                                    if (!isCurrentlyNegative) {
                                      processedValue = processedValue.replace(/[^0-9]/g, '');
                                    } else {
                                      processedValue = processedValue.replace(/[^0-9-]/g, '');
                                      if (processedValue.includes('-') && processedValue.indexOf('-') !== 0) {
                                        processedValue = processedValue.replace(/-/g, '');
                                      }
                                      if ((processedValue.match(/-/g) || []).length > 1) {
                                        processedValue = '-' + processedValue.replace(/-/g, '');
                                      }
                                      if (processedValue && !processedValue.startsWith('-') && isCurrentlyNegative) {
                                        processedValue = '-' + processedValue;
                                      }
                                    }
                                    
                                    if (processedValue === '') {
                                      setStudentNegativeModes(prev => ({ ...prev, [seatStudent.studentId]: false }));
                                      setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: 0 } }));
                                      return;
                                    }
                                    if (processedValue === '-') {
                                      setStudentNegativeModes(prev => ({ ...prev, [seatStudent.studentId]: false }));
                                      setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: 0 } }));
                                      return;
                                    }
                                    if (processedValue === '-0') {
                                      setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: 0 } }));
                                      return;
                                    }
                                    
                                    const numValue = parseInt(processedValue) || 0;
                                    setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: numValue } }));
                                  } else {
                                    setStudentNegativeModes(prev => ({ ...prev, [seatStudent.studentId]: false }));
                                    if (value === '' || /^\d+$/.test(value)) {
                                      setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: value === '' ? 0 : parseInt(value) } }));
                                    }
                                  }
                                }}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                  if (displayMode === 'coins') {
                                    if (e.key === '-') {
                                      e.preventDefault();
                                      const isCurrentlyNegative = studentNegativeModes[seatStudent.studentId] || false;
                                      if (isCurrentlyNegative) {
                                        setStudentNegativeModes(prev => ({ ...prev, [seatStudent.studentId]: false }));
                                        setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: 0 } }));
                                      } else {
                                        setStudentNegativeModes(prev => ({ ...prev, [seatStudent.studentId]: true }));
                                        setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: 0 } }));
                                      }
                                    } else if ((e.key === 'Delete' || e.key === 'Backspace') && (e.target as HTMLInputElement).value === '') {
                                      setStudentNegativeModes(prev => ({ ...prev, [seatStudent.studentId]: false }));
                                    }
                                  }
                                }}
                                onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  // 한번 클릭으로 포커스하고 텍스트 선택
                                  const input = e.target as HTMLInputElement;
                                  input.focus();
                                  input.select();
                                }}
                                onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                placeholder="0"
                              />
                            </PointInputContainer>
                          ) : (
                            // 포인트 확인 모드: 정보 표시
                            <>
                              {(() => {
                                if (displayMode === 'coins') {
                                  const compactCoin = formatCompact(totalCoins);
                                  const showCompactCoin = !!compactCoin;
                                  const displayCoinStr = showCompactCoin
                                    ? (totalCoins % 1000).toString().padStart(3, '0')
                                    : totalCoins.toString().padStart(3, '0');

                                  return (
                                    <>
                                      <CoinCompactRow style={{ marginBottom: '0px', gap: '1px' }}>
                                        <CoinIcon>
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                            <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                            <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                          </svg>
                                        </CoinIcon>
                                        <MultiplySign style={{ visibility: showCompactCoin ? 'visible' : 'hidden' }}>×</MultiplySign>
                                        <CompactCoinsText style={{ visibility: showCompactCoin ? 'visible' : 'hidden' }}>
                                          {showCompactCoin ? compactCoin : '0'}
                                        </CompactCoinsText>
                                      </CoinCompactRow>
                                      <CoinTotalDisplay 
                                        $digitCount={showCompactCoin ? 3 : totalCoins.toString().length}
                                        $isSeatMode={true}
                                        style={{ marginTop: '-2px', marginBottom: '-4px' }}
                                      >
                                        {displayCoinStr}
                                      </CoinTotalDisplay>
                                    </>
                                  );
                                }

                                const compactPoints = formatCompact(displayTotalPoints);
                                const showCompactPoints = !!compactPoints;
                                const displayPointsStr = showCompactPoints
                                  ? (displayTotalPoints % 1000).toString().padStart(3, '0')
                                  : displayTotalPoints.toString().padStart(3, '0');

                                return (
                                  <>
                                    <PointInfoRow style={{ marginBottom: '0px' }}>
                                      <CompactPointsRow style={{ gap: '1px' }}>
                                        <PointIcon>
                                          <img src="/point.svg" alt="포인트" width={18} height={18} />
                                        </PointIcon>
                                        <PointMultiplySign style={{ visibility: showCompactPoints ? 'visible' : 'hidden' }}>×</PointMultiplySign>
                                        <CompactPointsText style={{ visibility: showCompactPoints ? 'visible' : 'hidden' }}>
                                          {showCompactPoints ? compactPoints : '0'}
                                        </CompactPointsText>
                                      </CompactPointsRow>
                                    </PointInfoRow>
                                    <TotalPointsDisplay 
                                      $digitCount={showCompactPoints ? 3 : displayTotalPoints.toString().length}
                                      $isSeatMode={true}
                                      style={{ marginTop: '-2px', marginBottom: '-4px' }}
                                    >
                                      {displayPointsStr}
                                    </TotalPointsDisplay>
                                  </>
                                );
                              })()}
                            </>
                          )}
                        </div>

                        {/* 🎯 BottomRow: 세트 버튼 + 메모 필드 (포인트 지급 모드) */}
                        {isSelectionMode && (
                          <>
                            {/* 세트 버튼 컨테이너 */}
                            <div style={{
                              gridRow: '2 / 3',
                              gridColumn: '1 / 2',
                              display: 'flex',
                              alignItems: 'flex-end',
                              padding: '0'
                            }}>
                              {/* 세트 버튼 */}
                              <SetButton
                                $isDisabled={displayMode === 'coins'}
                                $isSeatMode={true}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  if (displayMode !== 'coins') {
                                    setCurrentSelectingStudentId(seatStudent.studentId);
                                    loadPointTypes();
                                    setShowPointTypeSelectModal(true);
                                  }
                                }}
                                tabIndex={-1}
                              >
                                {studentInput.pointTypeId ? (
                                  (() => {
                                    if (studentInput.pointTypeId === 'positive-default') {
                                      return <GemIcon color="red" size={14} />;
                                    } else if (studentInput.pointTypeId === 'negative-default') {
                                      return <GemIcon color="gray" size={14} />;
                                    } else {
                                      const allTypes = [...availablePointTypes.positive, ...availablePointTypes.negative];
                                      const type = allTypes.find(t => t.id === studentInput.pointTypeId);
                                      return type ? <GemIcon color={type.category === 'positive' ? 'red' : 'gray'} size={14} /> : '세트';
                                    }
                                  })()
                                ) : (
                                  '세트'
                                )}
                              </SetButton>
                            </div>
                            
                            {/* 메모 필드 컨테이너 */}
                            <div style={{
                              gridRow: '2 / 3',
                              gridColumn: '2 / 3',
                              display: 'flex',
                              alignItems: 'flex-end',
                              padding: '0'
                            }}>
                              {/* 메모 필드 */}
                              <input
                                type="text"
                                placeholder="메모"
                                value={studentInput.memo || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], memo: e.target.value } }));
                                }}
                                onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
                                style={{
                                  width: '100%',
                                  padding: '2px 2px',
                                  border: '1px solid var(--color-border)',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  height: '20px',
                                  boxSizing: 'border-box',
                                  minWidth: '0',
                                  outline: 'none',
                                  background: 'var(--color-student-block-element-bg)',
                                  color: 'var(--color-text-primary)'
                                }}
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                  e.currentTarget.style.borderColor = 'rgba(var(--color-accent-rgb), 0.5)';
                                  e.currentTarget.style.boxShadow = '0 0 0 2px var(--color-accent-soft)';
                                }}
                                onBlur={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--color-border)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                tabIndex={-1}
                              />
                            </div>
                          </>
                        )}

                        {/* 🎯 3구역: 제어 버튼 (포인트 지급 모드) */}
                        {isSelectionMode && (
                          <div style={{
                            gridRow: '1 / 3',
                            gridColumn: '3 / 4',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0'
                          }}>
                            {/* + 버튼 (위쪽 정렬) */}
                            <button
                              onClick={() => {
                                const incrementValue = displayMode === 'coins' ? coinDefaultPoints : 1;
                                setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: (prev[seatStudent.studentId]?.points || 0) + incrementValue } }));
                              }}
                              style={{
                                width: '20px',
                                height: '20px',
                                minWidth: '4px',
                                minHeight: '4px',
                                padding: '0',
                                fontSize: '12px',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-student-block-element-bg)',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.3s ease',
                                boxShadow: 'none'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--color-student-block-element-bg)';
                                e.currentTarget.style.borderColor = 'var(--color-text-muted)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--color-student-block-element-bg)';
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              onMouseDown={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              tabIndex={-1}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>

                            {/* × 버튼 (중앙 정렬) */}
                            <button
                              onClick={() => {
                                setStudentNegativeModes(prev => ({
                                  ...prev,
                                  [seatStudent.studentId]: false
                                }));
                                setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { points: 0, memo: '', pointTypeId: '' } }));
                              }}
                              style={{
                                width: '20px',
                                height: '20px',
                                minWidth: '4px',
                                minHeight: '4px',
                                padding: '0',
                                fontSize: '12px',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-student-block-element-bg)',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.3s ease',
                                boxShadow: 'none'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--color-student-block-element-bg)';
                                e.currentTarget.style.borderColor = 'var(--color-text-muted)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--color-student-block-element-bg)';
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              onMouseDown={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              tabIndex={-1}
                            >
                              <svg width="9" height="9" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="1" y="1" width="8" height="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                              </svg>
                            </button>

                            {/* − 버튼 (아래쪽 정렬) */}
                            <button
                              onClick={() => {
                                const decrementValue = displayMode === 'coins' ? coinDefaultPoints : 1;
                                setStudentPointInputs(prev => ({ ...prev, [seatStudent.studentId]: { ...prev[seatStudent.studentId], points: displayMode === 'coins' 
                                  ? (prev[seatStudent.studentId]?.points || 0) - decrementValue 
                                  : Math.max(0, (prev[seatStudent.studentId]?.points || 0) - decrementValue) } }));
                              }}
                              style={{
                                width: '20px',
                                height: '20px',
                                minWidth: '4px',
                                minHeight: '4px',
                                padding: '0',
                                fontSize: '12px',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-student-block-element-bg)',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.3s ease',
                                boxShadow: 'none'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--color-student-block-element-bg)';
                                e.currentTarget.style.borderColor = 'var(--color-text-muted)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--color-student-block-element-bg)';
                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              onMouseDown={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              tabIndex={-1}
                            >
                              <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        )}
                          </>
                        )}
                      </SeatModeStudentBlock>
                    );
                    })}
                  </SeatModeGrid>
                </>
              );
            })()
          )}
        </TableContainer>
      )}

      {/* 180도 회전 버튼 - 자리 모드일 때만 표시, TableContainer 밖에 위치 */}
      {tableData && viewMode === 'seat' && !seatArrangementError && seatArrangementData && (
                  <RotateButtonContainer>
                    <RotateButton onClick={() => setIsSeatRotated(!isSeatRotated)} title="180도 회전">
                      <RotateIconWrapper>
                        <RotateArrowIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                          </svg>
                        </RotateArrowIcon>
                        <RotateNumberIcon>180</RotateNumberIcon>
                      </RotateIconWrapper>
                    </RotateButton>
                  </RotateButtonContainer>
      )}
      
      {/* 그리드 정렬 토글 - 번호 모드일 때만 표시 (TableContainer 바깥) */}
      {tableData && viewMode === 'number' && (
        <GridAlignmentContainer>
          <GridAlignmentToggle 
            onClick={() => setGridAlignment(prev => prev === 'left' ? 'center' : 'left')}
            tabIndex={-1}
            title={gridAlignment === 'left' ? '중앙 정렬로 변경' : '왼쪽 정렬로 변경'}
          >
            <GridAlignmentOption $isActive={gridAlignment === 'left'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </GridAlignmentOption>
            <GridAlignmentOption $isActive={gridAlignment === 'center'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6h18M6 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </GridAlignmentOption>
          </GridAlignmentToggle>
        </GridAlignmentContainer>
      )}

      {selectedStudent && (
        <StudentDetailModal>
          <ModalOverlay onClick={() => setSelectedStudent(null)} />
          <ModalContent>
            <ModalHeader>
              <h3>{selectedStudent.studentNumber ? `${selectedStudent.studentNumber}번` : `ID: ${selectedStudent.studentId.slice(-4)}`} {selectedStudent.studentName} 상세 내역</h3>
              <CloseButton onClick={() => setSelectedStudent(null)}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              {/* 탭 네비게이션 */}
              <TabContainer>
                <TabButton 
                  active={activeTab === 'all'} 
                  onClick={() => setActiveTab('all')}
                >
                  전체
                </TabButton>
                <TabButton 
                  active={activeTab === 'general'} 
                  onClick={() => setActiveTab('general')}
                >
                  일반과제
                </TabButton>
                <TabButton 
                  active={activeTab === 'project'} 
                  onClick={() => setActiveTab('project')}
                >
                  프로젝트과제
                </TabButton>
                <TabButton 
                  active={activeTab === 'routine'} 
                  onClick={() => setActiveTab('routine')}
                >
                  루틴과제
                </TabButton>
              </TabContainer>

              {/* 개선된 종합 표시 (멤버 개인 정보) */}
              <SummaryContainer>
                <SummaryLeft>
                  <SummaryRow>
                    <SummaryType>일반과제</SummaryType>
                    <SummaryCount>{selectedStudentTypeStats.general.count}개</SummaryCount>
                    <SummaryPoints>{selectedStudentTypeStats.general.points}점</SummaryPoints>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryType>프로젝트과제</SummaryType>
                    <SummaryCount>{selectedStudentTypeStats.project.count}개</SummaryCount>
                    <SummaryPoints>{selectedStudentTypeStats.project.points}점</SummaryPoints>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryType>루틴과제</SummaryType>
                    <SummaryCount>{selectedStudentTypeStats.routine.count}개</SummaryCount>
                    <SummaryPoints>{selectedStudentTypeStats.routine.points}점</SummaryPoints>
                  </SummaryRow>
                </SummaryLeft>
                <SummaryRight>
                  <SummaryTotal>
                    <SummaryTotalLabel>총점</SummaryTotalLabel>
                    <SummaryTotalValue>{selectedStudent?.totalPoints || 0}점</SummaryTotalValue>
                  </SummaryTotal>
                </SummaryRight>
              </SummaryContainer>

              <AssignmentsTable>
                <TableHeader>
                  <tr>
                    <HeaderCell columnIndex={0}>타입</HeaderCell>
                    <HeaderCell columnIndex={1}>마감일</HeaderCell>
                    <HeaderCell columnIndex={2}>메모</HeaderCell>
                    <HeaderCell columnIndex={3}>포인트</HeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {filteredData?.students.find(s => s.studentId === selectedStudent.studentId)?.assignments.map(assignment => (
                    <TableRow key={assignment.assignmentId}>
                      <TableCell columnIndex={0}>
                        <TypeBadge type={assignment.type}>
                          {assignment.type === 'general' ? 'G' : 
                           assignment.type === 'project' ? 'P' : 'R'}
                        </TypeBadge>
                      </TableCell>
                      <TableCell columnIndex={1}>
                        {formatDueDateDisplay(assignment.dueDate)}
                      </TableCell>
                      <TableCell columnIndex={2}>
                        {assignment.memo || '-'}
                      </TableCell>
                      <TableCell columnIndex={3}>
                        <PointsText points={assignment.points} status={assignment.status}>{assignment.points}점</PointsText>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </AssignmentsTable>
            </ModalBody>
          </ModalContent>
        </StudentDetailModal>
      )}

      {/* 포인트 지급 모달 */}
      {showPointGrantModal && (
        <PointGrantModal>
          <PointGrantModalOverlay onClick={handleClosePointGrantModal} />
          <PointGrantModalContent>
            <ModalHeader>
              <h3>일괄 포인트 지급</h3>
              <CloseButton onClick={handleClosePointGrantModal}>×</CloseButton>
            </ModalHeader>
            
            {/* 포인트 타입 선택 */}
            <PointGrantFormGroup>
              <PointGrantLabel>포인트 타입별 세트</PointGrantLabel>
              <BulkPointTypeButton
                onClick={() => {
                  loadPointTypes();
                  loadTimeTags(); // 타임 태그도 함께 로드
                  setShowPointTypeSelectModal(true);
                }}
              >
                {bulkPointTypeId ? (
                  (() => {
                    if (bulkPointTypeId === 'positive-default') {
                      return (
                        <>
                          <GemIcon color="red" size={16} />
                          <span style={{ marginLeft: '8px' }}>긍정행동</span>
                        </>
                      );
                    } else if (bulkPointTypeId === 'negative-default') {
                      return (
                        <>
                          <GemIcon color="gray" size={16} />
                          <span style={{ marginLeft: '8px' }}>행동체크</span>
                        </>
                      );
                    } else {
                      const allTypes = [...availablePointTypes.positive, ...availablePointTypes.negative];
                      const selectedType = allTypes.find(type => type.id === bulkPointTypeId);
                      return selectedType ? (
                        <>
                          <GemIcon color={selectedType.category === 'positive' ? 'red' : 'gray'} size={16} />
                          <span style={{ marginLeft: '8px' }}>{selectedType.memo}</span>
                        </>
                      ) : '포인트 타입 및 세트 선택';
                    }
                  })()
                ) : (
                  '포인트 타입 및 세트 선택'
                )}
              </BulkPointTypeButton>
            </PointGrantFormGroup>


            {/* 지급 포인트 */}
            <PointGrantFormGroup>
              <PointGrantLabel>지급 포인트</PointGrantLabel>
              <PointInputWrapper>
                <PointMinusButton onClick={() => {
                  const current = parseInt(pointValue) || 0;
                  if (current > 0) {
                    setPointValue((current - 1).toString());
                  }
                }}>
                  −
                </PointMinusButton>
                <PointInput
                  type="number"
                  value={pointValue}
                  onChange={(e) => setPointValue(e.target.value)}
                  placeholder="포인트"
                  tabIndex={0}
                />
                <PointPlusButton onClick={() => {
                  const current = parseInt(pointValue) || 0;
                  setPointValue((current + 1).toString());
                }}>
                  +
                </PointPlusButton>
              </PointInputWrapper>
            </PointGrantFormGroup>

            {/* 포인트 메모 */}
            <PointGrantFormGroup>
              <PointGrantLabel>포인트 메모</PointGrantLabel>
              <PointGrantTextArea
                value={pointMemo}
                onChange={(e) => setPointMemo(e.target.value)}
                placeholder="포인트 지급 사유나 메모를 입력하세요"
              />
            </PointGrantFormGroup>

            <PointGrantModalFooter>
              <PointGrantCloseButton data-variant="modal-secondary" onClick={handleClosePointGrantModal} disabled={isGrantingPoints}>
                취소
              </PointGrantCloseButton>
              <PointGrantSaveButton onClick={handleGrantPointsToBlocks} disabled={isGrantingPoints}>
                블럭에 반영
              </PointGrantSaveButton>
              <PointGrantSaveButton onClick={handleGrantPoints} disabled={isGrantingPoints}>
                {isGrantingPoints ? '지급 중...' : '포인트 지급'}
              </PointGrantSaveButton>
            </PointGrantModalFooter>
          </PointGrantModalContent>
        </PointGrantModal>
      )}


      {/* 멤버 포인트 상세 모달 */}
      {showStudentPointDetailModal && user && currentClass && selectedStudentForDetail && (
        <StudentPointDetailModal
          isOpen={showStudentPointDetailModal}
          onClose={() => setShowStudentPointDetailModal(false)}
          student={selectedStudentForDetail}
          userId={user.id}
          classId={currentClass.id}
          memberGroup={currentMemberGroup}
          coinDefaultPoints={coinDefaultPoints}
          user={user}
          onLicenseClick={onLicenseClick}
          onDataChange={async () => {
            // 캐시 무효화 후 loadData만 호출 (setClassMembers 제거 → 중간에 미병합 데이터로 깜박임 방지)
            userDataService.invalidateMembersCache(user.id, currentClass.id, currentMemberGroup);
            await loadData();
          }}
        />
      )}

      {/* 프로필 이미지 선택 모달 */}
      {isProfileModalOpen && selectedStudentForProfile && (
        <ProfileImageSelectModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          onConfirm={handleProfileImageConfirm}
          currentImageId={selectedStudentForProfile.profileImage || 'basic-0'}
          unlockedProfiles={classMembers.find(m => m.id === selectedStudentForProfile.studentId)?.unlockedProfiles || []}
        />
      )}

      {/* 포인트 되돌리기 안내 모달 */}
      {showUndoModal && undoInfo && (() => {
        // pointData가 없으면 pointDataList에서 첫 번째 항목 사용
        const displayPointData = undoInfo.pointData || (undoInfo.pointDataList && undoInfo.studentIds.length > 0 
          ? undoInfo.pointDataList[undoInfo.studentIds[0]] 
          : null);
        
        if (!displayPointData) {
          // pointData도 pointDataList도 없으면 기본값 사용
          return null;
        }
        
        return (
        <>
          <UndoModalOverlay onClick={handleCloseUndoModal} />
          <UndoModalContainer>
            <UndoModalContent>
              <UndoIcon>✓</UndoIcon>
              <UndoTitle>포인트 지급 되돌리기 완료</UndoTitle>
              <UndoMessage>
                다음 포인트 지급이 취소되었습니다:
              </UndoMessage>
              <UndoDetails>
                <UndoDetailItem>
                  <UndoLabel>포인트 타입:</UndoLabel>
                  <UndoValue>{displayPointData.pointType || '일반'}</UndoValue>
                </UndoDetailItem>
                <UndoDetailItem>
                  <UndoLabel>포인트:</UndoLabel>
                  <UndoValue>{(displayPointData.pointValue || 0) > 0 ? '+' : ''}{displayPointData.pointValue || 0}점</UndoValue>
                </UndoDetailItem>
                {displayPointData.pointMemo && (
                  <UndoDetailItem>
                    <UndoLabel>메모:</UndoLabel>
                    <UndoValue>{displayPointData.pointMemo}</UndoValue>
                  </UndoDetailItem>
                )}
                <UndoDetailItem>
                  <UndoLabel>대상 멤버:</UndoLabel>
                  <UndoValue>{undoInfo.studentIds?.length || 0}명</UndoValue>
                </UndoDetailItem>
              </UndoDetails>
              <UndoStudentList>
                {(undoInfo.studentIds || []).map((studentId: string) => (
                  <UndoStudentItem key={studentId}>
                    {undoInfo.studentNames?.[studentId] || studentId}
                  </UndoStudentItem>
                ))}
              </UndoStudentList>
            </UndoModalContent>
            <UndoModalFooter>
              <UndoConfirmButton onClick={handleCloseUndoModal}>
                확인
              </UndoConfirmButton>
            </UndoModalFooter>
          </UndoModalContainer>
        </>
        );
      })()}

      {/* 일괄 코인 지급 모달 */}
      {showBulkCoinGrantModal && (
        <>
          <DeleteConfirmOverlay onClick={() => setShowBulkCoinGrantModal(false)} />
          <DeleteConfirmContainer onClick={(e) => e.stopPropagation()}>
            <CoinModalContent>
              <CoinModalTitle>일괄 코인 지급</CoinModalTitle>
              <CoinModalField>
                <CoinModalLabel>획득 코인</CoinModalLabel>
                <CoinAmountContainer>
                  <CoinButton 
                    onClick={() => {
                      // 항상 수를 줄임
                      if (bulkCoinAmount > -100) {
                        const newAmount = bulkCoinAmount - coinDefaultPoints;
                        setBulkCoinAmount(newAmount);
                        
                        // 0에서 음수로 가면 음수 모드로 전환
                        if (bulkCoinAmount === 0 && newAmount < 0) {
                          setIsBulkCoinNegativeMode(true);
                        }
                      }
                    }} 
                    type="button"
                  >
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </CoinButton>
                  <CoinAmountInput
                    type="text"
                    value={isBulkCoinNegativeMode && bulkCoinAmount === 0 ? '-0' : bulkCoinAmount.toString()}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value;
                      
                      // 음수 모드가 아닌 경우 숫자만 허용
                      if (!isBulkCoinNegativeMode) {
                        value = value.replace(/[^0-9]/g, '');
                      } else {
                        // 음수 모드인 경우 숫자와 마이너스 기호만 허용
                        value = value.replace(/[^0-9-]/g, '');
                        // 마이너스 기호는 맨 앞에만 허용
                        if (value.includes('-') && value.indexOf('-') !== 0) {
                          value = value.replace(/-/g, '');
                        }
                        // 마이너스 기호가 여러 개인 경우 첫 번째만 유지
                        if ((value.match(/-/g) || []).length > 1) {
                          value = '-' + value.replace(/-/g, '');
                        }
                        // 음수 모드인데 마이너스 기호가 없으면 자동으로 추가
                        if (value && !value.startsWith('-') && isBulkCoinNegativeMode) {
                          value = '-' + value;
                        }
                      }
                      
                      // 모든 수를 지우면 양수 모드로 되돌아가기
                      if (value === '') {
                        setIsBulkCoinNegativeMode(false);
                        setBulkCoinAmount(0);
                        return;
                      }
                      
                      // -0에서 0을 지우면 양수 모드로 되돌아가기
                      if (value === '-') {
                        setIsBulkCoinNegativeMode(false);
                        setBulkCoinAmount(0);
                        return;
                      }
                      
                      // -0 상태에서 숫자를 입력할 때 처리
                      if (value === '-0') {
                        setBulkCoinAmount(0);
                        return;
                      }
                      
                      const numValue = parseInt(value) || 0;
                      if (numValue >= -100 && numValue <= 100) {
                        setBulkCoinAmount(numValue);
                      }
                    }}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === '-') {
                        e.preventDefault();
                        if (isBulkCoinNegativeMode) {
                          // 음수 모드에서 양수 모드로 전환
                          setIsBulkCoinNegativeMode(false);
                          setBulkCoinAmount(0);
                        } else {
                          // 양수 모드에서 음수 모드로 전환
                          setIsBulkCoinNegativeMode(true);
                          setBulkCoinAmount(0); // -0으로 시작
                        }
                      }
                    }}
                    $isNegative={bulkCoinAmount < 0}
                  />
                  <CoinButton 
                    onClick={() => {
                      // 항상 수를 늘림
                      if (bulkCoinAmount < 100) {
                        const newAmount = bulkCoinAmount + coinDefaultPoints;
                        setBulkCoinAmount(newAmount);
                        
                        // 음수에서 양수로 가면 양수 모드로 전환
                        if (bulkCoinAmount < 0 && newAmount >= 0) {
                          setIsBulkCoinNegativeMode(false);
                        }
                      }
                    }} 
                    type="button"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </CoinButton>
                </CoinAmountContainer>
              </CoinModalField>
              
              <CoinModalField>
                <CoinModalLabel>메모</CoinModalLabel>
                <CoinModalInput
                  type="text"
                  placeholder="메모 입력"
                  value={bulkCoinMemo}
                  onChange={(e) => setBulkCoinMemo(e.target.value)}
                />
              </CoinModalField>
              
              <SelectedStudentsInfo>
                선택된 멤버: {selectedStudents.size}명
              </SelectedStudentsInfo>
            </CoinModalContent>
            <DeleteConfirmFooter>
              <CancelDeleteButton onClick={() => setShowBulkCoinGrantModal(false)}>
                취소
              </CancelDeleteButton>
              <ConfirmDeleteButton onClick={handleBulkCoinTableReflect}>
                테이블 반영
              </ConfirmDeleteButton>
              <ConfirmDeleteButton onClick={handleBulkCoinGrant}>
                코인 지급
              </ConfirmDeleteButton>
            </DeleteConfirmFooter>
          </DeleteConfirmContainer>
        </>
      )}

      {/* 레벨 설정 모달 */}
      {actualShowLevelSettingsModal && (() => {
        console.log('🔍 LevelSettingsModal 렌더링됨 - actualShowLevelSettingsModal:', actualShowLevelSettingsModal);
        console.log('🔍 LevelSettingsModal 데이터 확인 - user:', user?.name, 'currentClass:', currentClass?.name);
        return (
          <LevelSettingsModalOverlay onClick={handleCloseLevelSettingsModal}>
            <LevelSettingsModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>레벨 설정</h3>
              <CloseButton onClick={handleCloseLevelSettingsModal}>×</CloseButton>
            </ModalHeader>
            
            <LevelSettingsBody>
              <LevelSettingsFormGroup>
                <LevelSettingsLabel>레벨업 포인트</LevelSettingsLabel>
                <LevelPointInputWrapper>
                  <LevelPointMinusButton onClick={() => {
                    const current = levelUpPoints || 50;
                    if (current > 1) {
                      setLevelUpPoints(current - 1);
                    }
                  }}>
                    −
                  </LevelPointMinusButton>
                  <LevelPointInput
                    type="number"
                    value={levelUpPoints}
                    onChange={(e) => setLevelUpPoints(parseInt(e.target.value) || 50)}
                    min="1"
                    max="1000"
                    placeholder="포인트"
                  />
                  <LevelPointPlusButton onClick={() => {
                    const current = levelUpPoints || 50;
                    setLevelUpPoints(current + 1);
                  }}>
                    +
                  </LevelPointPlusButton>
                </LevelPointInputWrapper>
                <LevelSettingsDescription>
                  설정한 포인트 단위로 레벨이 올라갑니다. (예: 50포인트 설정 시 50점마다 레벨업)
                </LevelSettingsDescription>
              </LevelSettingsFormGroup>

              <LevelSettingsDivider />

              <LevelSettingsFormGroup>
                <LevelSettingsLabel>레벨 선택</LevelSettingsLabel>
                <LevelSelectContainer $level={selectedLevel || 1}>
                  <LevelSelectButtonRow>
                    <LevelSelectResetButton onClick={() => setSelectedLevel(1)}>
                      ㅁ
                    </LevelSelectResetButton>
                    <LevelSelectMinusButton onClick={() => {
                      const current = selectedLevel || 1;
                      if (current > 1) {
                        setSelectedLevel(current - 1);
                      }
                    }}>
                      −
                    </LevelSelectMinusButton>
                    <LevelSelectPlusButton onClick={() => {
                      const current = selectedLevel || 1;
                      setSelectedLevel(current + 1);
                    }}>
                      +
                    </LevelSelectPlusButton>
                  </LevelSelectButtonRow>
                  {isEditingLevel ? (
                    <LevelSelectInput
                      type="number"
                      min="1"
                      max="100"
                      placeholder="레벨 입력"
                      value={selectedLevel || ''}
                      onChange={(e) => setSelectedLevel(parseInt(e.target.value) || null)}
                      onBlur={() => setIsEditingLevel(false)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          setIsEditingLevel(false);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <LevelSelectDisplay
                      $level={selectedLevel || 1}
                      onClick={() => setIsEditingLevel(true)}
                    >
                      {selectedLevel || 1}
                    </LevelSelectDisplay>
                  )}
                </LevelSelectContainer>
              </LevelSettingsFormGroup>

              <LevelSettingsFormGroup>
                <LevelSettingsLabel>레벨별 획득 코인</LevelSettingsLabel>
                <LevelSettingsDescription>
                  각 레벨에 도달하면 자동으로 지급될 코인 금액을 설정합니다.
                </LevelSettingsDescription>

                {selectedLevel !== null && (
                  <LevelCoinSection $level={selectedLevel}>
                    <LevelCoinSectionTitle>레벨 {selectedLevel} 획득 코인</LevelCoinSectionTitle>
                    <LevelCoinInputWrapper>
                      <LevelCoinLabel>코인 금액:</LevelCoinLabel>
                      <LevelCoinInput
                        type="number"
                        min="0"
                        placeholder="0"
                        value={levelCoins[selectedLevel] || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = parseInt(e.target.value) || 0;
                          setLevelCoins({
                            ...levelCoins,
                            [selectedLevel]: value
                          });
                        }}
                      />
                      <LevelCoinIcon>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                          <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                          <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                        </svg>
                      </LevelCoinIcon>
                    </LevelCoinInputWrapper>
                  </LevelCoinSection>
                )}
              </LevelSettingsFormGroup>

              <LevelSettingsFormGroup>
                <LevelSettingsLabel>레벨별 획득 아이템 카드</LevelSettingsLabel>
                <LevelSettingsDescription>
                  각 레벨에 도달하면 자동으로 지급될 아이템 카드를 설정합니다.
                </LevelSettingsDescription>

                {selectedLevel !== null && (
                  <ItemCardSection $level={selectedLevel}>
                    <ItemCardSectionTitle>레벨 {selectedLevel} 획득 아이템</ItemCardSectionTitle>
                    
                    {availableItemCards.length > 0 ? (
                      <ItemCardSelectGrid>
                        {availableItemCards.map((item) => {
                          const isSelected = (levelItemCards[selectedLevel] || []).includes(item.itemCardId);
                          return (
                            <ItemCardSelectBlock
                              key={item.itemCardId}
                              $isSelected={isSelected}
                              onClick={() => {
                                const currentItems = levelItemCards[selectedLevel] || [];
                                if (isSelected) {
                                  // 제거
                                  setLevelItemCards({
                                    ...levelItemCards,
                                    [selectedLevel]: currentItems.filter(id => id !== item.itemCardId)
                                  });
                                } else {
                                  // 추가
                                  setLevelItemCards({
                                    ...levelItemCards,
                                    [selectedLevel]: [...currentItems, item.itemCardId]
                                  });
                                }
                              }}
                            >
                              <ItemCardIconArea>
                                {item.icon ? (
                                  <img src={item.icon} alt={item.name} />
                                ) : (
                                  <PlaceholderIcon>
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                                      <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                                    </svg>
                                  </PlaceholderIcon>
                                )}
                              </ItemCardIconArea>
                              <ItemCardName>{item.name}</ItemCardName>
                              <ItemCardMemo>{item.memo}</ItemCardMemo>
                              {((item.cost !== 'Nocharge') || (item.type === 'active' && item.useCount !== undefined)) && (
                                <ItemCardMetaGroup>
                                  {item.cost !== 'Nocharge' && (
                                    <ItemCardCost>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                      </svg>
                                      <span>X{item.cost}</span>
                                    </ItemCardCost>
                                  )}
                                  {item.type === 'active' && item.useCount !== undefined && (
                                    <ItemCardUseCount>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                      </svg>
                                      <span>X{item.useCount}</span>
                                    </ItemCardUseCount>
                                  )}
                                </ItemCardMetaGroup>
                              )}
                              {isSelected && (
                                <SelectedBadge>✓</SelectedBadge>
                              )}
                            </ItemCardSelectBlock>
                          );
                        })}
                      </ItemCardSelectGrid>
                    ) : (
                      <EmptyItemCardMessage>
                        레벨업 아이템이 없습니다. 아이템카드 설정에서 먼저 레벨업 아이템을 생성해주세요.
                      </EmptyItemCardMessage>
                    )}
                  </ItemCardSection>
                )}
              </LevelSettingsFormGroup>
            </LevelSettingsBody>
            
            <LevelSettingsModalFooter>
              <LevelSettingsCloseButton data-variant="modal-secondary" onClick={handleCloseLevelSettingsModal}>
                닫기
              </LevelSettingsCloseButton>
              <LevelSettingsSaveButton onClick={handleSaveLevelSettings}>
                저장하기
              </LevelSettingsSaveButton>
            </LevelSettingsModalFooter>
          </LevelSettingsModalContent>
        </LevelSettingsModalOverlay>
        );
      })()}
      
      {/* 달력 모달 */}
      {showCalendarModal && (
        <CalendarModalOverlay onClick={() => setShowCalendarModal(false)}>
          <CalendarModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>날짜 선택</h3>
              <CloseButton onClick={() => setShowCalendarModal(false)}>×</CloseButton>
            </ModalHeader>
            <CalendarComponent
              selectedDate={selectedDate}
              onDateSelect={(date) => {
                setSelectedDate(date);
                setShowCalendarModal(false);
              }}
            />
          </CalendarModalContent>
        </CalendarModalOverlay>
      )}

      {/* 타임 태그 선택 모달 */}
      <TimeTagSelectModal
        isOpen={showTimeTagSelectModal}
        onClose={() => setShowTimeTagSelectModal(false)}
        availableTimeTags={availableTimeTags}
        selectedTimeTag={selectedTimeTag}
        selectedDayOfWeek={selectedDayOfWeek}
        onTimeTagSelect={(timeTag) => {
          setSelectedTimeTag(timeTag);
          if (timeTag) {
            // 선택된 타임 태그의 dayOfWeek 배열에서 첫 번째 요일 또는 현재 선택된 요일 사용
            const days = Array.isArray(timeTag.dayOfWeek) ? timeTag.dayOfWeek : [timeTag.dayOfWeek];
            const dayToSelect = days.includes(selectedDayOfWeek as any) ? (selectedDayOfWeek as any) : (days[0] as any);
            setSelectedDayOfWeek(dayToSelect);
          }
          setShowTimeTagSelectModal(false);
        }}
        onDayOfWeekChange={setSelectedDayOfWeek}
      />

      {/* 멤버 태그 선택 모달 */}
      <StudentTagSelectModal
        key="point-input-modal"
        isOpen={showStudentTagModal}
        onClose={() => setShowStudentTagModal(false)}
        onConfirm={handleStudentTagConfirm}
        allStudents={tableData?.students.map((student, index) => ({
          id: student.studentId,
          name: student.studentName,
          number: (index + 1).toString(), // 문자열로 변환
          profileImage: student.profileImage || '',
          points: 0,
          level: 0,
          coins: 0,
          notes: '',
          accessCode: '', // 빈 문자열로 초기화
          createdAt: new Date(),
          updatedAt: new Date()
        })) || []}
        primaryStudentId={currentNoteStudentId}
        initialSelectedIds={currentNoteStudentId ? (studentPointInputs[currentNoteStudentId]?.relatedStudents || []).map(s => s.id) : []}
        currentClass={currentClass}
        user={user}
        currentGroup={currentMemberGroup}
        userId={user?.id}
        classId={currentClass?.id}
        forceLoadAllGroups={true}
      />

      {/* 일괄 노트 작성 모달 */}
      {showBulkNoteModal && (
        <BulkNoteModal>
          <BulkNoteModalOverlay onClick={handleCloseBulkNoteModal} />
          <BulkNoteModalContent>
            <ModalHeader>
              <h3>일괄 기록 작성</h3>
              <CloseButton onClick={handleCloseBulkNoteModal}>×</CloseButton>
            </ModalHeader>
            
            {/* 관련 멤버 선택 */}
            <BulkNoteFormGroup>
              <BulkNoteLabel>관련 멤버 선택</BulkNoteLabel>
              <BulkNoteStudentSelectButton onClick={handleOpenBulkNoteStudentSelectModal}>
                {bulkNoteRelatedStudents.length > 0 ? (
                  <div style={{ width: '100%' }}>
                    {/* 선택된 멤버들 그룹별로 표시 */}
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                      marginBottom: '8px',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {(() => {
                        // 그룹별로 묶어서 표시
                        const groupedByGroup = bulkNoteRelatedStudents.reduce((acc, student) => {
                          const groupName = student.groupName || '기타';
                          if (!acc[groupName]) {
                            acc[groupName] = [];
                          }
                          acc[groupName].push(student);
                          return acc;
                        }, {} as Record<string, typeof bulkNoteRelatedStudents>);

                        // 각 그룹 내에서 번호순으로 정렬
                        Object.keys(groupedByGroup).forEach(groupName => {
                          groupedByGroup[groupName].sort((a, b) => parseInt(a.number) - parseInt(b.number));
                        });

                        return Object.entries(groupedByGroup).map(([groupName, students]) => (
                          <div key={groupName} style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px',
                            alignItems: 'center',
                            marginBottom: '4px',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              color: 'var(--color-accent-text)',
                              fontSize: '12px',
                              fontWeight: '600',
                              whiteSpace: 'nowrap',
                              marginRight: '4px'
                            }}>
                              {groupName}:
                            </div>
                            {students.map(relatedStudent => (
                              <div
                                key={relatedStudent.id}
                                style={{
                                  padding: '4px 10px',
                                  backgroundColor: 'rgba(var(--color-accent-rgb), 0.1)',
                                  border: '1px solid rgba(var(--color-accent-rgb), 0.3)',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  color: 'var(--color-accent-text)',
                                  fontWeight: '500'
                                }}
                              >
                                {relatedStudent.number}번 {relatedStudent.name}
                              </div>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      관련 멤버 선택
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-gray)' }}>
                      클릭하여 관련 멤버를 선택하세요
                    </div>
                  </div>
                )}
              </BulkNoteStudentSelectButton>
            </BulkNoteFormGroup>

            {/* 기록 내용 */}
            <BulkNoteFormGroup>
              <BulkNoteLabel>기록 내용</BulkNoteLabel>
              <BulkNoteTextArea
                value={bulkNoteContent}
                onChange={(e) => setBulkNoteContent(e.target.value)}
                placeholder="멤버들에 대한 노트를 입력하세요"
              />
            </BulkNoteFormGroup>

            {/* 노트 타입 선택 */}
            <BulkNoteFormGroup>
              <BulkNoteLabel>노트 타입</BulkNoteLabel>
              <BulkNoteTypeButton onClick={() => {
                setBulkNoteType(prev => {
                  if (prev === '일반') return '학습';
                  if (prev === '학습') return '행동';
                  return '일반';
                });
              }}>
                {(() => {
                  const noteType = bulkNoteType;
                  const color = noteType === '일반' ? '#d3d3d3' :
                              noteType === '학습' ? '#4DC7AE' :
                              '#ff6b6b';
                  return (
                    <>
                      <span style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        marginRight: '8px'
                      }}></span>
                      {bulkNoteType}
                    </>
                  );
                })()}
              </BulkNoteTypeButton>
            </BulkNoteFormGroup>

            <BulkNoteSelectionInfo>
              선택된 멤버 {bulkNoteSelectedStudents.size}명
            </BulkNoteSelectionInfo>

            <BulkNoteModalFooter>
              <PointGrantCloseButton data-variant="modal-secondary" onClick={handleCloseBulkNoteModal}>
                취소
              </PointGrantCloseButton>
              <PointGrantSaveButton onClick={handleBulkNoteApplyToTable}>
                테이블 반영
              </PointGrantSaveButton>
              <PointGrantSaveButton onClick={handleBulkNoteSave}>
                기록 저장
              </PointGrantSaveButton>
            </BulkNoteModalFooter>
          </BulkNoteModalContent>
        </BulkNoteModal>
      )}

      {/* 일괄 노트용 관련 멤버 선택 모달 */}
      <StudentTagSelectModal
        isOpen={showBulkNoteStudentSelectModal}
        onClose={() => setShowBulkNoteStudentSelectModal(false)}
        onConfirm={handleBulkNoteStudentConfirm}
        allStudents={tableData?.students.map((student, index) => ({
          id: student.studentId,
          name: student.studentName,
          number: (index + 1).toString(),
          profileImage: student.profileImage || '',
          points: 0,
          level: 0,
          coins: 0,
          notes: '',
          accessCode: '',
          createdAt: new Date(),
          updatedAt: new Date()
        })) || []}
        primaryStudentId={''} // 관련 멤버 선택에서는 기본 선택 없음
        initialSelectedIds={bulkNoteRelatedStudents.map(s => s.id)}
        currentClass={currentClass}
        user={user}
        currentGroup={currentMemberGroup}
        userId={user?.id}
        classId={currentClass?.id}
        forceLoadAllGroups={true} // 모든 그룹의 멤버 로드
      />

      {/* 개별 기록 열람 비밀번호 모달 */}
      {showRecordViewPasswordModal && (
        <RecordViewPasswordOverlay onClick={() => setShowRecordViewPasswordModal(false)}>
          <RecordViewPasswordContent onClick={(e) => e.stopPropagation()}>
            <RecordViewPasswordTitle>개별 기록 열람</RecordViewPasswordTitle>
            <RecordViewPasswordDesc>기록 열람이 잠금 설정되어 있습니다. 비밀번호를 입력해 주세요.</RecordViewPasswordDesc>
            <RecordViewPasswordInput
              type="password"
              value={recordViewPasswordInput}
              onChange={(e) => { setRecordViewPasswordInput(e.target.value); setRecordViewPasswordError(''); }}
              placeholder="비밀번호"
              autoComplete="off"
              onKeyDown={(e) => e.key === 'Enter' && handleRecordViewPasswordSubmit()}
            />
            {recordViewPasswordError ? <RecordViewPasswordError>{recordViewPasswordError}</RecordViewPasswordError> : null}
            <RecordViewPasswordButtons>
              <RecordViewPasswordBtn type="button" onClick={() => setShowRecordViewPasswordModal(false)}>취소</RecordViewPasswordBtn>
              <RecordViewPasswordBtn $primary type="button" onClick={handleRecordViewPasswordSubmit}>확인</RecordViewPasswordBtn>
            </RecordViewPasswordButtons>
          </RecordViewPasswordContent>
        </RecordViewPasswordOverlay>
      )}

      {/* 개별 노트 관리 모달 */}
      {showNoteManagementModal && (() => {
        // 전체 노트 수 계산 (빈 노트 포함 - 생성 순서)
        // 삭제/추가 반영을 위해 mainNotes와 mainNoteCache만 사용 (originalMainNotes는 서버 원본이므로 제외)
        const totalNoteCount = Math.max(
          mainNotes.length,
          mainNoteCache.length,
          1 // 최소 1개
        );

        return (
          <>
            <NoteManagementModal onClick={handleRequestCloseNoteManagementModal}>
              <NoteManagementModalContent onClick={(e) => e.stopPropagation()}>
                <NoteManagementModalHeader>
                  <NoteManagementModalTitle>
                    {(() => {
                      const student = tableData?.students.find(s => s.studentId === currentNoteManagementStudentId);
                      return student ? `${student.studentNumber}번 ${student.studentName} 기록 관리` : '기록 관리';
                    })()}
                  </NoteManagementModalTitle>
                  <NoteManagementModalCloseButton
                    type="button"
                    onClick={handleRequestCloseNoteManagementModal}
                    aria-label="모달 닫기"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </NoteManagementModalCloseButton>
                </NoteManagementModalHeader>

            <NoteManagementModalColumns>
            {/* 개별 종합 기록 (좌측) */}
            <MainNoteSection>
            <SectionTitleRow>
              <MainNoteSectionTitle>개별 종합 기록</MainNoteSectionTitle>
              <SectionActionButtons>
                <MainNoteSectionCountButton
                  type="button"
                  onClick={handleDeleteMainNoteClick}
                  disabled={mainNoteCache.length <= 1}
                  title="종합 기록 삭제"
                >
                  −
                </MainNoteSectionCountButton>
                <MainNoteSectionCountButton type="button" onClick={handleAddMainNoteClick} title="종합 기록 추가">
                  +
                </MainNoteSectionCountButton>
                <SectionActionButton onClick={handleSaveMainNotesAsPdf} title="PDF 저장">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                </SectionActionButton>
                <SectionActionButton onClick={handlePrintMainNotes} title="프린트">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                  </svg>
                </SectionActionButton>
              </SectionActionButtons>
            </SectionTitleRow>

            <MainNoteTabButtonsRow>
              {Array.from({ length: totalNoteCount }, (_, i) =>
                editingMainNoteTitleIndex === i ? (
                  <MainNoteTitleTabInput
                    key={i}
                    type="text"
                    value={editingMainNoteTitleValue}
                    onChange={(e) => setEditingMainNoteTitleValue(e.target.value)}
                    onBlur={handleMainNoteTitleBlur}
                    onKeyDown={handleMainNoteTitleKeyDown}
                    autoFocus
                  />
                ) : (
                  <MainNoteTitleCategoryTabButton
                    key={i}
                    type="button"
                    $isActive={i === currentMainNoteIndex}
                    onClick={() => handleSelectMainNoteIndex(i)}
                    onDoubleClick={() => {
                      if (i !== currentMainNoteIndex) {
                        handleSelectMainNoteIndex(i);
                      }
                      handleMainNoteTitleDoubleClick(i);
                    }}
                    title="클릭: 선택 · 더블클릭: 제목 수정"
                  >
                    {mainNoteTitles[i] || `기록 ${i + 1}`}
                  </MainNoteTitleCategoryTabButton>
                )
              )}
            </MainNoteTabButtonsRow>

            <NoteManagementFormGroup $columnStretch>
              <NoteManagementSyncedBody>
                <MainNoteEditorBody>
                  <MainNoteSyncTextArea
                    value={(mainNotes[currentMainNoteIndex] !== undefined ? mainNotes[currentMainNoteIndex] : '') || ''}
                    onChange={(e) => handleMainNoteChange(e.target.value)}
                    placeholder="개별 종합 기록을 입력하세요"
                  />
                  <MainNoteSaveButtonContainer>
                    <MainNoteSectionSaveButton
                      onClick={handleSaveMainNote}
                      disabled={!hasUnsavedMainNoteChanges}
                      style={{ width: '100%' }}
                    >
                      저장하기
                    </MainNoteSectionSaveButton>
                  </MainNoteSaveButtonContainer>
                </MainNoteEditorBody>
              </NoteManagementSyncedBody>
            </NoteManagementFormGroup>
            </MainNoteSection>

            {/* 개별 누가기록 입력 */}
            <ObservationSection>
              <SectionTitleRow>
                <ObservationSectionTitle>개별 누가기록 입력</ObservationSectionTitle>
              </SectionTitleRow>
            <NoteManagementFormGroup
              $columnStretch
              data-observation-add-expanded={showAddObservationNote ? 'true' : 'false'}
            >
                  {!editingObservationNoteId ? (
                    <>
                      <ModalDateTimeButtonContainer>
                        <ModalButtonWrapper>
                          <ModalDateDisplay
                            selectedDate={modalSelectedDate}
                            onDateChange={(date) => setModalSelectedDate(date)}
                            onCalendarClick={() => setShowModalCalendar(true)}
                          />
                        </ModalButtonWrapper>
                        <ModalButtonWrapper>
                          <ModalTimeTagSelectButton onClick={() => setShowModalTimeTagSelect(true)}>
                            <TimeTagIcon>
                              {modalSelectedTimeTag
                                ? (modalSelectedDayOfWeek || (Array.isArray(modalSelectedTimeTag.dayOfWeek)
                                    ? modalSelectedTimeTag.dayOfWeek[0]
                                    : modalSelectedTimeTag.dayOfWeek))
                                : '#'}
                            </TimeTagIcon>
                            <TimeTagInfo>
                              {modalSelectedTimeTag ? (
                                <>
                                  <TimeTagName>
                                    {modalSelectedTimeTag.name}
                                  </TimeTagName>
                                  <TimeTagTime>{modalSelectedTimeTag.startTime} ~ {modalSelectedTimeTag.endTime}</TimeTagTime>
                                </>
                              ) : (
                                <>
                                  <TimeTagName>타임 태그</TimeTagName>
                                  <TimeTagTime>선택 안됨</TimeTagTime>
                                </>
                              )}
                            </TimeTagInfo>
                          </ModalTimeTagSelectButton>
                        </ModalButtonWrapper>
                      </ModalDateTimeButtonContainer>

                      <NoteManagementSyncedBody>
                      <MainNoteEditorBody>
                      <EtcNoteAddSection>
                        <ObservationTypePickerRow role="tablist" aria-label="누가기록 종류 선택">
                          {(['일반', '학습', '행동'] as const).map(tab => {
                            const typeColor = getObservationTabDotColor(tab);
                            return (
                              <ObservationTypePickerButton
                                key={tab}
                                type="button"
                                role="tab"
                                aria-selected={selectedObservationTab === tab}
                                $active={selectedObservationTab === tab}
                                onClick={() => {
                                  closeObservationEditSection();
                                  setSelectedObservationTab(tab);
                                }}
                              >
                                <ObservationTabDot $color={typeColor} />
                                {tab === '일반' ? '일반 기록' : tab === '학습' ? '학습 기록' : '행동 기록'}
                              </ObservationTypePickerButton>
                            );
                          })}
                        </ObservationTypePickerRow>
                        <div style={{ marginBottom: '12px', flexShrink: 0 }}>
                          <BulkNoteStudentSelectButton onClick={() => {
                            console.log('🎯 [PointsTablePage] 관련 멤버 선택 버튼 클릭됨');
                            setShowModalStudentTagSelect(true);
                          }}>
                            {modalRelatedStudents.length > 0 ? (
                              <div style={{ width: '100%' }}>
                                <div style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '4px',
                                  marginBottom: '8px',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}>
                                  {(() => {
                                    const groupedByGroup = modalRelatedStudents.reduce((acc, student) => {
                                      const groupName = student.groupName || '기타';
                                      if (!acc[groupName]) {
                                        acc[groupName] = [];
                                      }
                                      acc[groupName].push(student);
                                      return acc;
                                    }, {} as Record<string, typeof modalRelatedStudents>);

                                    Object.keys(groupedByGroup).forEach(groupName => {
                                      groupedByGroup[groupName].sort((a, b) => parseInt(a.number) - parseInt(b.number));
                                    });

                                    return Object.entries(groupedByGroup).map(([groupName, students]) => (
                                      <div key={groupName} style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        alignItems: 'center',
                                        marginBottom: '4px',
                                        justifyContent: 'center'
                                      }}>
                                        <div style={{
                                          color: 'var(--color-accent-text)',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          whiteSpace: 'nowrap',
                                          marginRight: '4px'
                                        }}>
                                          {groupName}:
                                        </div>
                                        {students.map(relatedStudent => (
                                          <div
                                            key={relatedStudent.id}
                                            style={{
                                              padding: '4px 10px',
                                              backgroundColor: 'rgba(var(--color-accent-rgb), 0.1)',
                                              border: '1px solid rgba(var(--color-accent-rgb), 0.3)',
                                              borderRadius: '4px',
                                              fontSize: '12px',
                                              color: 'var(--color-accent-text)',
                                              fontWeight: '500'
                                            }}
                                          >
                                            {relatedStudent.number}번 {relatedStudent.name}
                                          </div>
                                        ))}
                                      </div>
                                    ));
                                  })()}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                                  관련 멤버 선택
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--color-text-gray)' }}>
                                  클릭하여 관련 멤버를 선택하세요
                                </div>
                              </div>
                            )}
                          </BulkNoteStudentSelectButton>
                        </div>

                        <ObservationAddStretchTextArea
                          value={newObservationNoteContent}
                          onChange={(e) => setNewObservationNoteContent(e.target.value)}
                          placeholder="관찰 기록을 입력하세요"
                        />
                        <ObservationAddSaveFooter>
                          <ObservationSectionSaveButton
                            type="button"
                            onClick={() => void handleAddObservationNote()}
                            disabled={!observationAddSaveEnabled}
                            style={{ width: '100%' }}
                          >
                            저장하기
                          </ObservationSectionSaveButton>
                        </ObservationAddSaveFooter>
                      </EtcNoteAddSection>
                      </MainNoteEditorBody>
                      </NoteManagementSyncedBody>
                    </>
                  ) : (
                    <>
                      <ModalDateTimeButtonContainer>
                        <ModalButtonWrapper>
                          <ModalDateDisplay
                            selectedDate={editingObservationNoteDate}
                            onDateChange={(date) => setEditingObservationNoteDate(date)}
                            onCalendarClick={() => setShowModalCalendar(true)}
                          />
                        </ModalButtonWrapper>
                        <ModalButtonWrapper>
                          <ModalTimeTagSelectButton onClick={() => setShowModalTimeTagSelect(true)}>
                            <TimeTagIcon>
                              {editingObservationNoteTimeTag
                                ? (editingObservationNoteDayOfWeek ||
                                    (Array.isArray(editingObservationNoteTimeTag.dayOfWeek)
                                      ? editingObservationNoteTimeTag.dayOfWeek[0]
                                      : editingObservationNoteTimeTag.dayOfWeek))
                                : '#'}
                            </TimeTagIcon>
                            <TimeTagInfo>
                              {editingObservationNoteTimeTag ? (
                                <>
                                  <TimeTagName>{editingObservationNoteTimeTag.name}</TimeTagName>
                                  <TimeTagTime>
                                    {editingObservationNoteTimeTag.startTime} ~ {editingObservationNoteTimeTag.endTime}
                                  </TimeTagTime>
                                </>
                              ) : (
                                <>
                                  <TimeTagName>타임 태그</TimeTagName>
                                  <TimeTagTime>선택 안됨</TimeTagTime>
                                </>
                              )}
                            </TimeTagInfo>
                          </ModalTimeTagSelectButton>
                        </ModalButtonWrapper>
                      </ModalDateTimeButtonContainer>

                      <NoteManagementSyncedBody>
                        <MainNoteEditorBody>
                          <EtcNoteAddSection>
                            <ObservationTypePickerRow role="tablist" aria-label="누가기록 종류 선택">
                              {(['일반', '학습', '행동'] as const).map(tab => {
                                const typeColor = getObservationTabDotColor(tab);
                                return (
                                  <ObservationTypePickerButton
                                    key={tab}
                                    type="button"
                                    role="tab"
                                    aria-selected={selectedObservationTab === tab}
                                    $active={selectedObservationTab === tab}
                                    onClick={() => {
                                      setSelectedObservationTab(tab);
                                    }}
                                  >
                                    <ObservationTabDot $color={typeColor} />
                                    {tab === '일반' ? '일반 기록' : tab === '학습' ? '학습 기록' : '행동 기록'}
                                  </ObservationTypePickerButton>
                                );
                              })}
                            </ObservationTypePickerRow>
                            <div style={{ marginBottom: '12px', flexShrink: 0 }}>
                              <BulkNoteStudentSelectButton onClick={() => setShowModalStudentTagSelect(true)}>
                                {editingObservationNoteRelatedStudents.length > 0 ? (
                                  <div style={{ width: '100%' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        marginBottom: '8px',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                    >
                                      {(() => {
                                        const groupedByGroup = editingObservationNoteRelatedStudents.reduce(
                                          (acc, student) => {
                                            const groupName = student.groupName || '기타';
                                            if (!acc[groupName]) acc[groupName] = [];
                                            acc[groupName].push(student);
                                            return acc;
                                          },
                                          {} as Record<string, typeof editingObservationNoteRelatedStudents>
                                        );
                                        Object.keys(groupedByGroup).forEach(groupName => {
                                          groupedByGroup[groupName].sort((a, b) => parseInt(a.number) - parseInt(b.number));
                                        });
                                        return Object.entries(groupedByGroup).map(([groupName, students]) => (
                                          <React.Fragment key={groupName}>
                                            <div
                                              style={{
                                                color: 'var(--color-accent-text)',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                whiteSpace: 'nowrap',
                                                marginRight: '4px'
                                              }}
                                            >
                                              {groupName}:
                                            </div>
                                            {students.map(relatedStudent => (
                                              <div
                                                key={relatedStudent.id}
                                                style={{
                                                  padding: '4px 10px',
                                                  backgroundColor: 'rgba(var(--color-accent-rgb), 0.1)',
                                                  border: '1px solid rgba(var(--color-accent-rgb), 0.3)',
                                                  borderRadius: '4px',
                                                  fontSize: '12px',
                                                  color: 'var(--color-accent-text)',
                                                  fontWeight: '500'
                                                }}
                                              >
                                                {relatedStudent.number}번 {relatedStudent.name}
                                              </div>
                                            ))}
                                          </React.Fragment>
                                        ));
                                      })()}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                                      관련 멤버 선택
                                    </div>
                                    <div style={{ fontSize: '12px', color: 'var(--color-text-gray)' }}>
                                      클릭하여 관련 멤버를 선택하세요
                                    </div>
                                  </div>
                                )}
                              </BulkNoteStudentSelectButton>
                            </div>
                            <ObservationAddStretchTextArea
                              value={editingObservationNoteContent}
                              onChange={(e) => setEditingObservationNoteContent(e.target.value)}
                              placeholder="관찰 기록을 입력하세요"
                            />
                            <ObservationAddSaveFooter>
                              <EtcNoteAddButtons>
                                <PointGrantCloseButton
                                  data-variant="modal-secondary"
                                  type="button"
                                  onClick={closeObservationEditSection}
                                  style={{ flex: 1 }}
                                >
                                  닫기
                                </PointGrantCloseButton>
                                <PointGrantDeleteButton
                                  type="button"
                                  onClick={() => void handleDeleteObservationNote(editingObservationNoteId)}
                                  style={{ flex: 1 }}
                                >
                                  삭제
                                </PointGrantDeleteButton>
                                <ObservationSectionSaveButton
                                  type="button"
                                  onClick={() => void handleEditObservationNote(editingObservationNoteId)}
                                  disabled={!observationEditSaveEnabled}
                                  style={{ flex: 1 }}
                                >
                                  수정
                                </ObservationSectionSaveButton>
                              </EtcNoteAddButtons>
                            </ObservationAddSaveFooter>
                          </EtcNoteAddSection>
                        </MainNoteEditorBody>
                      </NoteManagementSyncedBody>
                    </>
                  )}
            </NoteManagementFormGroup>
            </ObservationSection>

            {/* 개별 누가기록 조회 */}
            <ObservationSection>
              <SectionTitleRow>
                <ObservationSectionTitle>개별 누가기록 조회</ObservationSectionTitle>
              <SectionActionButtons>
                <SectionActionButton onClick={() => {
                  setObservationFilterMode('save');
                  setShowObservationFilterModal(true);
                }} title="PDF 저장">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                </SectionActionButton>
                <SectionActionButton onClick={() => {
                  setObservationFilterMode('print');
                  setShowObservationFilterModal(true);
                }} title="프린트">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03-.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                  </svg>
                </SectionActionButton>
              </SectionActionButtons>
            </SectionTitleRow>
            <NoteManagementFormGroup $columnStretch>
              <ObservationTypePickerRow
                role="tablist"
                aria-label="조회할 기록 종류"
                style={{ marginBottom: '12px' }}
              >
                {(['일반', '학습', '행동'] as const).map(tab => {
                  const typeColor = getObservationTabDotColor(tab);
                  return (
                    <ObservationTypePickerButton
                      key={`view-${tab}`}
                      type="button"
                      role="tab"
                      aria-selected={observationListTypeFilter === tab}
                      $active={observationListTypeFilter === tab}
                      onClick={() => {
                        setCurrentObservationNotePage(1);
                        setObservationListTypeFilter(tab);
                      }}
                    >
                      <ObservationTabDot $color={typeColor} />
                      {tab === '일반' ? '일반 기록' : tab === '학습' ? '학습 기록' : '행동 기록'}
                    </ObservationTypePickerButton>
                  );
                })}
              </ObservationTypePickerRow>
              {(() => {
                const itemsPerPage = 5;
                const noteOrderMap = new Map(observationNotes.map((n, i) => [n.id, i]));
                const dateOnly = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
                const getTimeTagStartTime = (note: { timeTag: string }) => {
                  const parsed = parseTimeTagLabel(note.timeTag);
                  const tag = availableTimeTags.find((t: any) => t.name === parsed.name);
                  return tag?.startTime ?? '';
                };
                const filteredNotes = observationNotes
                  .filter(note => note.noteType === observationListTypeFilter)
                  .sort((a, b) => {
                    const dateCompare = dateOnly(b.recordedDate) - dateOnly(a.recordedDate);
                    if (dateCompare !== 0) return dateCompare;
                    const timeCompare = (getTimeTagStartTime(b) || '').localeCompare(getTimeTagStartTime(a) || '');
                    if (timeCompare !== 0) return timeCompare;
                    return (noteOrderMap.get(a.id) ?? 0) - (noteOrderMap.get(b.id) ?? 0);
                  });
                const totalPages = Math.max(1, Math.ceil(filteredNotes.length / itemsPerPage));
                const effectivePage = Math.min(currentObservationNotePage, totalPages);
                const startIndex = (effectivePage - 1) * itemsPerPage;
                const currentNotes = filteredNotes.slice(startIndex, startIndex + itemsPerPage);
                const navGloballyDisabled = totalPages <= 1;

                return (
                  <NoteManagementSyncedBody $overflowHidden>
                    <MainNoteEditorBody>
                      {observationNotes.length > 0 ? (
                        <EtcNoteList>
                          {Array.from({ length: itemsPerPage }, (_, slot) => {
                            const note = currentNotes[slot] ?? null;
                            if (!note) {
                              return (
                                <EtcNoteItem
                                  key={`obs-slot-${effectivePage}-${slot}`}
                                  $empty
                                  aria-hidden
                                />
                              );
                            }
                            const dateStr = note.recordedDate.toLocaleDateString('ko-KR');
                            const timeTagText = note.timeTag && note.timeTag !== '일반' ? note.timeTag : '';
                            const dateTimeText = timeTagText ? `${dateStr} | ${timeTagText}` : dateStr;
                            const relatedText = note.relatedStudents && note.relatedStudents.length > 0
                              ? ` | 관련멤버 ${note.relatedStudents.length}명`
                              : '';
                            const metaLine = `${dateTimeText}${relatedText}`;
                            return (
                              <EtcNoteItem key={note.id} $selected={note.id === editingObservationNoteId}>
                                <EtcNoteContent
                                  onClick={() => loadObservationNoteIntoEdit(note)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <ObservationNoteListMeta title={metaLine}>
                                    <ObservationTabDot $color={getObservationTabDotColor(note.noteType)} />
                                    <ObservationNoteListMetaText>{metaLine}</ObservationNoteListMetaText>
                                  </ObservationNoteListMeta>
                                  <ObservationNoteListText title={note.content}>
                                    <div style={OBSERVATION_NOTE_LIST_BODY_CLAMP_STYLE}>{note.content}</div>
                                  </ObservationNoteListText>
                                </EtcNoteContent>
                              </EtcNoteItem>
                            );
                          })}
                        </EtcNoteList>
                      ) : (
                        <div style={{ flex: 1, minHeight: 0 }} aria-hidden />
                      )}
                      <ObservationNotesListPaginationBar>
                        <EtcNotePagination>
                          <EtcNotePageButton
                            type="button"
                            onClick={() => setCurrentObservationNotePage(prev => Math.max(1, prev - 1))}
                            disabled={navGloballyDisabled || effectivePage <= 1}
                            title={navGloballyDisabled ? '페이지 없음' : '이전'}
                            aria-label="이전 페이지"
                          >
                            &lt;
                          </EtcNotePageButton>
                          <EtcNotePaginationInfo>
                            {effectivePage}/{totalPages}
                          </EtcNotePaginationInfo>
                          <EtcNotePageButton
                            type="button"
                            onClick={() => setCurrentObservationNotePage(prev => Math.min(totalPages, prev + 1))}
                            disabled={navGloballyDisabled || effectivePage >= totalPages}
                            title={navGloballyDisabled ? '페이지 없음' : '다음'}
                            aria-label="다음 페이지"
                          >
                            &gt;
                          </EtcNotePageButton>
                        </EtcNotePagination>
                      </ObservationNotesListPaginationBar>
                    </MainNoteEditorBody>
                  </NoteManagementSyncedBody>
                );
              })()}
            </NoteManagementFormGroup>
            </ObservationSection>
            </NoteManagementModalColumns>
          </NoteManagementModalContent>
        </NoteManagementModal>
        </>
        );
      })()}

      {/* 관찰 기록 필터 모달 */}
      {showObservationFilterModal && (
        <CalendarModalOverlay onClick={() => setShowObservationFilterModal(false)}>
          <CalendarModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <ModalHeader>
              <h3>{observationFilterMode === 'save' ? 'PDF 저장 옵션' : '인쇄 옵션'}</h3>
              <CloseButton onClick={() => setShowObservationFilterModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <div style={{ marginBottom: '32px' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: 'var(--color-text-primary)', textAlign: 'center' }}>
                  기록 종류 선택
                </div>
                <FilterOptionsRow>
                  {(['일반', '학습', '행동'] as Array<'일반' | '학습' | '행동'>).map(type => (
                    <FilterOptionButton
                      key={type}
                      $isSelected={selectedNoteTypes.includes(type)}
                      onClick={() => {
                        if (selectedNoteTypes.includes(type)) {
                          setSelectedNoteTypes(selectedNoteTypes.filter(t => t !== type));
                        } else {
                          setSelectedNoteTypes([...selectedNoteTypes, type]);
                        }
                      }}
                    >
                      <FilterCheckIcon $isSelected={selectedNoteTypes.includes(type)}>
                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 12l5 5l10 -10" />
                        </svg>
                      </FilterCheckIcon>
                      {type}
                    </FilterOptionButton>
                  ))}
                </FilterOptionsRow>
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: 'var(--color-text-primary)', textAlign: 'center' }}>
                  정렬 옵션
                </div>
                <FilterOptionsRow>
                  <FilterOptionButton
                    $isSelected={observationSortOrder === 'time'}
                    onClick={() => setObservationSortOrder('time')}
                  >
                    <FilterCheckIcon $isSelected={observationSortOrder === 'time'}>
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12l5 5l10 -10" />
                      </svg>
                    </FilterCheckIcon>
                    시간순 정렬
                  </FilterOptionButton>
                  <FilterOptionButton
                    $isSelected={observationSortOrder === 'type'}
                    onClick={() => setObservationSortOrder('type')}
                  >
                    <FilterCheckIcon $isSelected={observationSortOrder === 'type'}>
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12l5 5l10 -10" />
                      </svg>
                    </FilterCheckIcon>
                    종류별 정렬
                  </FilterOptionButton>
                </FilterOptionsRow>
              </div>
            </ModalBody>
            <div style={{ padding: '20px', borderTop: '1px solid var(--color-border)', display: 'flex', gap: '12px' }}>
              <PointGrantCloseButton
                data-variant="modal-secondary"
                onClick={() => setShowObservationFilterModal(false)}
                style={{ flex: 1 }}
              >
                취소
              </PointGrantCloseButton>
              <PointGrantSaveButton
                onClick={handleObservationFilterConfirm}
                style={{ flex: 1 }}
              >
                확인
              </PointGrantSaveButton>
            </div>
          </CalendarModalContent>
        </CalendarModalOverlay>
      )}

      {/* 모달용 달력 */}
      {showModalCalendar && (
        <CalendarModalOverlay onClick={() => setShowModalCalendar(false)}>
          <CalendarModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>날짜 선택</h3>
              <CloseButton onClick={() => setShowModalCalendar(false)}>×</CloseButton>
            </ModalHeader>
            <CalendarComponent
              selectedDate={editingObservationNoteId ? editingObservationNoteDate.toISOString().split('T')[0] : modalSelectedDate.toISOString().split('T')[0]}
              onDateSelect={(date) => {
                if (editingObservationNoteId) {
                  setEditingObservationNoteDate(new Date(date));
                } else {
                  setModalSelectedDate(new Date(date));
                }
                setShowModalCalendar(false);
              }}
            />
          </CalendarModalContent>
        </CalendarModalOverlay>
      )}

      {/* 모달용 타임태그 선택 */}
      {showModalTimeTagSelect && (
        <TimeTagSelectModal
          isOpen={showModalTimeTagSelect}
          onClose={() => setShowModalTimeTagSelect(false)}
          availableTimeTags={availableTimeTags}
          selectedTimeTag={editingObservationNoteId ? editingObservationNoteTimeTag : modalSelectedTimeTag}
        selectedDayOfWeek={editingObservationNoteId 
          ? editingObservationNoteDayOfWeek
          : modalSelectedDayOfWeek}
          onTimeTagSelect={(timeTag) => {
            if (editingObservationNoteId) {
              setEditingObservationNoteTimeTag(timeTag);
              if (timeTag) {
                const days = Array.isArray(timeTag.dayOfWeek) ? timeTag.dayOfWeek : [timeTag.dayOfWeek];
              const currentDay = editingObservationNoteDayOfWeek || '일반';
              const dayToSelect = days.includes(currentDay as any) ? currentDay : days[0];
              setEditingObservationNoteDayOfWeek(dayToSelect as string);
              }
            } else {
              setModalSelectedTimeTag(timeTag);
              if (timeTag) {
                const days = Array.isArray(timeTag.dayOfWeek) ? timeTag.dayOfWeek : [timeTag.dayOfWeek];
                const dayToSelect = days.includes(modalSelectedDayOfWeek as any) ? (modalSelectedDayOfWeek as any) : (days[0] as any);
                setModalSelectedDayOfWeek(dayToSelect);
              }
            }
            setShowModalTimeTagSelect(false);
          }}
          onDayOfWeekChange={(dayOfWeek) => {
            if (editingObservationNoteId) {
              setEditingObservationNoteDayOfWeek(dayOfWeek);
            } else {
              setModalSelectedDayOfWeek(dayOfWeek);
            }
          }}
        />
      )}

      {/* 모달용 관련 멤버 선택 */}
      {console.log('🔍 [PointsTablePage] showModalStudentTagSelect:', showModalStudentTagSelect)}
      <StudentTagSelectModal
        isOpen={showModalStudentTagSelect}
        onClose={() => setShowModalStudentTagSelect(false)}
        onConfirm={(selectedStudents) => {
          // 본인은 관련 멤버에 포함되지 않도록 제거
          const filteredStudents = selectedStudents.filter(student => student.id !== currentNoteManagementStudentId);

          // 수정 모드일 때는 수정용 상태 업데이트
          if (editingObservationNoteId) {
            setEditingObservationNoteRelatedStudents(filteredStudents);
          } else {
            // 일반 모드일 때는 기존 상태 업데이트
            setModalRelatedStudents(filteredStudents);
          }
          setShowModalStudentTagSelect(false);
        }}
        allStudents={tableData?.students.map((student, index) => ({
          id: student.studentId,
          name: student.studentName,
          number: (index + 1).toString(),
          profileImage: student.profileImage || '',
          points: 0,
          level: 0,
          coins: 0,
          notes: '',
          accessCode: '',
          createdAt: new Date(),
          updatedAt: new Date()
        })) || []}
        primaryStudentId={currentNoteManagementStudentId}
        initialSelectedIds={editingObservationNoteId ? editingObservationNoteRelatedStudents.map(s => s.id) : modalRelatedStudents.map(s => s.id)}
        currentClass={currentClass}
        user={user}
        userId={user?.id}
        classId={currentClass?.id}
        forceLoadAllGroups={true} // 모든 그룹 멤버 로드 강제
        currentGroup={currentMemberGroup}
      />

      {/* 메인 노트 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={showDeleteMainNoteConfirm}
        onClose={() => setShowDeleteMainNoteConfirm(false)}
        onConfirm={executeDeleteMainNote}
        title={[
          '개별 종합 기록을 삭제하시겠습니까?',
          '삭제된 기록은 복구할 수 없습니다.'
        ]}
        confirmText="삭제"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-exempted)"
      />

      <ConfirmModal
        isOpen={showNoteManagementCloseConfirm}
        onClose={handleDismissNoteManagementCloseConfirm}
        onCancel={handleCloseNoteManagementWithoutSave}
        onConfirm={() => {
          void handleSaveAndCloseNoteManagementModal();
        }}
        title={['변경된 데이터가 있습니다.', '저장하지 않고 닫으시겠습니까?']}
        cancelText="저장하지 않고 닫기"
        confirmText="저장하고 닫기"
        icon="!"
        iconBackground="var(--status-pending)"
      />

      {/* 메인 노트 제목 입력 모달 */}
      {showAddMainNoteTitleModal && (
        <MainNoteTitleModalOverlay onClick={() => setShowAddMainNoteTitleModal(false)}>
          <MainNoteTitleModalWrapper>
            <MainNoteTitleModalContent onClick={(e) => e.stopPropagation()}>
              <MainNoteTitleModalTitle>개별 종합 기록 추가</MainNoteTitleModalTitle>
              
              <MainNoteTitleInputContainer>
                <MainNoteTitleInputLabel>기록 제목을 입력하세요</MainNoteTitleInputLabel>
                <MainNoteTitleInputField
                  type="text"
                  value={newMainNoteTitle}
                  onChange={(e) => setNewMainNoteTitle(e.target.value)}
                  placeholder="예: 기록 1"
                  maxLength={50}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newMainNoteTitle.trim()) {
                      e.preventDefault();
                      executeAddMainNote();
                    } else if (e.key === 'Escape') {
                      setShowAddMainNoteTitleModal(false);
                      setNewMainNoteTitle('');
                    }
                  }}
                />
              </MainNoteTitleInputContainer>

              <MainNoteTitleButtonGroup>
                <MainNoteTitleCancelButton data-variant="modal-secondary" onClick={() => {
                  setShowAddMainNoteTitleModal(false);
                  setNewMainNoteTitle('');
                }}>
                  취소
                </MainNoteTitleCancelButton>
                <MainNoteTitleSaveButton onClick={executeAddMainNote} disabled={!newMainNoteTitle.trim()}>
                  저장
                </MainNoteTitleSaveButton>
              </MainNoteTitleButtonGroup>
            </MainNoteTitleModalContent>
          </MainNoteTitleModalWrapper>
        </MainNoteTitleModalOverlay>
      )}
      
      {/* 포인트 타입 선택 모달 */}
      {showPointTypeSelectModal && (
        <PointTypeSelectModalOverlay onClick={() => setShowPointTypeSelectModal(false)}>
          <PointTypeSelectModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>포인트 타입 및 세트 선택</h3>
              <ModalHeaderButtons>
                <RefreshButton
                  onClick={async (e) => {
                    e.stopPropagation();
                    // 포인트 타입 데이터 새로고침
                    await loadPointTypes();
                  }}
                  title="새로고침"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 3v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 21v-5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </RefreshButton>
                <EditButton
                  onClick={(e) => {
                    e.stopPropagation();
                    // 긍정행동 포인트 탭이 열리도록 먼저 탭 설정 (기본값)
                    setPointManagementModalInitialTab('positive');
                    // 포인트 타입 선택 모달이 열려있었다는 상태 저장 (닫지 않음)
                    setWasPointTypeSelectModalOpen(true);
                    // 모달 열기는 다음 이벤트 루프에서 실행하여 탭 설정이 먼저 반영되도록 함
                    setTimeout(() => {
                      actualSetShowPointManagementModal(true);
                    }, 0);
                  }}
                  title="포인트 타입별 세트 설정"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </EditButton>
                <CloseButton onClick={() => setShowPointTypeSelectModal(false)}>×</CloseButton>
              </ModalHeaderButtons>
            </ModalHeader>
            
            <PointTypeSelectBody>
              <PointTypeSection>
                <PointTypeSectionTitleContainer>
                  <PointTypeSectionTitle>긍정행동 포인트</PointTypeSectionTitle>
                </PointTypeSectionTitleContainer>
                
                {/* 긍정행동 기본 블럭 */}
                <PointTypeSelectItem
                  key="positive-default"
                  onClick={() => {
                    if (currentSelectingStudentId) {
                      // 개별 멤버용 처리 - 카테고리만 설정
                      setStudentPointInputs(prev => ({
                        ...prev,
                        [currentSelectingStudentId]: {
                          ...prev[currentSelectingStudentId],
                          pointTypeId: 'positive-default',
                          points: 0, // 사용자가 직접 입력
                          memo: '' // 사용자가 직접 입력
                        }
                      }));
                    } else {
                      // 일괄 포인트 지급용 처리 - 카테고리만 설정
                      setBulkPointTypeId('positive-default');
                      setPointValue('0'); // 사용자가 직접 입력
                      setPointMemo(''); // 사용자가 직접 입력
                    }
                    setShowPointTypeSelectModal(false);
                    setCurrentSelectingStudentId(null);
                  }}
                >
                  <GemIcon color="red" size={20} />
                  <PointTypeInfo>
                    <PointTypeMemo style={{ color: 'var(--color-text-muted)' }}>메모 직접 입력</PointTypeMemo>
                    <PointTypePoints style={{ color: 'var(--color-text-muted)' }}>포인트 직접 입력</PointTypePoints>
                  </PointTypeInfo>
                </PointTypeSelectItem>
                
                {availablePointTypes.positive.map(type => (
                  <PointTypeSelectItem
                    key={type.id}
                    onClick={() => {
                      if (currentSelectingStudentId) {
                        // 개별 멤버용 처리
                        setStudentPointInputs(prev => ({
                          ...prev,
                          [currentSelectingStudentId]: {
                            ...prev[currentSelectingStudentId],
                            pointTypeId: type.id,
                            points: type.points,
                            memo: type.memo
                          }
                        }));
                      } else {
                        // 일괄 포인트 지급용 처리
                        setBulkPointTypeId(type.id);
                        setPointValue(type.points.toString());
                        setPointMemo(type.memo);
                      }
                      setShowPointTypeSelectModal(false);
                      setCurrentSelectingStudentId(null);
                    }}
                  >
                    <GemIcon color="red" size={20} />
                    <PointTypeInfo>
                      <PointTypeMemo>{type.memo}</PointTypeMemo>
                      <PointTypePoints>{type.points}점</PointTypePoints>
                    </PointTypeInfo>
                  </PointTypeSelectItem>
                ))}
              </PointTypeSection>
              
              <PointTypeSection>
                <PointTypeSectionTitleContainer>
                  <PointTypeSectionTitle>행동체크 포인트</PointTypeSectionTitle>
                </PointTypeSectionTitleContainer>
                
                
                {/* 행동체크 기본 블럭 */}
                <PointTypeSelectItem
                  key="negative-default"
                  onClick={() => {
                    if (currentSelectingStudentId) {
                      // 개별 멤버용 처리 - 카테고리만 설정
                      setStudentPointInputs(prev => ({
                        ...prev,
                        [currentSelectingStudentId]: {
                          ...prev[currentSelectingStudentId],
                          pointTypeId: 'negative-default',
                          points: 0, // 사용자가 직접 입력
                          memo: '' // 사용자가 직접 입력
                        }
                      }));
                    } else {
                      // 일괄 포인트 지급용 처리 - 카테고리만 설정
                      setBulkPointTypeId('negative-default');
                      setPointValue('0'); // 사용자가 직접 입력
                      setPointMemo(''); // 사용자가 직접 입력
                    }
                    setShowPointTypeSelectModal(false);
                    setCurrentSelectingStudentId(null);
                  }}
                >
                  <GemIcon color="gray" size={20} />
                  <PointTypeInfo>
                    <PointTypeMemo style={{ color: 'var(--color-text-muted)' }}>메모 직접 입력</PointTypeMemo>
                    <PointTypePoints style={{ color: 'var(--color-text-muted)' }}>포인트 직접 입력</PointTypePoints>
                  </PointTypeInfo>
                </PointTypeSelectItem>
                
                {availablePointTypes.negative.map(type => (
                  <PointTypeSelectItem
                    key={type.id}
                    onClick={() => {
                      if (currentSelectingStudentId) {
                        // 개별 멤버용 처리
                        setStudentPointInputs(prev => ({
                          ...prev,
                          [currentSelectingStudentId]: {
                            ...prev[currentSelectingStudentId],
                            pointTypeId: type.id,
                            points: type.points,
                            memo: type.memo
                          }
                        }));
                      } else {
                        // 일괄 포인트 지급용 처리
                        setBulkPointTypeId(type.id);
                        setPointValue(type.points.toString());
                        setPointMemo(type.memo);
                      }
                      setShowPointTypeSelectModal(false);
                      setCurrentSelectingStudentId(null);
                    }}
                  >
                    <GemIcon color="gray" size={20} />
                    <PointTypeInfo>
                      <PointTypeMemo>{type.memo}</PointTypeMemo>
                      <PointTypePoints>{type.points}점</PointTypePoints>
                    </PointTypeInfo>
                  </PointTypeSelectItem>
                ))}
              </PointTypeSection>
            </PointTypeSelectBody>
          </PointTypeSelectModalContent>
        </PointTypeSelectModalOverlay>
      )}
      </Content>
      
      {/* 저장 호버 버튼 — 기록 관리 모달 열림 시 숨김(모달과 겹침 방지) */}
      {!showNoteManagementModal && (
        <SaveFloatingButton
          $isDisabled={!hasUnsavedChanges()}
          onClick={handleSaveAllChanges}
          tabIndex={-1}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </SaveFloatingButton>
      )}
      
      {/* 토스트 알림 */}
      {showToast && (
        <ToastOverlay>
          <ToastMessage onClick={(e) => e.stopPropagation()}>
            <ToastText dangerouslySetInnerHTML={{ __html: toastMessage }} />
            <ToastConfirmButton onClick={async () => {
              // 토스트를 먼저 닫기 (즉시 닫히도록)
              const changesToProcess = [...pendingLevelChanges];
              setPendingLevelChanges([]);
              setShowToast(false);
              
              // 확인 버튼 클릭 시 레벨 변화에 따른 보상 지급/회수 실행 (백그라운드에서 실행)
              if (changesToProcess.length > 0 && user && currentClass) {
                // 비동기로 실행 (토스트 닫힌 후 백그라운드에서 처리)
                (async () => {
                  try {
                    const { syncLevelUpRewardsByPoints } = await import('../utils/levelUpRewardHelper');
                    
                    console.log(`🚀 레벨 변화 보상 처리 시작: ${changesToProcess.length}명`, changesToProcess);
                    
                    // 각 학생에 대해 보상 지급/회수
                    for (const change of changesToProcess) {
                      console.log(`📊 처리 중: 학생 ${change.studentId}`, {
                        studentId: change.studentId,
                        oldTotalPoints: change.oldTotalPoints,
                        newTotalPoints: change.newTotalPoints,
                        oldLevel: change.oldLevel,
                        newLevel: change.newLevel,
                        levelUpPoints
                      });
                      
                      await syncLevelUpRewardsByPoints(
                        user.id,
                        currentClass.id,
                        change.studentId,
                        change.oldTotalPoints,
                        change.newTotalPoints,
                        levelUpPoints,
                        currentMemberGroup,
                        change.oldLevel,  // 이미 계산된 oldLevel 전달
                        change.newLevel   // 이미 계산된 newLevel 전달
                      );
                    }
                    
                    console.log(`✅ 레벨 변화 보상 처리 완료: ${changesToProcess.length}명`);

                    // 데이터 변경 후 캐시 무효화
                    try {
                      const { userDataService } = await import('../services/userDataService');
                      userDataService.invalidateMembersCache(user.id, currentClass.id, currentMemberGroup);
                      console.log('🗑️ 레벨 변화 후 멤버 캐시 무효화 완료');
                    } catch (error) {
                      console.warn('레벨 변화 후 캐시 무효화 실패 (무시):', error);
                    }

                    // 데이터 다시 로드하여 변경사항 반영 (강제 새로고침으로 레벨 즉시 반영)
                    if (loadDataRef.current) {
                      loadDataRef.current(true);
                    }
                  } catch (error) {
                    console.error('❌ 레벨 변화 보상 처리 실패:', error);
                    alert('레벨 변화 보상 처리 중 오류가 발생했습니다. 콘솔을 확인해주세요.');
                  }
                })();
              }
            }}>
              확인
            </ToastConfirmButton>
          </ToastMessage>
        </ToastOverlay>
      )}
      
      {/* 포인트 타입별 세트 설정 모달 */}
      {user && currentClass && (
        <PointManagementModal
          isOpen={actualShowPointManagementModal}
          onClose={async () => {
            actualSetShowPointManagementModal(false);
            // 포인트 타입 선택 모달이 열려있었으면 데이터를 다시 로드하여 변경사항 반영
            if (wasPointTypeSelectModalOpen) {
              // 모달이 닫힐 때 항상 데이터를 새로고침하여 최신 상태 유지
              try {
                await loadPointTypes();
                // 데이터 로드 완료 후 상태 초기화
                setWasPointTypeSelectModalOpen(false);
              } catch (error) {
                console.error('포인트 타입 로드 실패:', error);
                // 에러가 발생해도 상태는 초기화
                setWasPointTypeSelectModalOpen(false);
              }
            }
          }}
          userId={user.id}
          classId={currentClass.id}
          defaultPointValue={1}
          initialTab={pointManagementModalInitialTab}
          onDataChanged={async () => {
            // 포인트 타입 데이터가 변경되면 즉시 다시 로드하여 포인트 타입 선택 모달에 반영
            // 항상 최신 데이터를 로드하여 포인트 타입 선택 모달이 열려있을 때 즉시 반영되도록 함
            await loadPointTypes();
          }}
        />
      )}
    </Container>
  );
});

// Styled Components
const PointInputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  z-index: 1;
  pointer-events: auto;
`;

const Container = styled.div`
  min-height: 100vh;
  background-color: var(--color-bg);
  padding: 36px 0 20px 0; /* 상단 헤더, 하단 네비바 공간 확보 */
`;

const Content = styled.div`
  padding-top: 20px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 20px;
`;



const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--color-text-gray);
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Message = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--color-text-gray);
`;

const TableContainer = styled.div`
  background: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  overflow-x: auto;
  box-shadow: none;

  /* 가로 스크롤바를 아주 컴팩트하게 */
  scrollbar-width: thin; /* Firefox */
  :root[data-theme='light'] & {
    scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
  }
  :root:not([data-theme='light']) & {
    scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.16);
    border-radius: 999px;
  }

  :root:not([data-theme='light']) &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.22);
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.26);
  }

  :root:not([data-theme='light']) &:hover::-webkit-scrollbar-thumb {
    background-color: var(--color-block-border);
  }
`;

const StudentsGrid = styled.div<{ $alignment?: 'left' | 'center' }>`
  padding: 16px;
  margin-top: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: ${props => props.$alignment === 'center' ? 'center' : 'flex-start'};
  
  & > * {
    flex: 0 0 calc((100% - 32px - 64px) / 5);
    min-width: 0;
    box-sizing: border-box;
  }
  
  @media (max-width: 1500px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 48px) / 4);
    }
  }
  
  @media (max-width: 1220px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 32px) / 3);
    }
  }
  
  @media (max-width: 930px) {
    & > * {
      flex: 0 0 calc((100% - 32px - 16px) / 2);
    }
  }
  
  @media (max-width: 670px) {
    & > * {
      flex: 0 0 calc(100% - 32px);
    }
  }
`;

const StudentCard = styled.div<{ $isSelected?: boolean; $isSelectionMode?: boolean; $hasData?: boolean }>`
  background: ${props => 
    props.$hasData 
      ? 'var(--color-student-card-data-bg)' 
      : 'var(--color-surface)'};
  border: 2px solid ${props => {
    if (props.$hasData) {
      return 'var(--color-student-card-data-border)';
    }
    if (props.$isSelectionMode && props.$isSelected) {
      return 'var(--color-accent-strong)';
    }
    return 'var(--color-border)';
  }};
  
  /* 라이트 모드: 학생 블럭 테두리 조금 더 진한 회색 */
  :root[data-theme='light'] & {
    background: ${props =>
      props.$hasData
        ? 'var(--color-student-card-data-bg)'
        : 'rgba(255, 255, 255, 0.6)'};
    border-color: ${props => {
      if (props.$hasData) return 'var(--color-student-card-data-border)';
      if (props.$isSelectionMode && props.$isSelected) return 'var(--color-accent-strong)';
      return 'var(--color-block-border)';
    }};
  }
  
  /* 다크 모드: 학생 블럭 테두리 더 옅은 회색 */
  :root:not([data-theme='light']) & {
    border-color: ${props => {
      if (props.$hasData) return 'var(--color-student-card-data-border)';
      if (props.$isSelectionMode && props.$isSelected) return 'rgba(255, 255, 255, 0.8)';
      return 'var(--color-block-border)';
    }};
  }
  
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s;
  height: 120px;
  overflow: hidden;
  position: relative;
  isolation: isolate;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${props => {
      if (props.$isSelectionMode) {
        if (props.$isSelected) return 'var(--color-accent-strong)';
        return 'var(--color-border)';
      }
      if (props.$hasData) return 'var(--color-student-card-data-border)';
      return 'rgba(var(--color-accent-rgb), 0.8)';
    }};
  }
  
  /* 라이트 모드 호버: 테두리 메인테마 초록색 */
  :root[data-theme='light'] &:hover {
    border-color: ${props => {
      if (props.$isSelectionMode && !props.$isSelected) return 'var(--color-block-border)';
      if (props.$hasData) return 'var(--color-student-card-data-border)';
      return 'var(--color-accent-strong)';
    }};
  }
  
  /* 다크 모드 호버: 테두리 흰색 */
  :root:not([data-theme='light']) &:hover {
    border-color: ${props => {
      if (props.$isSelectionMode && !props.$isSelected) return 'var(--color-block-border)';
      if (props.$hasData) return 'var(--color-student-card-data-border)';
      return 'rgba(255, 255, 255, 0.8)';
    }};
  }
`;

/** 자리 모드 학생 블럭 - 번호 모드 StudentCard와 동일한 테두리/호버 */
const SeatModeStudentBlock = styled.div<{ $hasData?: boolean }>`
  background: ${props =>
    props.$hasData ? 'var(--color-student-card-data-bg)' : 'var(--color-surface)'};
  border: 2px solid ${props =>
    props.$hasData ? 'var(--color-student-card-data-border)' : 'var(--color-border)'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  :root[data-theme='light'] & {
    background: ${props =>
      props.$hasData ? 'var(--color-student-card-data-bg)' : 'rgba(255, 255, 255, 0.6)'};
    border-color: ${props =>
      props.$hasData ? 'var(--color-student-card-data-border)' : 'var(--color-block-border)'};
  }
  :root:not([data-theme='light']) & {
    border-color: ${props =>
      props.$hasData ? 'var(--color-student-card-data-border)' : 'var(--color-block-border)'};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  :root[data-theme='light'] &:hover {
    border-color: ${props =>
      props.$hasData ? 'var(--color-student-card-data-border)' : 'var(--color-accent-strong)'};
  }
  :root:not([data-theme='light']) &:hover {
    border-color: ${props =>
      props.$hasData ? 'var(--color-student-card-data-border)' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

const StudentCardLayout = styled.div<{ $isSelectionMode?: boolean; $displayMode?: 'points' | 'coins' | 'notes' }>`
  display: grid;
  grid-template-columns: ${props => {
    // 선택 모드: 기존 크기 유지(60px)
    // 데이터 조회 모드: 학생 블럭(120px) 패딩(8px*2) 고려해 높이 100px에 맞춰 정사각
    const avatarCol = props.$isSelectionMode ? '60px' : '100px';
    return `${avatarCol} 1fr auto`;
  }};
  grid-template-rows: ${props => props.$isSelectionMode ? 'auto auto' : '1fr'};
  gap: 6px;
  height: 100%;
`;

const ProfileImageArea = styled.div<{ $isSelectionMode?: boolean; $displayMode?: 'points' | 'coins' | 'notes' }>`
  width: ${props => props.$isSelectionMode ? '60px' : '100px'};
  min-width: ${props => props.$isSelectionMode ? '60px' : '100px'};
  max-width: ${props => props.$isSelectionMode ? '60px' : '100px'};
  height: ${props => props.$isSelectionMode ? '60px' : '100px'}; /* 블럭 높이(120) - 패딩(8*2) */
  min-height: ${props => props.$isSelectionMode ? '60px' : '100px'};
  max-height: ${props => props.$isSelectionMode ? '60px' : '100px'};
  aspect-ratio: 1; /* 항상 정사각 비율 유지 */
  background: transparent;
  border: 2px solid #000000;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  grid-row: ${props => props.$isSelectionMode ? '1 / 2' : '1 / 2'};
  grid-column: 1 / 2;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  z-index: 10; /* 모든 모드에서 높은 우선순위로 클릭 영역 분리 */
  pointer-events: auto; /* 모든 모드에서 명시적 클릭 이벤트 활성화 */

  &:hover {
    transform: scale(1.05);
  }
`;

const SelectionIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const PointTypeSelectButton = styled.button<{ disabled?: boolean }>`
  width: 60px;
  height: 26px;
  min-height: 26px;
  background: var(--color-student-block-element-bg);
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: none;
  flex-shrink: 0;
  overflow: hidden;
  
  /* 아이콘 크기 고정을 위한 스타일 */
  svg {
    flex-shrink: 0;
    width: 16px !important;
    height: 16px !important;
  }
  
  @media (max-width: 600px) {
    width: 56px;
    height: 24px;
    min-height: 24px;
    font-size: 11px;
    
    svg {
      width: 14px !important;
      height: 14px !important;
    }
  }
  
  &:hover {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
    ${props => !props.disabled && `
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    `}
  }

  &:active {
    ${props => !props.disabled && `
      transform: translateY(0);
      box-shadow: none;
    `}
  }
`;

const InfoArea = styled.div<{ $isSelectionMode?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  grid-row: 1 / 2;
  grid-column: 2 / 3;
  margin-bottom: ${props => props.$isSelectionMode ? '0' : '0'};
  height: 100%;
  min-height: 0;
`;


const BottomRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  grid-row: 2 / 3;
  grid-column: 1 / 3;
`;

const InfoTopRow = styled.div<{ $isSelectionMode?: boolean }>`
  display: flex;
  gap: 4px;
  align-items: baseline;
  justify-content: space-between; // 양 끝 정렬 (번호/이름 vs 레벨배지)
  margin-bottom: ${props => props.$isSelectionMode ? '0px' : '0px'};
`;

const StudentInfoGroup = styled.div`
  display: flex;
  gap: 0;
  align-items: baseline;
  flex: 1; // 남은 공간을 모두 차지
`;

const NoteMiddleName = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
`;

const NoteMiddleNameText = styled.div`
  font-size: 32px;
  font-weight: 900;
  color: var(--color-text-primary);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.05;
  width: 100%;
`;

const NoteCountBottomRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--color-text-gray);
  line-height: 1.3;
  white-space: nowrap;
`;

const NoteCountBadge = styled.div<{ $bg: string }>`
  background: ${props => props.$bg};
  color: #ffffff;
  border-radius: 7px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 900;
  min-width: 34px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`;

const StudentNumber = styled.span`
  font-size: 16px;
  color: var(--color-text-gray);
  font-weight: 700;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
`;

const StudentName = styled.span`
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  position: relative;
  z-index: 2;
`;

const LevelBadge = styled.div<{ $level: number }>`
  background: ${props => getLevelColor(props.$level)};
  color: ${props => getLevelTextColor(props.$level)};
  font-size: 14px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0; // InfoTopRow 내부에 있으므로 margin 제거
  min-width: 36px;
  height: 26px;
  text-align: center;
  line-height: 14px;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 2;
  flex-shrink: 0; // 레벨 배지가 줄어들지 않도록
`;

const TotalPointsDisplay = styled.div<{ $digitCount: number; $isSeatMode?: boolean }>`
  font-size: ${props => {
    if (props.$isSeatMode) {
      if (props.$digitCount <= 3) return '51px';
      if (props.$digitCount === 4) return '44px';
      if (props.$digitCount === 5) return '37px';
      return '31px';
    }
    if (props.$digitCount <= 3) return '60px';
    if (props.$digitCount === 4) return '52px';
    if (props.$digitCount === 5) return '46px';
    return '40px'; // 6자리 이상
  }};
  font-weight: bold;
  color: var(--color-accent-text);
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  line-height: 0.82;
  text-align: left;
  transition: font-size 0.2s ease;
  padding: 0;
  margin: 0;
  margin-top: 4px;
  margin-bottom: -12px;
  display: inline-block;
  vertical-align: top;
  overflow: hidden;
  height: 0.82em;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
`;

const CoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MultiplySign = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #f39c12;
  margin: 0;
`;

const CoinTotalDisplay = styled(TotalPointsDisplay)`
  color: #f39c12;
`;

const CoinCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  margin-bottom: 0px;
  color: #f39c12;
`;

const CompactCoinsText = styled.span`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
`;

const RightArea = styled.div<{ $isSelectionMode?: boolean }>`
  width: ${props => props.$isSelectionMode ? '36px' : '20px'}; // 포인트 지급 모드에서 더 넓게
  height: ${props => props.$isSelectionMode ? '100%' : '100px'}; // 데이터 조회 모드(포인트/코인/기록) 높이 고정
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  grid-row: ${props => props.$isSelectionMode ? '1 / 3' : '1 / 2'};
  grid-column: 3 / 4;
  background: transparent;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
`;

const LevelProgressBar = styled.div<{ $progress: number, $level: number }>`
  width: 12px;
  height: 100%;
  max-height: 100%;
  margin: 0;
  background: var(--color-border); /* 기본 배경색 (회색) */
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  z-index: 1;

  /* 진행도에 따른 채우기 */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${props => props.$progress}%;
    background: ${props => getLevelColor(props.$level)};
    border-radius: 6px;
    transition: height 0.3s ease;
  }
`;

const PointControlButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: space-between;
`;

const CenterButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: center;
`;

const MinusButtonBottom = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  min-width: 28px;
  background: var(--color-student-block-element-bg);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: none;
  
  &:hover {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  @media (max-width: 600px) {
    width: 28px;
    height: 26px;
    min-width: 28px;
    min-height: 26px;
  }
`;

const ClearButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  min-width: 28px;
  background: var(--color-student-block-element-bg);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: none;
  
  &:hover {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  @media (max-width: 600px) {
    width: 28px;
    height: 26px;
    min-width: 28px;
    min-height: 26px;
  }
`;

const PlusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  min-width: 28px;
  background: var(--color-student-block-element-bg);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: none;
  
  &:hover {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  @media (max-width: 600px) {
    width: 28px;
    height: 26px;
    min-width: 28px;
    min-height: 26px;
  }
`;

const PointValueInput = styled.input<{ $isCoinMode?: boolean; $isNegative?: boolean; $isZero?: boolean }>`
  text-align: left;
  font-size: 42px;
  font-weight: bold;
  font-family: 'Rajdhani', 'Orbitron', 'DS-Digital', 'LCD', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  color: ${props => {
    if (props.$isZero) {
      // 0일 때는 모든 모드에서 옅은 색으로 표시
      if (props.$isCoinMode) {
        return 'rgba(255, 165, 0, 0.3)'; // 코인 모드에서 0일 때 옅은 오렌지
      }
      return 'var(--color-student-points-zero)'; // 포인트 모드에서 0일 때
    }
    if (props.$isNegative && props.$isCoinMode) {
      return '#ff6b6b'; // 코인 모드에서 음수일 때 빨간색
    }
    return props.$isCoinMode ? '#ffa500' : 'var(--color-accent-text)'; // 코인 모드일 때 오렌지색, 아니면 accent-text 색상
  }};
  border: none;
  background: transparent;
  flex: 1;
  min-width: 0;
  padding: 0;
  line-height: 1;
  height: 1em;
  transition: color 0.2s ease;
  
  &::placeholder {
    color: ${props => props.$isCoinMode ? 'rgba(255, 165, 0, 0.3)' : 'rgba(var(--color-accent-rgb), 0.3)'}; // 코인 모드일 때 옅은 오렌지색
  }
  box-sizing: border-box;
  margin: 0;
  display: inline-block;
  vertical-align: top;
  overflow: hidden;
  
  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(var(--color-accent-rgb), 0.3);
  }
`;

const MemoInputField = styled.input`
  flex: 1;
  padding: 4px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 12px;
  height: 26px;
  box-sizing: border-box;
  min-width: 0;
  background: var(--color-student-block-element-bg);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

// Modal Styles
const StudentDetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalContent = styled.div`
  position: relative;
  background: var(--color-surface);
  border-radius: 12px;
  max-width: 900px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-large);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
  
  h3 {
    margin: 0;
    color: var(--color-accent-text);
  }
`;

const ModalHeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-gray);
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    color: var(--color-accent-strong);
    
    svg {
      animation: rotate 0.6s ease-in-out;
    }
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-gray);
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    color: var(--color-accent-strong);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-gray);
  
  &:hover {
    color: var(--color-text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
`;

const AssignmentsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff3cd;
`;

const TableHeader = styled.thead`
  background: var(--color-panel);
`;

const HeaderCell = styled.th<{ columnIndex?: number }>`
  padding: ${props =>
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? '8px 4px' : '8px 4px'};
  text-align: ${props => {
    // 타입열(0), 날짜열(1), 포인트열(3)은 가운데 정렬
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    // 메모열(2)은 왼쪽 정렬
    return 'left';
  }};
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-bottom: 2px solid var(--color-border);
  border-right: 1px solid var(--color-border);
  white-space: nowrap;
  
  /* 포인트 열(3) 너비 조정 */
  ${props => props.columnIndex === 3 && `
    width: 100px;
    min-width: 100px;
  `}

  &:last-child {
    border-right: none;
  }

  @media (max-width: 600px) {
    padding: 8px;
    font-size: 12px;
    
    /* 모바일에서 포인트 열 너비 조정 */
    ${props => props.columnIndex === 3 && `
      width: 80px;
      min-width: 80px;
    `}
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--color-border);
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-panel);
  }
`;

const TableCell = styled.td<{ columnIndex?: number }>`
  position: relative;
  padding: ${props =>
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? '8px 4px' : '8px 4px'};
  font-size: 14px;
  color: #212529;
  border-right: 1px solid var(--color-border);
  text-align: ${props => {
    // 타입열(0), 날짜열(1), 포인트열(3)은 가운데 정렬
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    // 메모열(2)은 왼쪽 정렬
    return 'left';
  }};
  vertical-align: middle;
  
  /* 포인트 열(3) 너비 조정 - 헤더와 동일하게 */
  ${props => props.columnIndex === 3 && `
    width: 100px;
    min-width: 100px;
  `}

  &:last-child {
    border-right: none;
  }

  @media (max-width: 600px) {
    padding: 8px;
    font-size: 12px;
    
    /* 모바일에서 포인트 열 너비 조정 */
    ${props => props.columnIndex === 3 && `
      width: 80px;
      min-width: 80px;
    `}
  }
`;

const TypeBadge = styled.span<{ type: string }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  color: white;
  
  ${props => props.type === 'general' ? `
    background: #6c757d; /* 일반과제 - 기본 회색 */
  ` : props.type === 'project' ? `
    background: var(--color-accent-strong); /* 프로젝트 - 메인 청록색 */
  ` : `
    background: #ff6b6b; /* 루틴과제 - 루틴과제 고유 카테고리 색상 */
  `}
`;

const PointsText = styled.span<{ points: number; status?: string }>`
  font-weight: bold;
  color: ${props => {
    if (props.status === 'pending') return 'var(--status-pending)'; // 미확인 - 핑크색 (이미지 1번)
    if (props.status === 'confirmed') return 'var(--color-accent-strong)'; // 확인 - 청록색 (이미지 2번)
    if (props.status === 'delayed') return '#A5D6A7'; // 지연확인 - 이미지 3번의 연한 녹색
    if (props.status === 'exempted') return '#FFD54F'; // 예외 - 이미지 4번의 연한 노랑/주황
    if (props.status === 'excluded') return '#6c757d'; // 제외 - 회색 (기존 유지)
    return props.points === 0 ? 'var(--status-pending)' : 'var(--color-accent-strong)'; // 기본값 (0점이면 미확인 색상, 아니면 확인 색상)
  }};
  font-size: 14px;
`;

// 탭 네비게이션 스타일
const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  padding: 0;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: ${props => props.active ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.active ? 'white' : 'var(--color-text-primary)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'rgba(var(--color-accent-rgb), 0.9)' : 'var(--color-panel)'};
  }
`;

// 개선된 종합 표시 스타일
const SummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--color-panel);
  border-radius: 12px;
  padding: 20px;
  margin: 0 0 20px 0;
  gap: 20px;
`;

const SummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  height: 20px; /* 간격 절반으로 줄임 */
`;

const SummaryType = styled.span`
  font-weight: 500;
  color: var(--color-text-primary);
  min-width: 80px;
  text-align: right;
`;

const SummaryCount = styled.span`
  color: var(--color-text-gray);
  min-width: 40px;
  text-align: right;
`;

const SummaryPoints = styled.span`
  font-weight: 600;
  color: var(--color-accent-text);
  min-width: 50px;
  text-align: right;
`;

const SummaryRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 62px; /* 3행의 총 높이 (20px * 3 + gap 1px * 2) */
`;

const SummaryTotal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SummaryTotalLabel = styled.span`
  font-size: 14px;
  color: var(--color-text-gray);
  font-weight: 500;
`;

const SummaryTotalValue = styled.span`
  font-size: 32px;
  font-weight: bold;
  color: var(--color-accent-text);
  line-height: 1;
`;

// 포인트 관리 컨트롤 스타일
// 포인트 지급 모달 스타일
const PointGrantModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PointGrantModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const PointGrantModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  z-index: 1001;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
`;

const PointGrantModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px 24px 20px;
  border-top: 1px solid var(--color-border);
`;

const PointGrantCloseButton = styled.button`
  padding: 10px 20px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 60px;
  min-height: 40px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &[data-variant="modal-secondary"] {
    background: transparent !important;
    color: var(--color-accent-text) !important;
    border: 1px solid var(--color-accent-strong) !important;
    
    &:hover:not(:disabled) {
      background: var(--color-accent-soft) !important;
      border-color: rgba(var(--color-accent-rgb), 0.8) !important;
    }
  }

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PointGrantDeleteButton = styled.button`
  padding: 10px 20px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-accent-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 60px;
  min-height: 40px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &:hover:not(:disabled) {
    background: var(--color-accent-soft);
    border-color: rgba(var(--color-accent-rgb), 0.8);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PointGrantSaveButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  min-width: 60px;
  min-height: 40px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }
`;

/** 개별 종합 기록·누가기록 입력 저장: 비활성 시 섹션 배경보다 한 톤 진한 회색(PointGrantSaveButton의 props.disabled 스타일 덮음) */
const noteSectionSaveDisabled = `
  &[disabled],
  &:disabled {
    background: #bdbdbd !important;
    color: #5c5c5c !important;
    cursor: not-allowed !important;
    transform: none !important;
    box-shadow: none !important;
    opacity: 1 !important;
  }
  :root:not([data-theme='light']) &[disabled],
  :root:not([data-theme='light']) &:disabled {
    background: #2a2a2a !important;
    color: #8a8a8a !important;
  }
`;

const MainNoteSectionSaveButton = styled(PointGrantSaveButton)`
  ${noteSectionSaveDisabled}
`;

const ObservationSectionSaveButton = styled(PointGrantSaveButton)`
  ${noteSectionSaveDisabled}
`;

// 일괄 노트 작성 모달 스타일
const BulkNoteModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BulkNoteModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const BulkNoteModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  z-index: 1001;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
`;

const RecordViewPasswordOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
`;
const RecordViewPasswordContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
`;
const RecordViewPasswordTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  color: var(--color-text-primary);
`;
const RecordViewPasswordDesc = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;
const RecordViewPasswordInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  color: var(--color-text-primary);
  background: var(--color-surface);
  box-sizing: border-box;
  margin-bottom: 10px;
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
`;
const RecordViewPasswordError = styled.p`
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #c0392b;
`;
const RecordViewPasswordButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;
const RecordViewPasswordBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 10px 20px;
  border: 2px solid var(--color-border);
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$primary ? 'white' : 'var(--color-text-primary)'};
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
`;

const BulkNoteModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px 24px 20px;
  border-top: 1px solid var(--color-border);
`;

const BulkNoteFormGroup = styled.div`
  margin-bottom: 20px;
`;

const BulkNoteLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  margin-top: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
  font-size: 14px;
`;

const BulkNoteTypeButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: rgba(var(--color-accent-rgb), 0.3);
    background: var(--color-panel);
  }

  &:active {
    background: rgba(var(--color-accent-rgb), 0.1);
  }
`;

const BulkNoteStudentSelectButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: rgba(var(--color-accent-rgb), 0.3);
    background: var(--color-panel);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const BulkNoteTextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  outline: none;
  background: transparent;
  color: var(--color-text-primary);
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const BulkNoteSelectionInfo = styled.div`
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 16px;
  text-align: center;
`;

// 개별 노트 관리 모달 — 넓은 화면: 과제 모달보다 약간 낮게(FAB 여유). 1100 이하: 1열·오버레이 전체 스크롤
const NoteManagementModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: max(16px, env(safe-area-inset-top, 0px));
  padding-bottom: 16px;
  padding-left: 16px;
  padding-right: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  box-sizing: border-box;

  /* 1열: 카드가 늘어나도 하단 저장 FAB·safe-area 위에 스크롤 여유 */
  @media (max-width: 1100px) {
    padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px) + 96px);
  }
`;

/**
 * 과제 관리 카드 모달(App → AssignmentCardPage embedded) Header 와 동일:
 * 부모에 패딩 없이 헤더가 카드 전체 너비, padding 16px 20px · min-height 52px · border-bottom
 */
const NoteManagementModalHeader = styled.div`
  /* 모달 본문(NoteManagementModalContent)과 동일 — --color-bg는 라이트에서 헤더만 살짝 회색으로 보임 */
  background: var(--color-surface);
  color: var(--color-text-primary);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: sticky;
  top: 0;
  left: auto;
  right: auto;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  z-index: 1100;
  height: auto;
  min-height: 52px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;

  /* 1100 이하: 오버레이 전체 스크롤 → sticky면 헤더가 뷰포트에 붙어 고정됨. 일반 흐름으로 스크롤 */
  @media (max-width: 1100px) {
    position: static;
    top: auto;
    z-index: auto;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }
`;

const NoteManagementModalTitle = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  flex: 1;
  min-width: 0;
  text-align: left;
`;

/* AssignmentCardPage ModalCloseButton 과 동일 */
const NoteManagementModalCloseButton = styled.button`
  width: 36px;
  height: 36px;
  margin-left: auto;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #f5f5f5;
    color: #6c757d;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  :root:not([data-theme='light']) & {
    color: #9ca3af;

    &:hover {
      background: var(--color-panel);
      color: var(--color-text-secondary);
    }
  }
`;

/* 과제 카드/테이블 모달(App inner box): 패딩 없음 — 헤더·본문이 카드 안에서 각각 여백 처리 */
const NoteManagementModalContent = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: min(1560px, calc(100vw - 24px));
  min-height: 0;
  /* 과제 관리 모달보다 약간 낮게: max 높이·하단 inset으로 저장 FAB과 겹침 완화 */
  height: min(860px, calc(100vh - max(32px, env(safe-area-inset-top, 0px)) - 76px));
  max-height: min(860px, calc(100vh - max(32px, env(safe-area-inset-top, 0px)) - 76px));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-radius: 16px;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-xlarge);
  padding: 0;
  overflow: hidden;

  @media (max-width: 1100px) {
    height: auto;
    max-height: none;
    min-height: 0;
    overflow: visible;
    flex-shrink: 0;
  }
`;

/** 개별 종합 기록 · 누가기록 입력 · 누가기록 조회 (3등분) — 본문 패딩(과제 카드 Content 20px에 맞춤) */
const NoteManagementModalColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  align-items: stretch;
  box-sizing: border-box;
  padding: 16px 24px 24px;
  margin: 0;
  min-width: 0;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    flex: 0 1 auto;
    min-height: auto;
    overflow-y: visible;
    overflow-x: hidden;
  }
`;

const MainNoteSection = styled.div`
  background: var(--color-section-bg);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  margin-bottom: 0;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 1100px) {
    flex: 0 0 auto;
    min-height: auto;
    overflow: visible;
  }
`;

/** 섹션 타이틀(좌) + PDF/프린트(우) 한 줄 — 노트 관리 모달 3열 타이틀 높이 통일 */
const SectionTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  margin-bottom: 12px;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const MainNoteSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  text-align: left;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const ObservationSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  text-align: left;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const ObservationSection = styled.div`
  background: var(--color-section-bg);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  margin-bottom: 0;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 1100px) {
    flex: 0 0 auto;
    min-height: auto;
    overflow: visible;
  }
`;

const SectionActionButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin: 0;
  flex-shrink: 0;
`;

const SectionActionButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: var(--color-text-secondary);
  }
`;

/** 개별 종합 기록 섹션 타이틀 줄의 − / + (PDF·프린트와 동일 36px 그리드) */
const MainNoteSectionCountButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    color: var(--color-text-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    background: var(--color-panel);
  }
`;

const FilterOptionButton = styled.button<{ $isSelected: boolean }>`
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  position: relative;
  flex: 1;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const FilterCheckIcon = styled.div<{ $isSelected: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'transparent'};
  border: ${props => props.$isSelected ? '2px solid var(--color-accent-strong)' : '2px solid var(--color-border)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 8px;
    height: 8px;
    stroke: white;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    opacity: ${props => props.$isSelected ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const FilterOptionsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  width: 100%;
`;

const MainNoteTitleInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 16px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  outline: none;
  font-family: inherit;
  min-height: 36px;
  height: 36px;
  box-sizing: border-box;
`;

const MainNoteTitleTabInput = styled(MainNoteTitleInput)`
  flex: 1 1 0;
  height: 48px;
  min-height: 48px;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  background: var(--color-date-block-bg);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  box-shadow: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: var(--color-accent-strong);
    background: rgba(var(--color-accent-rgb), 0.1);
  }

  :root:not([data-theme='light']) &:hover {
    border-color: #505050;
    background: rgba(128, 128, 128, 0.2);
  }
`;

const MainNoteTabButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;
  width: 100%;
  min-height: 48px;
  margin-bottom: 12px;
  box-sizing: border-box;
`;

/** 모달 타임블럭(ModalTimeTagSelectButton)과 동일 높이·배경·테두리 톤 */
const MainNoteTitleCategoryTabButton = styled.button<{ $isActive: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 12px;
  height: 48px;
  min-height: 48px;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  color: ${props => (props.$isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)')};
  cursor: pointer;
  border-radius: 8px;
  border: 2px solid
    ${props => (props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  background: var(--color-date-block-bg);
  box-shadow: none;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    border-color: var(--color-accent-strong);
    background: ${props =>
      props.$isActive
        ? 'rgba(var(--color-accent-rgb), 0.12)'
        : 'rgba(var(--color-accent-rgb), 0.1)'};
    color: var(--color-text-primary);
  }

  :root:not([data-theme='light']) &:hover {
    border-color: #505050;
    background: rgba(128, 128, 128, 0.2);
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  @media (max-width: 420px) {
    font-size: clamp(12px, 3vw, 16px);
  }
`;

const NoteManagementFormGroup = styled.div<{ $columnStretch?: boolean }>`
  margin-bottom: 0;
  border-top: none;
  padding-top: 0;

  ${props =>
    props.$columnStretch
      ? `
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `
      : ''}

  @media (max-width: 1100px) {
    flex: 0 1 auto !important;
    min-height: auto !important;
  }
`;

/** 종합/입력/조회 열 — 컨트롤 스트립 아래 본문 시작선 통일 */
const NoteManagementSyncedBody = styled.div<{ $overflowHidden?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--color-border);
  padding-top: 16px;
  overflow: ${props => (props.$overflowHidden ? 'hidden' : 'auto')};
  box-sizing: border-box;

  @media (max-width: 1100px) {
    flex: 0 1 auto;
    min-height: auto;
    overflow: ${props => (props.$overflowHidden ? 'hidden' : 'visible')};
  }
`;

/** 누가기록 입력: 타입 선택 (캡슐형 버튼 3개) */
const ObservationTypePickerRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

/* 종합 기록 탭(MainNoteTitleCategoryTabButton)과 동일: 배경 고정·선택 시 테두리만 강조 */
const ObservationTypePickerButton = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px solid ${props => (props.$active ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  background: var(--color-date-block-bg);
  color: ${props => (props.$active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)')};
  box-shadow: none;

  &:hover {
    border-color: var(--color-accent-strong);
    background: ${props =>
      props.$active ? 'rgba(var(--color-accent-rgb), 0.12)' : 'rgba(var(--color-accent-rgb), 0.1)'};
    color: var(--color-text-primary);
  }

  :root:not([data-theme='light']) &:hover {
    border-color: #505050;
    background: rgba(128, 128, 128, 0.2);
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  @media (max-width: 420px) {
    font-size: 12px;
    padding: 9px 8px;
  }
`;

const ObservationTabDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
  flex-shrink: 0;
`;

const ModalDateTimeButtonContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  margin-bottom: 12px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const ModalButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: stretch;

  & > * {
    flex: 1;
    min-width: 0;
  }
`;

// 모달 전용 날짜 표시 컨테이너
const ModalDateDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-date-block-bg);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: border-color 0.2s ease, background-color 0.2s ease;
  height: 48px;
  box-sizing: border-box;
  flex-shrink: 0;
  
  /* 다크모드: 호버 시 테두리를 회색으로 (내부 아이콘과 텍스트는 별도로 처리됨) */
  :root:not([data-theme='light']) &:hover {
    border-color: #505050;
  }
`;

// 모달 전용 타임태그 선택 버튼
const ModalTimeTagSelectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--color-date-block-bg);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  width: 100%;
  justify-content: flex-start;
  box-shadow: none;
  height: 48px;
  box-sizing: border-box;
  flex-shrink: 0;

  &:hover {
    border-color: var(--color-accent-strong);
    background: ${props => {
      if (props.disabled) {
        return 'transparent';
      }
      return 'rgba(var(--color-accent-rgb), 0.1)';
    }};
  }
  
  /* 다크모드: 호버 시 테두리와 배경을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    border-color: #505050;
    background: ${props => {
      if (props.disabled) {
        return 'transparent';
      }
      return 'rgba(128, 128, 128, 0.2)';
    }};
  }
`;

const NoteManagementLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  margin-top: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
  font-size: 14px;
`;

const NoteManagementTextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

/** 기록 관리 모달: 입력칸이 남는 세로 공간을 채우고 하단에 저장 버튼 고정 */
const MainNoteEditorBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

const MainNoteSyncTextArea = styled(NoteManagementTextArea)`
  flex: 1 1 auto;
  min-height: 120px;
  resize: none;
  overflow-y: auto;
`;

const MainNoteSaveButtonContainer = styled.div`
  margin-top: auto;
  padding-top: 12px;
  flex-shrink: 0;
  width: 100%;
`;

/* 점선 프레임 제거 후: 패딩 없이 부모 섹션 너비·남는 높이를 입력/버튼이 사용 */
const EtcNoteAddSection = styled.div`
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  align-self: stretch;
`;

const EtcNoteAddTextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  margin-bottom: 12px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ObservationAddStretchTextArea = styled(EtcNoteAddTextArea)`
  flex: 1 1 auto;
  min-height: 100px;
  margin-bottom: 0;
  resize: none;
  overflow-y: auto;
`;

const ObservationAddSaveFooter = styled.div`
  margin-top: auto;
  padding-top: 12px;
  flex-shrink: 0;
  width: 100%;
`;

/** 누가기록 조회: 저장하기 풋터와 동일 padding-top(12px) + 컨트롤 높이(40px) */
const ObservationNotesListPaginationBar = styled.div`
  margin-top: auto;
  padding-top: 12px;
  flex-shrink: 0;
  width: 100%;
  border-top: 1px solid var(--color-border);
  box-sizing: border-box;
`;

const EtcNoteAddButtons = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

/**
 * 누가기록 조회 목록: 5행 동일 높이.
 * 본문은 3줄(line-clamp 3) + 메타 1줄 + 패딩(10×2)에 맞춘 최소 행 높이(96px) — 잘리지 않고 … 로 끝남.
 * 데스크톱: minmax(96px, 1fr)로 남는 공간 균등 분배, 1100px 이하: 행 고정 96px.
 */
const EtcNoteList = styled.div`
  margin-top: 0;
  flex: 1;
  min-height: 0;
  align-self: stretch;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: repeat(5, minmax(96px, 1fr));
  gap: 8px;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    flex: 0 0 auto;
    align-self: stretch;
    height: calc(5 * 96px + 32px);
    min-height: calc(5 * 96px + 32px);
    max-height: calc(5 * 96px + 32px);
    grid-template-rows: repeat(5, 96px);
  }
`;

const EtcNoteItem = styled.div<{ $selected?: boolean; $empty?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  align-items: stretch;
  justify-content: flex-start;
  background: ${props => (props.$empty ? 'transparent' : 'var(--color-surface)')};
  border: ${props => (props.$empty ? 'none' : '1px solid')};
  border-color: ${props =>
    props.$empty
      ? 'transparent'
      : props.$selected
        ? 'var(--color-accent-strong)'
        : 'var(--color-border)'};
  border-radius: 8px;
  padding: ${props => (props.$empty ? '0' : '10px 12px')};
  margin-bottom: 0;
  box-shadow: ${props =>
    props.$empty
      ? 'none'
      : props.$selected
        ? '0 2px 8px rgba(var(--color-accent-rgb), 0.25)'
        : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  transition: all 0.2s ease;
  position: relative;
  pointer-events: ${props => (props.$empty ? 'none' : 'auto')};

  &:hover {
    box-shadow: ${props =>
      props.$empty ? 'none' : '0 4px 8px rgba(0, 0, 0, 0.15)'};
    border-color: ${props =>
      props.$empty
        ? 'transparent'
        : props.$selected
          ? 'var(--color-accent-strong)'
          : 'rgba(var(--color-accent-rgb), 0.3)'};
  }
`;

const EtcNoteContent = styled.div`
  font-size: 13px;
  color: var(--color-text-primary);
  line-height: 1.38;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  text-align: left;
  padding-right: 0;
  overflow: hidden;
  cursor: inherit;
`;

const ObservationNoteListMeta = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: var(--color-text-gray);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

const ObservationNoteListMetaText = styled.span`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** flex 자식 밖에 줄 제한·말줄임을 두기 위한 래퍼 */
const ObservationNoteListText = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;
`;

/**
 * 누가기록 조회 본문: 최대 3줄 + 말줄임(…). 행 높이(96px)와 맞춤.
 * 인라인 style로 -webkit-box-orient 유지.
 */
const OBSERVATION_NOTE_LIST_BODY_CLAMP_STYLE: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: '100%',
  minHeight: 0,
  margin: 0,
  padding: 0,
  fontSize: '13px',
  lineHeight: 1.3,
  color: 'var(--color-text-primary)',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineClamp: 3,
} as React.CSSProperties;

/* 관찰 기록 페이지네이션 — 컨트롤 줄 높이 = PointGrantSaveButton min-height(40px) */
const EtcNotePagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0;
  padding: 0;
  min-height: 40px;
  box-sizing: border-box;
`;

const EtcNotePageButton = styled.button<{ disabled?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const EtcNotePaginationInfo = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  box-sizing: border-box;
`;

const PointGrantFormGroup = styled.div`
  margin-bottom: 20px;
`;

const PointGrantLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  margin-top: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
  font-size: 14px;
`;

const PointGrantTextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  resize: none;
  height: 40px;
  transition: border-color 0.2s ease;
  overflow-y: auto;
  text-align: center;
  background: transparent;
  color: var(--color-text-primary);

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.25);
  }
`;

const BulkPointTypeButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

// 코인 관련 스타일 (중복 제거됨)


const SelectedStudentsInfo = styled.div`
  margin-top: 20px;
  padding: 12px;
  background: var(--color-panel);
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  color: var(--color-text-secondary);
`;


const PointInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PointMinusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const PointPlusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const PointInput = styled.input`
  padding: 8px 4px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-weight: normal;
  flex: 1;
  min-width: 0;
  text-align: center;
  height: 38px;
  line-height: 38px;
  box-sizing: border-box;
  transition: all 0.15s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  /* 숫자 입력 화살표 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

// 포인트 타입 선택 모달 스타일
const PointTypeSelectModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const PointTypeSelectModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 70vh;
  overflow-y: auto;
  position: relative;
  z-index: 2001;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
`;

const TimeTagName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 420px) {
    font-size: clamp(12px, 3vw, 16px);
  }
`;

const TimeTagTime = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-gray);
  margin: 0;
  padding: 0;
  line-height: 1;
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 14px);
  }
`;

// 1구역: 날짜 버튼, 타임 태그 버튼 컨테이너
const LeftSectionContainer = styled.div`
  display: flex;
  align-items: stretch;
  height: 100%;
  width: 100%;
  grid-column: 1; /* 1구역 */
  gap: 8px;
  justify-content: flex-start;
  padding-left: 0;
  
  @media (max-width: 800px) {
    height: auto;
    justify-content: center;
    width: 100%;
    grid-column: auto;
  }
`;

// 2구역: 포인트 지급 모드 버튼 컨테이너
const CenterSectionContainer = styled.div`
  display: flex;
  align-items: stretch;
  height: 100%;
  width: 100%;
  grid-column: 2; /* 2구역 */
  justify-content: center;
  
  @media (max-width: 800px) {
    height: auto;
    justify-content: center;
    width: 100%;
    grid-column: auto;
  }
`;

// 커스텀 날짜 표시 컨테이너
const DateDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 160px;
  padding: 8px 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  height: 48px;
  box-sizing: border-box;
  flex-shrink: 0;
  
  @media (max-width: 800px) {
    flex: 1;
    width: auto;
    min-width: 0;
  }
  
  @media (max-width: 420px) {
    padding: 4px 6px;
    gap: 3px;
    border-radius: 6px;
  }
  
  @media (max-width: 360px) {
    padding: 2px 4px;
    gap: 2px;
    font-size: clamp(8px, 2vw, 14px);
  }

  &:hover {
    border-color: rgba(var(--color-accent-rgb), 0.3);
  }
`;

// 날짜 표시 텍스트
const DateDisplayText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  font-size: 12px;
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 12px);
  }
`;

// 년도 행
const DateYearRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  padding: 0;
  margin: 0;
  line-height: 1;
  
  @media (max-width: 420px) {
    font-size: clamp(12px, 3vw, 16px);
  }
`;

// 월일 행
const DateMonthDayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-gray);
  padding: 0;
  margin: 0;
  line-height: 1;
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 14px);
    gap: 2px;
  }
`;

// 날짜 부분 (년, 월, 일)
const DatePart = styled.span`
  cursor: pointer;
  padding: 2px 2px;
  border-radius: 4px;
  transition: all 0.2s ease;
  user-select: none;
  font-size: 14px;

  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    color: #10504A;
  }

  &:active {
    background-color: rgba(var(--color-accent-rgb), 0.2);
  }
  
  /* 다크모드: 호버 시 색상을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background-color: rgba(128, 128, 128, 0.2);
    color: #9a9a9a;
  }
  
  :root:not([data-theme='light']) &:active {
    background-color: rgba(128, 128, 128, 0.3);
  }
  
  @media (max-width: 420px) {
    font-size: clamp(10px, 2.5vw, 14px);
    padding: 1px 1px;
  }
`;

// 인라인 입력 필드
const DateInput = styled.input<{ $fieldType?: 'year' | 'month' | 'day' }>`
  border: 2px solid #10504A;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  background: var(--color-surface);
  outline: none;
  width: ${props => props.$fieldType === 'year' ? '60px' : '32px'};
  text-align: center;

  &:focus {
    border-color: #0D4039;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.2);
  }
`;

// 달력 아이콘
const CalendarIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;

  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    color: #10504A;
  }

  &:active {
    background-color: rgba(var(--color-accent-rgb), 0.2);
  }
  
  /* 다크모드: 호버 시 색상을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background-color: rgba(128, 128, 128, 0.2);
    color: #9a9a9a;
  }
  
  :root:not([data-theme='light']) &:active {
    background-color: rgba(128, 128, 128, 0.3);
  }

  svg {
    width: 32px;
    height: 32px;
  }
  
  @media (max-width: 360px) {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

// 달력 모달 스타일
const CalendarModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2004;
`;

const CalendarModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-border);
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  z-index: 2005;
  box-shadow: var(--modal-shadow);
`;

const TimeTagSelectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--color-date-block-bg);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  width: 160px;
  justify-content: flex-start;
  box-shadow: none;
  height: 48px;
  box-sizing: border-box;
  
  @media (max-width: 420px) {
    gap: 14px;
  }
  flex-shrink: 0;
  
  @media (max-width: 800px) {
    flex: 1;
    width: auto;
    min-width: 0;
  }
  
  @media (max-width: 420px) {
    padding: 4px 6px;
    gap: 3px;
    border-radius: 6px;
  }
  
  @media (max-width: 360px) {
    padding: 2px 4px;
    gap: 2px;
    font-size: clamp(8px, 2vw, 14px);
  }

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-color: rgba(var(--color-accent-rgb), 0.3);
  }
`;

const TimeTagIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--color-text-primary);
  border: 3px solid var(--color-text-primary);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  
  @media (max-width: 360px) {
    width: 28px;
    height: 28px;
    font-size: 10px;
    border-width: 2px;
  }
`;

const TimeTagInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  
  @media (max-width: 360px) {
    gap: 1px;
  }
`;

const PointTypeSelectBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PointTypeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PointTypeSectionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 8px 0;
`;

const PointTypeSectionTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  padding-bottom: 4px;
`;

const PointTypeSelectItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    border-color: rgba(var(--color-accent-rgb), 0.3);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const PointTypeInfo = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PointTypeMemo = styled.span`
  font-size: 14px;
  color: var(--color-text-primary);
  font-weight: 500;
`;

const PointTypePoints = styled.span`
  font-size: 14px;
  color: var(--color-accent-text);
  font-weight: 600;
`;

// 새로운 스타일 컴포넌트들

// 3구역: 저장하기 버튼 컨테이너
const RightSectionContainer = styled.div`
  display: flex;
  align-items: stretch;
  height: 100%;
  width: 100%;
  grid-column: 3; /* 3구역 */
  justify-content: flex-end;
  
  @media (max-width: 800px) {
    height: auto;
  justify-content: center;
    width: 100%;
    grid-column: auto;
  }
`;

const FirstRowContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: stretch;
  padding: 8px 20px;
  background: var(--color-bg);
  border-bottom: 1px solid var(--color-border);
  gap: 16px;
  height: 64px;
  
  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
    height: auto;
    gap: 12px;
    padding: 12px 20px;
  }
`;

const SecondRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 20px;
  background: var(--color-bg);
  margin-bottom: 4px;
  
  @media (min-width: 801px) {
    flex-direction: row;
    align-items: center;
  justify-content: center;
  }
  
  @media (max-width: 800px) {
    gap: 6px;
    padding: 12px 16px;
  }
  
  @media (max-width: 600px) {
    gap: 4px;
    padding: 12px 8px;
  }
  
  @media (max-width: 480px) {
    gap: 3px;
    padding: 12px 4px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  
  @media (min-width: 801px) {
    flex-wrap: nowrap;
  }
  
  @media (max-width: 600px) {
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    gap: 3px;
  }
`;

const ToggleButton = styled.div<{ $displayMode: 'points' | 'coins' | 'notes'; $isDisabled?: boolean }>`
  display: flex;
  background: ${props => props.$isDisabled ? 'rgba(var(--color-accent-rgb), 0.3)' : 'var(--color-accent-strong)'};
  border-radius: 13px; // CompactButton 높이 26px에 맞춰 조정
  padding: 2px;
  position: relative;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  height: 26px; // CompactButton과 동일한 높이
  min-width: 150px; // 3개 옵션을 위해 최소 가로 길이 증가
  width: auto; // 자동 너비
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => {
      if (props.$displayMode === 'points') return '2px';
      if (props.$displayMode === 'coins') return 'calc(33.33% + 1px)';
      return 'calc(66.66% + 0.5px)'; // notes - 오른쪽 여백 조정
    }};
    width: calc(33.33% - 2px);
    height: calc(100% - 4px);
    background: ${props => props.$isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'var(--color-surface)'};
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const ToggleOption = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 2px 8px; // 3개 옵션을 위해 패딩 감소
  font-size: 10px; // 3개 옵션을 위해 텍스트 크기 감소
  font-weight: 600;
  color: ${props => {
    if (props.$isDisabled) {
      return props.$isActive ? 'rgba(var(--color-accent-rgb), 0.4)' : 'rgba(255, 255, 255, 0.6)';
    }
    return props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)';
  }};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap; // 텍스트 줄바꿈 방지
  
  @media (max-width: 600px) {
    padding: 2px 8px;
    font-size: 10px;
  }
`;

// 아이콘 슬라이드 토글 (SaveAllButton과 동일한 높이 48px)
const ModeIconToggle = styled.div<{ $isSelectionMode: boolean }>`
  position: relative;
  height: 48px;
  border-radius: 24px;
  background: var(--color-accent-strong);
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 60px; /* 좌우로 노브 공간(42px) + 여백 확보 */
  width: auto; /* 내용에 맞춰 컴팩트 */
  min-width: 220px; /* 조금 더 넓게 */
  cursor: pointer;
  user-select: none;
  transition: background 0.2s ease;

  @media (max-width: 800px) {
    display: flex;       /* 줄바꿈 환경에서도 가득 채우기 */
    flex: 1;             /* 가용 폭을 모두 차지 */
    width: auto;         /* 부모 가로에 맞춤 */
    min-width: 0;        /* 축소 허용 */
  }
`;

const ModeCurrentLabel = styled.div`
  position: absolute;
  left: 60px; /* 좌측 노브 공간 비움 */
  right: 60px; /* 우측 노브 공간 비움 */
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 800;
  font-size: 18px;
  white-space: nowrap;
`;

const ToggleKnob = styled.div<{ $isSelectionMode: boolean }>`
  position: absolute;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--color-surface);
  top: 3px;
  left: ${props => props.$isSelectionMode ? 'calc(100% - 45px)' : '3px'};
  transition: left 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 2px var(--color-accent-strong);
`;

// SVG Icons
const SearchIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 20L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PenIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M20.71 7.04c.39-.39.39-1.02 0-1.41L18.37 3.29a1 1 0 0 0-1.41 0l-.88.88 3.75 3.75.88-.88z" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
);


const CompactButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 8px;
  height: 26px;
  min-height: 26px;
  background: var(--color-date-block-bg);
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  opacity: ${props => props.disabled ? 0.5 : 1};

  @media (max-width: 768px) {
    padding: 4px 6px;
  }

  @media (max-width: 600px) {
    padding: 4px 5px;
  }

  @media (max-width: 480px) {
    padding: 4px 4px;
  }

  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-color: var(--color-text-muted);
  }
`;

const SaveAllButton = styled.button<{ disabled?: boolean }>`
  width: 160px;
  padding: 8px 12px;
  background: ${props => props.disabled ? 'var(--color-panel)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'white'};
  border: ${props => props.disabled ? '2px solid var(--color-border)' : 'none'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  flex-shrink: 0;
  
  @media (max-width: 800px) {
    flex: 1;
    width: auto;
    min-width: 0;
  }

  @media (max-width: 768px) {
    width: 280px;
  }

  @media (max-width: 600px) {
    width: 270px;
  }

  @media (max-width: 480px) {
    width: 260px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ResetAllButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 16px;
  height: 26px;
  min-height: 26px;
  background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-reset-button-bg)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-reset-button-text)'};
  border: 2px solid ${props => props.disabled ? 'var(--color-border)' : 'var(--color-reset-button-text)'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 4px 12px;
  }

  @media (max-width: 600px) {
    font-size: 14px;
    padding: 4px 10px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 4px 8px;
  }

  &:hover {
    background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-reset-button-bg)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.98)'};
  }
`;

const MainActionButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 12px;
  height: 26px;
  min-height: 26px;
  background: ${props => props.disabled ? 'var(--color-panel)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'white'};
  border: ${props => props.disabled ? '2px solid var(--color-border)' : 'none'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 4px 10px;
  }

  @media (max-width: 600px) {
    padding: 4px 8px;
  }

  @media (max-width: 480px) {
    padding: 4px 6px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

// 레벨 설정 모달 스타일
const LevelSettingsModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: block;
  z-index: 1000;
  overflow-y: auto;
  padding: 80px 20px;
  min-height: 100vh;

  @media (max-width: 600px) {
    padding: 60px 20px;
  }
`;

const LevelSettingsModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  margin: 0 auto;
  overflow: visible;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-xlarge);
`;

const LevelSettingsBody = styled.div`
  padding: 24px;
`;

const LevelSettingsFormGroup = styled.div`
  margin-bottom: 20px;
`;

const LevelSettingsLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 8px;
`;

// 레벨 설정용 포인트 입력 스타일 (과제 체크 모달과 동일)
const LevelPointInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LevelPointMinusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const LevelPointInput = styled.input`
  padding: 8px 4px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-weight: normal;
  flex: 1;
  min-width: 0;
  text-align: center;
  height: 38px;
  line-height: 38px;
  box-sizing: border-box;
  
  /* number input의 기본 증감 버튼 제거 */
  -moz-appearance: textfield;
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const LevelPointPlusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const LevelSettingsDescription = styled.p`
  font-size: 12px;
  color: var(--color-text-gray);
  margin-top: 8px;
  line-height: 1.4;
`;

const LevelSettingsModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;
`;

const LevelSettingsCloseButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 저장 호버 버튼 (AssignmentCardPage와 동일 스타일, 항상 표시)
const SaveFloatingButton = styled.button<{ $isDisabled?: boolean }>`
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 60px);
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$isDisabled ? '#ccc' : '#10504A'};
  color: white;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(var(--color-accent-rgb), 0.3)'};
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isDisabled ? 0.6 : 1};
  
  &:hover {
    background: ${props => props.$isDisabled ? '#ccc' : '#0D4039'};
    transform: ${props => props.$isDisabled ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 6px 20px rgba(var(--color-accent-rgb), 0.4)'};
  }
  
  &:active {
    transform: ${props => props.$isDisabled ? 'none' : 'scale(0.95)'};
  }
  
  /* 다크모드: 흰색 버튼에 진한 회색 아이콘 */
  :root:not([data-theme='light']) & {
    background: ${props => props.$isDisabled ? '#666' : '#ffffff'};
    border: 1px solid var(--color-border);
    color: ${props => props.$isDisabled ? '#999' : '#505050'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(0, 0, 0, 0.2)'};
  }
  
  :root:not([data-theme='light']) &:hover {
    background: ${props => props.$isDisabled ? '#666' : '#ffffff'};
    border-color: var(--color-text-muted);
    color: ${props => props.$isDisabled ? '#999' : '#505050'};
    box-shadow: ${props => props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 6px 20px rgba(0, 0, 0, 0.3)'};
  }
  
  :root:not([data-theme='light']) & svg {
    stroke: ${props => props.$isDisabled ? '#999' : '#505050'};
  }
`;

const LevelSettingsSaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 포인트 되돌리기 버튼
const UndoButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 16px;
  height: 26px;
  min-height: 26px;
  background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-reset-button-bg)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-reset-button-text)'};
  border: 2px solid ${props => props.disabled ? 'var(--color-border)' : 'var(--color-reset-button-text)'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-reset-button-bg)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.98)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 4px 12px;
  }

  @media (max-width: 600px) {
    font-size: 14px;
    padding: 4px 10px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 4px 8px;
  }
`;

// 그리드 정렬 컨테이너
const GridAlignmentContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`;

// 그리드 정렬 토글 버튼
const GridAlignmentToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 2px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const GridAlignmentOption = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.$isActive ? 'var(--color-grid-alignment-active-bg)' : 'transparent'};
  color: ${props => props.$isActive ? 'var(--color-grid-alignment-active-icon)' : 'var(--color-grid-alignment-icon)'};
  box-shadow: ${props => props.$isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: all 0.2s ease;
`;

// 되돌리기 모달 스타일
const UndoModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
`;

const UndoModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 2001;
  width: 90vw;
  max-width: 500px;
  overflow: hidden;
  
  @media (max-width: 480px) {
    width: 95vw;
  }
`;

const UndoModalContent = styled.div`
  padding: 32px 24px 24px;
  text-align: center;
`;

const UndoIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 16px;
`;

const UndoTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  line-height: 1.4;
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const UndoMessage = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #6c757d;
  line-height: 1.5;
`;

const UndoDetails = styled.div`
  background: var(--color-panel);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  text-align: left;
`;

const UndoDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }
`;

const UndoLabel = styled.span`
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const UndoValue = styled.span`
  font-size: 14px;
  color: #212529;
  font-weight: 600;
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const UndoStudentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  background: var(--color-panel);
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
  justify-content: center;
  align-items: center;
`;

const UndoStudentItem = styled.div`
  padding: 8px;
  border-radius: 4px;
  background: var(--color-surface);
  font-size: 13px;
  color: var(--color-text-secondary);
  border: 2px solid var(--color-border);
  text-align: center;
  font-weight: 500;
  min-width: 80px;
  flex: 0 0 auto;
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const UndoModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
`;

const UndoConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// CSS 클래스 스타일 추가
const GlobalStyles = styled.div`
  .desktop-only {
  @media (max-width: 600px) {
    display: none;
  }
  }
  
  .mobile-only {
    display: none;
    
    @media (max-width: 600px) {
      display: block;
    }
  }
`;

// 토스트 알림 스타일
const ToastOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ToastMessage = styled.div`
  background: #ffffff; /* 라이트/다크 공통 흰색 배경 */
  color: #000000;      /* 라이트/다크 공통 검정 텍스트 */
  padding: 24px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 300px;
  max-width: 500px;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ToastText = styled.div`
  white-space: pre-line;
  line-height: 1.5;
`;

const ToastConfirmButton = styled.button`
  background: var(--color-accent-strong);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: center;
  
  &:hover {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// 레벨 설정 - 아이템 카드 섹션 스타일
const LevelSettingsDivider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--color-border);
  margin: 24px 0;
`;

const LevelSelectContainer = styled.div<{ $level?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  padding: 16px;
  background: var(--color-panel);
  border: 3px solid ${props => props.$level ? getLevelColor(props.$level) : 'var(--color-border)'};
  border-radius: 8px;
`;

const LevelSelectButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LevelSelectResetButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const LevelSelectMinusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const LevelSelectDisplay = styled.div<{ $level: number }>`
  background: ${props => getLevelColor(props.$level)};
  color: white;
  font-size: 16px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 4px;
  width: 28px;
  height: 26px;
  text-align: center;
  line-height: 18px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const LevelSelectInput = styled.input`
  padding: 4px 8px;
  border: 2px solid ${props => getLevelColor(props.value ? parseInt(props.value.toString()) : 1)};
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  width: 28px;
  height: 26px;
  text-align: center;
  line-height: 18px;
  box-sizing: border-box;
  outline: none;
  background: var(--color-surface);
  color: ${props => getLevelColor(props.value ? parseInt(props.value.toString()) : 1)};
  transition: all 0.2s;

  /* number input의 기본 증감 버튼 제거 */
  -moz-appearance: textfield;
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const LevelSelectPlusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const LevelCoinSection = styled.div<{ $level: number }>`
  margin-top: 20px;
  padding: 16px;
  background: var(--color-panel);
  border: 3px solid ${props => getLevelColor(props.$level)};
  border-radius: 8px;
`;

const LevelCoinSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 12px 0;
  text-align: center;
`;

const LevelCoinInputWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const LevelCoinLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
`;

const LevelCoinInput = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  width: 120px;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
  
  /* number input의 기본 증감 버튼 제거 */
  -moz-appearance: textfield;
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const LevelCoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemCardSection = styled.div<{ $level: number }>`
  margin-top: 20px;
  padding: 16px;
  background: var(--color-panel);
  border: 3px solid ${props => getLevelColor(props.$level)};
  border-radius: 8px;
`;

const ItemCardSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 16px 0;
`;

const ItemCardSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 120px);
  gap: 14px;
  justify-content: center;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const ItemCardSelectBlock = styled.div<{ $isSelected?: boolean }>`
  width: 120px;
  height: 200px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ItemCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  overflow: hidden;
  background: var(--color-panel);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemCardName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemCardMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const ItemCardMetaGroup = styled.div`
  width: 100%;
  margin-top: 6%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ItemCardCost = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  svg {
    width: 14px;
    height: 14px;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: #f39c12;
    line-height: 1;
  }
`;

const ItemCardUseCount = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  svg {
    width: 12px;
    height: 12px;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: #ff4757;
    line-height: 1;
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
`;

const EmptyItemCardMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: var(--color-text-gray);
  font-size: 14px;
  line-height: 1.6;
`;

// 코인 추가 모달과 동일한 스타일 컴포넌트들
const DeleteConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const DeleteConfirmContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  z-index: 10000;
  width: 90vw;
  max-width: 400px;
  padding: 24px;
  overflow: hidden;
  
  @media (max-width: 480px) {
    width: 95vw;
  }
`;

const CoinModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CoinModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 10px 0;
  text-align: center;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
`;

const CoinModalField = styled.div`
  margin-bottom: 16px;
`;

const CoinModalLabel = styled.label`
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: #000;
  background: var(--color-surface);
  padding: 4px 16px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  margin-bottom: 8px;
  margin-top: 8px;
`;

const CoinModalInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CoinAmountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 0;
`;

const CoinButton = styled.button`
  padding: 8px 12px;
  height: 40px;
  min-height: 40px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 24px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 40px;
  min-width: 40px;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CoinAmountInput = styled.input<{ $isNegative?: boolean }>`
  font-size: 2.25rem; /* 코인 추가 모달과 동일한 크기 */
  font-weight: bold;
  color: ${props => props.$isNegative ? '#ff6b6b' : 'var(--color-text-primary)'};
  text-align: center;
  min-width: 140px;
  padding: 4px 20px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  background: var(--color-surface);
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const DeleteConfirmFooter = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelDeleteButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  background: transparent;
  color: var(--color-accent-text);
  border: 1px solid var(--color-accent-strong);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ConfirmDeleteButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 10px 20px;
  background: ${props => props.disabled ? 'var(--color-panel)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'white'};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.9);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 노트 모드용 스타일 컴포넌트들
const NoteCardLayout = styled.div<{ $isSelectionMode?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: ${props => props.$isSelectionMode ? 'auto auto' : '1fr'};
  gap: 6px;
  height: 100%;
`;

const NoteProfileColumn = styled.div`
  display: flex;
  align-items: flex-end;
  grid-row: 1 / 3;
  grid-column: 1 / 2;
  padding: 0;
`;

// 번호 모드용 타입 버튼 (원래 높이 유지)
const NumberModeTypeButton = styled.button`
  width: 100%;
  height: 26px;
  min-height: 26px;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--color-border);
  background: var(--color-student-block-element-bg);
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  flex-shrink: 0;
  box-shadow: none;
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

// 자리 모드용 타입 버튼
const SeatModeTypeButton = styled.button`
  width: 100%;
  height: 26px;
  min-height: 26px;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--color-border);
  background: var(--color-student-block-element-bg);
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  flex-shrink: 0;
  box-shadow: none;
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-student-block-element-bg);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

// 포인트 모드의 세트 버튼용 styled component
const SetButton = styled.button<{ $isDisabled?: boolean; $isSeatMode?: boolean }>`
  width: 100%;
  height: ${props => props.$isSeatMode ? '20px' : '26px'};
  min-height: ${props => props.$isSeatMode ? '20px' : '26px'};
  padding: 0;
  font-size: ${props => props.$isSeatMode ? '10px' : '12px'};
  font-weight: 500;
  border: 1px solid var(--color-border);
  background: ${props => props.$isDisabled ? 'var(--color-surface-highlight)' : 'var(--color-student-block-element-bg)'};
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  border-radius: ${props => props.$isSeatMode ? '4px' : '6px'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$isDisabled ? '#bdbdbd' : 'var(--color-text-primary)'};
  flex-shrink: 0;
  box-shadow: none;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$isDisabled ? 'var(--color-surface-highlight)' : 'var(--color-student-block-element-bg)'};
    border-color: var(--color-text-muted);
    ${props => !props.$isDisabled && `
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    `}
  }

  &:active {
    background: ${props => props.$isDisabled ? 'var(--color-surface-highlight)' : 'var(--color-student-block-element-bg)'};
    ${props => !props.$isDisabled && `
      transform: translateY(0);
      box-shadow: none;
    `}
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const NoteContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  grid-row: 1 / 3;
  grid-column: 2 / -1;
  overflow: visible; /* 팝업이 잘리지 않도록 */
`;

const StudentInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PersonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-text);
  flex-shrink: 0;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
  }
  
  &:active {
    background-color: rgba(var(--color-accent-rgb), 0.2);
  }
`;

const NoteStudentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const RelatedStudentsInfo = styled.div`
  font-size: 12px;
  color: var(--color-text-gray);
  margin-left: 4px;
`;

const NoteTextArea = styled.textarea`
  width: 100%;
  flex: 1;
  padding: 8px 8px 6px 8px; /* 위쪽 여백 유지, 아래 여백 살짝 축소 */
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  line-height: 1.4; /* 내용이 위쪽부터 자연스럽게 쌓이도록 */
  text-align: left; /* 수평 중앙정렬 방지 */
  vertical-align: top; /* 상단 정렬 의도 명시 */
  min-height: 64px; /* 고정 높이에서 상단 정렬 보장 */
  background: var(--color-student-block-input-bg);
  color: var(--color-text-primary);
  overflow-y: auto;
  display: block;
  background: var(--color-student-block-element-bg);
  color: var(--color-text-primary);
  
  &:focus {
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 번호/자리 토글 버튼용 별도 스타일 컴포넌트들 (2개 옵션)
const ViewModeToggleButton = styled.div<{ $isSeatMode: boolean; $isDisabled?: boolean }>`
  display: flex;
  background: ${props => props.$isDisabled ? 'rgba(var(--color-accent-rgb), 0.3)' : 'var(--color-accent-strong)'};
  border-radius: 13px; // CompactButton 높이 26px에 맞춰 조정
  padding: 2px;
  position: relative;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  height: 26px; // CompactButton과 동일한 높이
  min-width: 120px; // 원래 2개 옵션용 최소 가로 길이
  width: auto; // 자동 너비
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isSeatMode ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: ${props => props.$isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'var(--color-surface)'};
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const ViewModeToggleOption = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 2px 12px; // 2개 옵션용 원래 패딩
  font-size: 11px; // 2개 옵션용 원래 텍스트 크기
  font-weight: 600;
  color: ${props => {
    if (props.$isDisabled) {
      return props.$isActive ? 'rgba(var(--color-accent-rgb), 0.4)' : 'rgba(255, 255, 255, 0.6)';
    }
    return props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)';
  }};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap; // 텍스트 줄바꿈 방지
  
  @media (max-width: 600px) {
    padding: 2px 10px;
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 10px;
  }
`;


// 메인 노트 제목 입력 모달 스타일
const MainNoteTitleModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
`;

const MainNoteTitleModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 40px 0 160px 0;
  box-sizing: border-box;
  z-index: 2001;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MainNoteTitleModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  margin: auto;
`;

const MainNoteTitleModalTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: bold;
`;

const MainNoteTitleInputContainer = styled.div`
  margin-bottom: 30px;
`;

const MainNoteTitleInputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  font-weight: 500;
`;

const MainNoteTitleInputField = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MainNoteTitleButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const MainNoteTitleSaveButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-weight: 600;
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MainNoteTitleCancelButton = styled.button`
  background: transparent;
  color: var(--color-accent-text);
  border: 1px solid var(--color-accent-strong);
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-color: var(--color-accent);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SeatModeGrid = styled.div<{ $gridTemplateColumns: string; $gridTemplateRows: string }>`
  display: grid;
  grid-template-columns: ${props => props.$gridTemplateColumns};
  grid-template-rows: ${props => props.$gridTemplateRows};
  gap: 4px;
  /* 상/하단 빈 행 trim 이후에도 시각적 여백을 유지 */
  padding: 16px 4px;
  background: transparent;
  width: 100%;
  min-width: 1440px;
  align-content: start;
  height: fit-content;
  box-shadow: none;
`;

const RotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const RotateButton = styled.button`
  width: 60px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:active {
    transform: none;
  }
`;

const RotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--color-grid-alignment-active-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
`;

const RotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.3s ease, transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  /* 라이트 모드: 아이콘 색상 */
  :root[data-theme='light'] & svg {
    stroke: #717785;
  }

  ${RotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const RotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.3s ease, transform 0.3s ease;

  /* 라이트 모드: 180 텍스트 색상 */
  :root[data-theme='light'] & {
    color: #717785;
  }

  ${RotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

PointsTablePage.displayName = "PointsTablePage";
export default PointsTablePage;
